import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Crown,
  Users,
  Play,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  Copy,
  Check,
  History,
  Trash2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  PlusCircle,
  Sparkles,
  HelpCircle,
  FileText,
  X,
  Download,
  Presentation,
  Wrench,
  Settings,
  Upload,
  Type,
  Database,
  DollarSign,
  MessageSquare,
  Send,
  AlertTriangle,
  RefreshCw,
  Paperclip,
  Plus,
  ImageIcon,
  Globe,
  ExternalLink,
  Expand,
  Minimize2,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MD_TABLE_COMPONENTS } from '../components/Common/MarkdownContent';
import api from '../api';
import ConfirmDialog, { ConfirmRequest } from '../components/ProcessMaps/ConfirmDialog';
import VerificationBadge from '../components/Verification/VerificationBadge';
import VerificationPanel from '../components/Verification/VerificationPanel';
import VerificationToggle from '../components/Verification/VerificationToggle';
import { useProject } from '../contexts/ProjectContext';
import { useNotifications } from '../contexts/NotificationContext';
import toast from 'react-hot-toast';
import SmartRouterBanner from '../components/SmartRouter/SmartRouterBanner';
import OrchestratorProjectMismatchBanner from '../components/SmartRouter/OrchestratorProjectMismatchBanner';
import { useOrchestratorContext } from '../hooks/useOrchestratorContext';
import { useSmartRouterStepSync } from '../hooks/useSmartRouterStepSync';
import { useSmartRouterSessionContext } from '../hooks/useSmartRouterSessionContext';
import { getLatestSmartStartDocuments } from '../services/smartStartApi';
import SessionCollaboratorsList from '../components/Collaborators/SessionCollaboratorsList';
import SynthesisVisualizations from '../components/SynthesisVisualizations/SynthesisVisualizations';
import UrsWorkspace from '../components/StrategySynthesis/UrsWorkspace';
import type { UrsCorpus, UrsInterview } from '../components/StrategySynthesis/UrsWorkspace';
import { useProjectIngestJob } from '../hooks/useProjectIngestJob';
import { countOf } from '../utils/pluralize';
import {
  hasNewPipelineProgress,
  shouldResetCollapsedStepsOnLoad,
} from './aiCouncilPipelineSync';
import {
  getAICouncilCollaborators,
  getAvailableAICouncilCollaborators,
  addAICouncilCollaborator,
  removeAICouncilCollaborator,
  getTenantUsers,
} from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CouncilModel {
  id: string;
  name: string;
  provider: string;
  available: boolean;
  isPresident: boolean;
  contextWindowChars?: number;
}

interface MemberResult {
  modelId: string;
  status: 'success' | 'error';
  response: string | null;
  error?: string;
  durationMs: number;
}

interface PresidentEvaluation {
  convergence: string;
  divergence: string;
  qualityAssessment: string;
  finalResponse: string;
  raw: string;
}

interface CouncilResult {
  prompt: string;
  hasInputData: boolean;
  memberResults: MemberResult[];
  presidentModel: string;
  presidentResult: MemberResult;
  presidentEvaluation: PresidentEvaluation | null;
  finalSynthesis?: FinalSynthesis | null;
  summary: {
    totalModels: number;
    successfulModels: number;
    failedModels: number;
    presidentSuccess: boolean;
  };
  // Snapshot of which uploaded docs were excluded (0-char extraction) AT RUN TIME, not read live
  // from uploadedDocs — that state can change (session switch, "Sync from server", new upload)
  // between when a run completes and when this result re-renders, which previously made the
  // "this analysis excludes N documents" banner silently disappear even for a run that genuinely
  // excluded files. Optional because older/restored sessions may not have it (see loadSessionById,
  // which now backfills it from input_data's [EXCLUDED: ...] markers).
  excludedDocNames?: string[];
}

interface SessionSummary {
  id: number;
  title: string;
  prompt: string;
  selected_models: string[];
  summary: CouncilResult['summary'] & { session_type?: 'council' | 'strategy-synthesis'; personaCount?: number; modelUsed?: string };
  created_at: string;
  project_id: number | null;
  is_collaborator?: boolean;
  collaborator_names?: string[];
}

interface Persona {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface StrategicSubTool {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  gradient: string;
  placeholder: string;
}

interface StrategySynthesisResult {
  subToolId: string;
  subToolName: string;
  response: string;
  // Present only when the report came from the Interview Intelligence pipeline rather than
  // the legacy single-shot call (M4 slice 3, Asana 1216725196813620). Optional on purpose:
  // every other tool, and this one before the cutover, returns prose and nothing
  // else, and the card has to keep rendering exactly as it does today for all of them.
  source?: string;
  structured?: {
    json: any;
    citations: Array<{ verbatim: string; extracted_value_id: string | null; speaker: string | null; timestamp: string | null }>;
    run_count: number;
    ingestion_completeness: number;
    pending_merge_count: number;
    stakeholder_coverage?: { status?: string; reason?: string; interviewed?: number | null; expected?: number | null };
  };
  promotion?: { interview_count: number; eligible: boolean };
  preliminary?: boolean;
  interviewContextUsed?: boolean;
  interviewContextCount?: number | null;
}

interface ImplementationPersonaResult {
  personaName: string;
  emoji: string;
  response: string;
}

interface FinalSynthesis {
  confirmedStrengths: string;
  designConstraints: string;
  requiredModifications: string;
  openQuestions: string;
  contradictionResolution: string;
  changeLog: string;
  decisionRegister: string;
  raw: string;
  customStructure?: boolean;
}

interface ImplementationReviewResult {
  councilOutput: string;
  modelUsed: string;
  personaResults: ImplementationPersonaResult[];
  updatedPlan: string;
  finalSynthesis?: FinalSynthesis;
  raw: string;
}

// Extract a human-readable error from a failed blob-download request. When a
// request uses responseType:'blob', an error response body arrives as a Blob,
// so the usual err.response.data.error is unreadable until we parse it.
async function readBlobError(err: any): Promise<string | null> {
  const data = err?.response?.data;
  try {
    if (data instanceof Blob) {
      const text = await data.text();
      try { return JSON.parse(text)?.error || null; } catch { return text?.slice(0, 200) || null; }
    }
    if (data?.error) return data.error;
  } catch { /* ignore */ }
  if (err?.code === 'ECONNABORTED') return 'The export timed out — the document may be too large. Please try again.';
  return err?.message || null;
}

interface SlideOutlineItem {
  slideNum: number;
  title: string;
  bullets: string[];
  type?: string;
  subtitle?: string;
  items?: any[];
  headers?: string[];
  rows?: any[][];
  what?: string;
  soWhat?: string;
  nowWhat?: string;
}

const SHOW_REFINE_CHAT = true;

// ─── Provider color badges ─────────────────────────────────────────────────

const PROVIDER_COLORS: Record<string, string> = {
  Anthropic: 'bg-orange-100 text-orange-800',
  OpenAI:    'bg-green-100 text-green-800',
  Google:    'bg-blue-100 text-blue-800',
  Mistral:   'bg-purple-100 text-purple-800',
};

const STRATEGIC_SUBTOOLS: StrategicSubTool[] = [
  {
    id: 'user-research-synthesis',
    name: 'User Research Synthesis',
    description: 'Synthesize stakeholder interviews, transcripts, surveys, or raw data into structured insights, themes, pain points, and prioritized recommendations.',
    icon: 'Users',
    color: '#6366f1',
    gradient: 'from-indigo-50 to-white',
    placeholder: 'Paste stakeholder interviews, survey responses, or raw research data here...',
  },
  {
    id: 'roadmap-narrative',
    name: 'Roadmap Narrative',
    description: 'Transform a prioritized initiative list into a compelling strategic roadmap narrative for executives and operational teams.',
    icon: 'Map',
    color: '#f59e0b',
    gradient: 'from-amber-50 to-white',
    placeholder: 'List your OKRs, initiatives, and timeline here...',
  },
  {
    id: 'stakeholder-influence-mapper',
    name: 'Stakeholder Influence Mapper',
    description: 'Build stakeholder maps, influence strategies, and tailored talking points to secure alignment and buy-in.',
    icon: 'Network',
    color: '#10b981',
    gradient: 'from-emerald-50 to-white',
    placeholder: 'Describe the initiative and list key stakeholders with their concerns...',
  },
  {
    id: 'okr-cascade-generator',
    name: 'OKR Cascade Generator',
    description: 'Automate company-to-team OKR cascading with alignment scoring and metrics for strategic initiatives.',
    icon: 'Target',
    color: '#8b5cf6',
    gradient: 'from-violet-50 to-white',
    placeholder: 'Enter company OKRs and the teams to cascade to...',
  },
  {
    id: 'change-management-suite',
    name: 'Change Management Suite',
    description: 'Full suite: change impact assessment, board deck structure, scenario war room, and governance playbook.',
    icon: 'Layers',
    color: '#ef4444',
    gradient: 'from-red-50 to-white',
    placeholder: 'Describe the change initiative, scope, timeline, and key risks...',
  },
  {
    id: 'synthesis-visualizations',
    name: 'Synthesis Visualizations',
    description: 'Eight lenses on synthesized interview research — themes, insights, quotes, journey, mind map, workflow architecture, first principles, and coverage.',
    icon: 'Share2',
    color: '#7c3aed',
    gradient: 'from-violet-50 to-white',
    placeholder: '',
  },
];

const DEFAULT_IMPLEMENTATION_PERSONAS: Persona[] = [
  {
    id: 'imp-1',
    name: 'Technology & Data Architecture Lead',
    emoji: '🏗️',
    description: `You are the Technology and Data Architecture Lead on an implementation council.

Mission:
Evaluate this plan from the perspective of systems design, data flows, scalability, reliability, and technical feasibility.

Thinking style:
- Use first-principles thinking: break the problem down into fundamental constraints (compute, storage, latency, interfaces, data models) and rebuild the architecture from the ground up.
- Favor simple, robust architectures over clever but fragile designs.
- Make tradeoffs and assumptions explicit.

When you respond, follow this structure:
1) Restate the core goal of the plan in your own words.
2) First-principles decomposition:
   - What are the fundamental technical requirements?
   - What are the core data entities, flows, and trust boundaries?
   - What hard constraints (latency, throughput, security, integrations) govern the design?
3) Proposed architecture:
   - High-level components and how they interact.
   - Data model and storage approach.
   - Key integration points and dependencies.
4) Risks and failure modes:
   - At least 5 specific technical or data-related risks.
   - Likelihood/impact and how to mitigate each.
5) Implementation guidance:
   - Phased rollout (MVP vs future-state).
   - Technical decision principles (what we should always/never do).

Be concrete and specific. If information is missing, say exactly what you would need to design this properly. If the plan is sound, give it your approval.`,
  },
  {
    id: 'imp-2',
    name: 'Risk & Cybersecurity Lead',
    emoji: '🔒',
    description: `You are the Risk and Cybersecurity Lead on an implementation council.

Mission:
Identify and mitigate information security, privacy, operational, and strategic risks in this plan.

Thinking style:
- Start from first principles about risk: assets, threats, vulnerabilities, impact, and likelihood.
- Assume that motivated adversaries will look for weakest links.
- Think in terms of defense-in-depth and least privilege.

When you respond, follow this structure:
1) Summarize the plan and its most critical assets (data, systems, processes).
2) Threat modeling from first principles:
   - Who are the likely threat actors?
   - What do they want?
   - What are the main attack surfaces created by this plan?
3) Risk analysis:
   - At least 8 concrete risks across: confidentiality, integrity, availability, privacy, and business continuity.
   - For each: likelihood (low/med/high), impact (low/med/high), and short rationale.
4) Controls and mitigations:
   - Technical controls (identity, access, encryption, logging, monitoring).
   - Process controls (policies, training, incident response).
   - Governance (ownership, decision rights, risk acceptance thresholds).
5) Residual risk and go/no-go view:
   - Which risks are acceptable vs not?
   - What must be in place before launch?

Be specific about configurations, practices, and tradeoffs, not just generic "do security best practices."`,
  },
  {
    id: 'imp-3',
    name: 'People & Change Management Lead',
    emoji: '👥',
    description: `You are the People and Change Management Lead on an implementation council.

Mission:
Ensure this plan is executable by real humans in a real organization, minimizing resistance and maximizing adoption.

Thinking style:
- Start from first principles about human behavior: incentives, identity, status, habits, and cognitive load.
- Assume that people do not change just because a slide says so; they change when it is easier, safer, and more rewarding.

When you respond, follow this structure:
1) Summarize how this plan will change how people work (who does what differently).
2) Stakeholder map:
   - Key groups (leaders, managers, ICs, customers, partners).
   - What each group stands to gain or lose.
3) Adoption risks:
   - At least 6 specific risks related to behavior, culture, skills, and communication.
   - Example: "Middle managers may quietly resist because it threatens X."
4) Change strategy:
   - Narrative: how we explain the "why" in plain language for each major group.
   - Incentives: how performance measures, rewards, or recognition must change.
   - Capability: training, coaching, or hiring required.
   - Support: change champions, feedback loops, and how we handle pushback.
5) 90-day people plan:
   - Key milestones, owner roles, and measurable signals of adoption vs resistance.

Avoid generic "communicate early and often" advice; anchor in the specific dynamics implied by the plan.`,
  },
  {
    id: 'imp-4',
    name: 'Regulatory & Compliance Lead',
    emoji: '⚖️',
    description: `You are the Regulatory and Compliance Lead on an implementation council.

Mission:
Assess this plan for alignment with relevant laws, regulations, contractual obligations, and internal policies, and prevent regulatory or legal blowback.

Thinking style:
- Use first-principles reasoning around regulatory risk: what obligations exist, what exposures are created, and what enforcement mechanisms apply.
- Assume regulators and auditors will find what you miss.
- Think in terms of both current compliance and future regulatory trajectory.

When you respond, follow this structure:
1) Summarize the plan and identify its key regulatory domains (data privacy, financial, employment, industry-specific).
2) Regulatory landscape:
   - Applicable laws, regulations, and standards (be specific: GDPR, CCPA, SOX, HIPAA, etc.).
   - Contractual and policy obligations (vendor agreements, internal policies).
   - Jurisdictional considerations if multi-region.
3) Compliance gap analysis:
   - At least 6 specific compliance risks or gaps created by this plan.
   - For each: the specific regulation or obligation at risk, likelihood, and potential consequence (fine, sanction, litigation, reputational damage).
4) Remediation and controls:
   - Required changes to bring the plan into compliance.
   - Compliance checkpoints and approval gates.
   - Documentation, audit trail, and evidence requirements.
5) Go/no-go view:
   - Which compliance requirements are blockers vs. manageable?
   - What must be resolved before launch vs. can be addressed post-launch?
   - Recommended legal and compliance sign-offs required.

Be specific about regulations and obligations; do not just say "ensure GDPR compliance" — identify the specific articles, requirements, or provisions at risk.`,
  },
  {
    id: 'imp-5',
    name: 'Process & Implementation Lead',
    emoji: '⚙️',
    description: `You are the Process and Implementation Lead on an implementation council.

Mission:
Turn this plan into an executable, phased program with clear sequencing, ownership, and feedback loops.

Thinking style:
- Use first principles about execution: constraints on time, capacity, dependencies, and risk.
- Assume that plans fail at handoffs, unclear ownership, and underestimated complexity.
- Think in terms of critical path, resource constraints, and feedback loops.

When you respond, follow this structure:
1) Restate the plan's core deliverable and success criteria in operational terms.
2) Dependency and constraint mapping:
   - What must happen before what? (critical path)
   - What external dependencies exist (vendors, approvals, data, systems)?
   - What resource constraints apply (people, budget, time, technology)?
3) Phased implementation plan:
   - Phase 1 (MVP/pilot): minimal viable version, scope, timeline, success criteria.
   - Phase 2 (scale): what changes, what new dependencies, what risks emerge.
   - Phase 3 (steady state): operating model, ongoing ownership, optimization.
4) Execution risks:
   - At least 6 specific risks related to sequencing, capacity, dependencies, or handoffs.
   - For each: likelihood, impact, and mitigation.
5) Operating model and governance:
   - Who owns what? Decision rights and escalation paths.
   - Key performance indicators and how success is measured.
   - Feedback loops: how do we know if we are on track or need to course-correct?

Be specific about timelines, owners, and deliverables. Flag assumptions about capacity or timeline that may not hold.`,
  },
  {
    id: 'imp-6',
    name: "Devil's Advocate",
    emoji: '😈',
    description: `You are the Devil's Advocate on an implementation council.

Mission:
Pressure-test this plan by assuming it is flawed and trying to break it, so that only robust ideas survive.

Thinking style:
- Use first-principles thinking to question assumptions: if we erase all inherited constraints and accepted wisdom, does this still make sense?
- Argue in good faith but aggressively; your job is to find cracks, not to be polite.

When you respond, follow this structure:
1) Briefly restate the core thesis of the plan.
2) Assumption hunting:
   - List explicit and implicit assumptions the plan relies on (market, tech, people, timing, capital).
   - Mark each as "well-founded," "uncertain," or "weak."
3) First-principles attack:
   - For the most critical assumptions, ask: "Why must this be true?"
   - Propose alternative ways the world could be that would make the plan fail.
4) Failure modes:
   - At least 10 distinct ways this plan could fail, grouped by category (market, product, tech, org, finance, external shocks).
   - Short explanation for each.
5) Stress tests and experiments:
   - Design 3–5 concrete experiments, simulations, or leading indicators that would quickly reveal whether the risky assumptions hold.
6) Conditional recommendation:
   - Under what conditions would you support this plan?
   - Under what conditions would you strongly advise against it?

Be sharp, specific, and unflinching. Do not sugarcoat; your value is in what everyone else is afraid to say.`,
  },
];

const PPTX_PHASE_LABELS = [
  'Analyzing document structure',
  'Building coverage manifest',
  'Generating sections',
  'Verifying completeness',
];

// ─── Main Component ────────────────────────────────────────────────────────────

const AICouncil: React.FC = () => {
  const { currentProject, projects, setCurrentProject } = useProject();
  const location = useLocation();
  const navigate = useNavigate();
  const orchestratorCtx = useOrchestratorContext();

  // Read and consume pending data from Intelligence (sessionStorage set before navigation)
  const consumeIntelIntent = (): { prompt: string; inputData: string } | null => {
    try {
      const raw = sessionStorage.getItem('_intel_to_council');
      if (raw) {
        sessionStorage.removeItem('_intel_to_council');
        return JSON.parse(raw);
      }
    } catch {}
    return null;
  };

  // Read once on mount — shared across both useState initializers via ref trick
  const mountIntentRef = useRef<{ prompt: string; inputData: string } | null | 'used'>('used');
  if (mountIntentRef.current === 'used') {
    mountIntentRef.current = consumeIntelIntent();
  }


  const [prompt, setPrompt]               = useState(() => {
    const intent = mountIntentRef.current as any;
    if (intent?.prompt) return intent.prompt;
    if (orchestratorCtx.inRouter) {
      const scope = orchestratorCtx.extraParams?.scope || '';
      const purpose = orchestratorCtx.extraParams?.purpose || '';
      const audience = orchestratorCtx.extraParams?.audience || '';
      const parts: string[] = [];
      if (scope) parts.push(scope);
      if (purpose) parts.push(`Purpose: ${purpose}.`);
      if (audience) parts.push(`Audience: ${audience}.`);
      if (parts.length > 0) return parts.join('\n\n');
    }
    return '';
  });
  const [inputData, setInputData]         = useState(() => {
    const intent = mountIntentRef.current as any;
    if (intent?.inputData) return intent.inputData;
    return '';
  });

  // Handle navigation from Intelligence page via location.state
  useEffect(() => {
    const state = location.state as { prompt?: string; inputData?: string } | null;
    console.log('[AICouncil] location.key effect fired | key:', location.key, '| state:', state);
    if (state?.prompt) {
      console.log('[AICouncil] Intel intent received — prompt length:', state.prompt.length, '| inputData length:', state.inputData?.length ?? 0);
      // Store in ref so currentProject effect knows not to clear this data
      intelIntentRef.current = { prompt: state.prompt, inputData: state.inputData || '' };
      setPrompt(state.prompt);
      if (state.inputData) {
        setInputData(state.inputData);
        setSourceTab('paste');
      }
      setResult(null);
      setActiveSessionId(null);
      // Clear state so refresh doesn't re-populate
      window.history.replaceState({}, '', window.location.pathname);
    } else {
      console.log('[AICouncil] location.key effect — no intel state, skipping');
    }
  }, [location.key]);
  const [availableModels, setAvailableModels] = useState<CouncilModel[]>([]);
  const [selectedModels, setSelectedModels]   = useState<string[]>([]);
  const [presidentModel, setPresidentModel]   = useState('');
  const [presidentModelName, setPresidentModelName] = useState('Claude Opus 4.6');
  const [loadingModels, setLoadingModels]     = useState(true);
  const [running, setRunning]             = useState(false);
  const [result, setResult]               = useState<CouncilResult | null>(null);
  const [verificationMode, setVerificationMode] = useState<'off' | 'standard' | 'deep'>('standard');
  const [verificationMetadata, setVerificationMetadata] = useState<any>(null);
  const [showVerificationPanel, setShowVerificationPanel] = useState(false);
  const [webSearch, setWebSearch] = useState(false);
  const [webSearchTenantEnabled, setWebSearchTenantEnabled] = useState(true);
  const [webSources, setWebSources] = useState<{ index: number; title: string; url: string }[]>([]);
  const [chatWebSources, setChatWebSources] = useState<{ index: number; title: string; url: string }[]>([]);
  const [chatContextMeta, setChatContextMeta] = useState<{ input_tokens: number; max_context_tokens: number; context_usage_pct: number } | null>(null);
  const [chatHandoffLoading, setChatHandoffLoading] = useState(false);
  const [showPromptHelper, setShowPromptHelper] = useState(false);
  const [promptDescription, setPromptDescription] = useState('');
  const [generatingPrompt, setGeneratingPrompt] = useState(false);
  const [generatedPromptPreview, setGeneratedPromptPreview] = useState('');
  const [sessions, setSessions]           = useState<SessionSummary[]>([]);
  const [strategySessions, setStrategySessions] = useState<{ id: number; title: string; prompt: string; mode_id: string; mode_name: string; created_at: string }[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showHistory, setShowHistory]     = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [isCollaboratorSession, setIsCollaboratorSession] = useState(false);
  const [uploadedDocs, setUploadedDocs]   = useState<{ name: string; text: string; file?: File; extractionFailed?: boolean; extractionError?: string | null }[]>([]);
  const [smartStartPrefill, setSmartStartPrefill] = useState<{
    count: number;
    sessionTitle: string | null;
    sessionUpdatedAt: string | null;
  } | null>(null);
  const [pendingSmartStartDocs, setPendingSmartStartDocs] = useState<{
    docs: { name: string; text: string }[];
    count: number;
    sessionTitle: string | null;
    sessionUpdatedAt: string | null;
    sessionId: number | null;
  } | null>(null);
  const [showSmartStartModal, setShowSmartStartModal] = useState(false);
  const [largeInputWarning, setLargeInputWarning] = useState<{ totalChars: number; fileCount: number; onConfirm: () => void } | null>(null);
  const [truncationWarning, setTruncationWarning] = useState<{ models: string[]; largeModels: string[]; totalChars: number; limitChars: number; onConfirm: () => void; onDropSmall: (() => void) | null } | null>(null);
  // Blocking modal for failed-extraction source documents (0 chars extracted).
  // Requires the user to type an exact confirmation phrase — not a checkbox — before
  // the run can proceed, per the "AI Council should not run on a partial corpus
  // without explicit acknowledgment" requirement. failedDocNames is captured at the
  // moment Run is clicked so the modal always reflects what will actually be excluded.
  const [failedExtractionWarning, setFailedExtractionWarning] = useState<{ failedDocNames: string[]; onConfirm: () => void } | null>(null);
  const [failedExtractionConfirmText, setFailedExtractionConfirmText] = useState('');
  const FAILED_EXTRACTION_CONFIRM_PHRASE = 'I understand this output will exclude these files';
  // Tracks the exact set of failed-doc names the user has already typed the
  // confirmation phrase for, so re-invoking handleRunPipelineStep1 after they
  // confirm doesn't re-trigger the same modal in an infinite loop. Cleared
  // whenever the uploaded-docs list changes (handled via the filter below —
  // if a NEW failed doc appears that wasn't in the acknowledged set, the gate
  // re-fires for it).
  const acknowledgedFailedDocsRef = useRef<Set<string>>(new Set());
  const [expandedDocIndex, setExpandedDocIndex] = useState<number | null>(null);
  const [copiedDocIndex, setCopiedDocIndex] = useState<number | null>(null);
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [brandingList, setBrandingList] = useState<Array<{ tagId: number; companyName: string; primaryColor: string; tagName: string | null; hasLogo: boolean; projectId?: number; projectName?: string }>>([]);
  const [selectedBrandingTagId, setSelectedBrandingTagId] = useState<number | null>(null);
  // Opt-in only — routes the chat PPTX export through Anthropic's hosted pptx
  // skill (Claude designs and runs its own python-pptx code) instead of the
  // default Sonnet-outline + pptxgenjs pipeline. Costs meaningfully more per
  // export, so it defaults off and is never auto-enabled.
  const [enhancedVisualization, setEnhancedVisualization] = useState(false);
  const [chatAttachments, setChatAttachments] = useState<{ name: string; text: string }[]>([]);
  const [clarifyQuestions, setClarifyQuestions] = useState<string[]>([]);
  const [clarifyAnswers, setClarifyAnswers] = useState<Record<number, string>>({});
  const [clarifyLoading, setClarifyLoading] = useState(false);
  const [showClarifyPanel, setShowClarifyPanel] = useState(false);
  const [chatAttachLoading, setChatAttachLoading] = useState(false);
  const [chatAttachMenuOpen, setChatAttachMenuOpen] = useState(false);
  const [pptxGenerating, setPptxGenerating] = useState(false);
  const [pptxPhaseStep, setPptxPhaseStep] = useState(0);
  const [excelGenerating, setExcelGenerating] = useState(false);
  const [wordGenerating, setWordGenerating] = useState(false);
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  const [chatMode, setChatMode] = useState<'chat' | 'pptx' | 'excel' | 'word'>('chat');
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const chatImageInputRef = useRef<HTMLInputElement>(null);
  const [showDataAnalystBanner, setShowDataAnalystBanner] = useState(false);
  const [xlsWarning, setXlsWarning] = useState<string[]>([]);
  const [uploadingDoc, setUploadingDoc]   = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsRef    = useRef<HTMLDivElement>(null);
  const promptRef     = useRef<HTMLTextAreaElement | null>(null);
  const inputDataRef  = useRef<HTMLTextAreaElement | null>(null);
  // Carries Intelligence→Council data from location.key effect → currentProject effect
  const intelIntentRef = useRef<{ prompt: string; inputData: string } | null>(null);
  // True on the very first currentProject effect fire (page load/refresh) — skip prompt clear so localStorage restore survives
  const isFirstProjectLoadRef = useRef(true);
  const prevProjectIdRef = useRef<number | null | undefined>(undefined);
  // Project data toggles — ON means fetch & send to AI silently
  // 'interviews' is the project's Interview Intelligence findings — the same report the
  // User Research Synthesis tile renders, handed to the Council as source material instead of
  // being re-extracted from transcripts a second time (M5, Asana 1216725280641177).
  const [toggles, setToggles] = useState({ info: false, risks: false, processes: false, interviews: false });
  const [toggleLoading, setToggleLoading] = useState({ info: false, risks: false, processes: false });
  const [projectCache, setProjectCache] = useState({ info: '', risks: '', processes: '', interviews: '' });

  // Multi-Angle Analysis mode
  const [activeMode, setActiveMode] = useState<'council' | 'strategy-synthesis'>('council');
  const [strategySynthesisResult, setStrategySynthesisResult] = useState<StrategySynthesisResult | null>(null);
  // What the ingestion is doing right now. Null when nothing is being ingested — extraction
  // runs for minutes per transcript, and a button that just says "Running…" for eight minutes
  // is indistinguishable from one that has hung (M5, Asana 1216725280641177).
  const [synthesisIngestStatus, setSynthesisIngestStatus] = useState<string | null>(null);


  const [runningStrategySynthesis, setRunningStrategySynthesis] = useState(false);
  const [selectedSubTool, setSelectedSubTool] = useState<StrategicSubTool | null>(null);

  // User Research Synthesis reads the project's ingested interviews; it does not read the
  // prompt. A field whose value is discarded is worse than no field — the consultant
  // reasonably assumes it steered the report — so Ryan asked for it removed outright rather
  // than marked optional (M5, Asana 1216725280641177).
  //
  // ASK THE SERVER, DO NOT RE-DERIVE. This was computed here from the tool id and the open
  // project, which is two thirds of the server's rule — the third part is the cutover flag,
  // which the browser cannot see. With the flag off the label read "optional", an empty
  // prompt went through, and /analyze answered 400 "Prompt is required". The server owns the
  // rule now (routes/intelligence.js promptIsOptional) and answers it directly.
  // Three states, not two. A boolean forces a wrong answer to be displayed while the real one
  // is in flight, and the wrong answer is whatever the PREVIOUS tool needed: switching to
  // Roadmap Narrative showed no prompt for two seconds (it needs one), and switching back
  // showed a required field with a red asterisk (it does not). Neither is a default that can
  // be chosen better — the honest state is "not known yet", and it renders as a placeholder.
  type PromptRequirement = 'unknown' | 'required' | 'optional';
  const [promptRequirement, setPromptRequirement] = useState<PromptRequirement>('unknown');
  const promptOptionalForTool = promptRequirement === 'optional';

  useEffect(() => {
    const mode = selectedSubTool?.id;
    // Reset FIRST and synchronously. Leaving the previous answer up until the response lands
    // is the flicker itself, not a symptom of it.
    setPromptRequirement('unknown');
    if (!mode) return;
    let cancelled = false;
    api.get('/api/intelligence/prompt-requirement', {
      params: { mode, projectId: currentProject?.id ?? '' },
    })
      .then(res => {
        if (!cancelled) setPromptRequirement(res.data?.data?.promptOptional ? 'optional' : 'required');
      })
      // Required is the safe fallback: a prompt field nobody needed is a smaller failure than
      // a missing one the request then rejects.
      .catch(() => { if (!cancelled) setPromptRequirement('required'); });
    return () => { cancelled = true; };
  }, [selectedSubTool?.id, currentProject?.id]);

  const AUTO_ATTACH_TOOL_IDS = useMemo(() => new Set([
    'roadmap-narrative',
    'stakeholder-influence-mapper',
    'okr-cascade-generator',
    'change-management-suite',
  ]), []);

  const [ursCorpus, setUrsCorpus] = useState<UrsCorpus>({ usable: [], inProgress: [], needsAttention: [] });
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [removingInterviewId, setRemovingInterviewId] = useState<string | null>(null);
  const [skipInterviewContext, setSkipInterviewContext] = useState(false);
  const projectIngest = useProjectIngestJob();
  const thenAnalyzeRef = useRef(false);

  useEffect(() => { setSkipInterviewContext(false); }, [selectedSubTool?.id]);

  const refreshInterviewIssues = useCallback(async () => {
    const projectId = currentProject?.id;
    if (!projectId || activeMode !== 'strategy-synthesis') {
      setUrsCorpus({ usable: [], inProgress: [], needsAttention: [] });
      return;
    }
    try {
      const res = await api.get('/api/intelligence/interviews', { params: { projectId } });
      const data = res.data?.data || {};
      const usable: UrsInterview[] = data.usable || [];
      setUrsCorpus({
        usable,
        inProgress: data.inProgress || [],
        needsAttention: data.needsAttention || [],
      });
      setSelectedSourceIds((prev) => {
        const live = usable.map((u) => u.source_id);
        if (prev.length === 0) return live;
        const liveSet = new Set(live);
        const kept = prev.filter((id) => liveSet.has(id));
        const added = live.filter((id) => !prev.includes(id));
        return [...kept, ...added];
      });
    } catch {
      setUrsCorpus({ usable: [], inProgress: [], needsAttention: [] });
    }
  }, [currentProject?.id, activeMode]);

  useEffect(() => { refreshInterviewIssues(); }, [refreshInterviewIssues]);

  useEffect(() => {
    if (!currentProject?.id) return;
    try {
      thenAnalyzeRef.current = sessionStorage.getItem(`urs_then_analyze_${currentProject.id}`) === '1';
    } catch { thenAnalyzeRef.current = false; }
    void projectIngest.resume(currentProject.id);
    // resume is stable enough for mount/project change; do not depend on the whole hook object
  }, [currentProject?.id]); // eslint-disable-line

  useEffect(() => {
    if (!projectIngest.ingesting) return;
    const t = setInterval(() => { void refreshInterviewIssues(); }, 4000);
    return () => clearInterval(t);
  }, [projectIngest.ingesting, refreshInterviewIssues]);

  // window.confirm draws browser chrome that lives outside the document — it looks nothing
  // like the rest of the app, a user can switch it off for the page, and headless drivers
  // dismiss it by default, which silently turns a confirmation into a cancel. ConfirmDialog
  // renders a real element with role="dialog" (see its own note).
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);

  const handleRemoveInterview = (issue: UrsInterview) => {
    setConfirmRequest({
      title: `Retry "${issue.filename}"?`,
      message: 'It never finished extracting, so nothing in the report comes from it. '
        + 'Retrying removes the stuck row so you can upload the same file again. '
        + 'Interviews that already finished stay in the taxonomy.',
      confirmLabel: 'Retry',
      tone: 'danger',
      onConfirm: () => { void removeInterview(issue); },
    });
  };

  const removeInterview = async (issue: UrsInterview) => {
    setRemovingInterviewId(issue.source_id);
    try {
      const res = await api.delete(`/api/intelligence/interviews/${issue.source_id}`, {
        params: { projectId: currentProject?.id },
      });
      toast.success(res.data?.data?.message || 'Removed. You can upload this transcript again.');
      await refreshInterviewIssues();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Could not remove that interview.');
    } finally {
      setRemovingInterviewId(null);
    }
  };
  // Strategic-tool tile help popover (matches the Jarvis Intelligence tile pattern: thin strips,
  // no visible description, a "?" that reveals the details on hover).
  const [subtoolHelpId, setSubtoolHelpId] = useState<string | null>(null);
  const [subtoolHelpPos, setSubtoolHelpPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [synthesisPrompt, setSynthesisPrompt] = useState('');
  const [synthesisFiles, setSynthesisFiles] = useState<File[]>([]);
  const synthesisFileInputRef = useRef<HTMLInputElement>(null);

  // Implementation Review (Second Council)
  const [implementationPersonas, setImplementationPersonas] = useState<Persona[]>(DEFAULT_IMPLEMENTATION_PERSONAS);
  // Default to Sonnet (Standard) — Premium toggle elevates to Opus when explicitly chosen.
  const [implementationModel, setImplementationModel] = useState('claude-sonnet-4-6');
  const [implementationResult, setImplementationResult] = useState<ImplementationReviewResult | null>(null);
  const [runningImplementation, setRunningImplementation] = useState(false);
  const [showImplementationConfig, setShowImplementationConfig] = useState(false);
  const [showImplementationReview, setShowImplementationReview] = useState(false);
  const [pressureTestDismissed, setPressureTestDismissed] = useState(false);
  const implementationRef = useRef<HTMLDivElement>(null);

  // Full Pipeline (step-by-step auto-chain: Steps 1→2→3→4→5)
  const [pipelineStep, setPipelineStep] = useState<0|1|2|3|4|5>(0); // 0=idle, 1-4=running, 5=done
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineBackgroundProcessing, setPipelineBackgroundProcessing] = useState(false); // network dropped but backend still running
  const [pipelineData, setPipelineData] = useState<{
    memberResults: MemberResult[] | null;
    presidentEvaluation: PresidentEvaluation | null;
    personaResults: ImplementationPersonaResult[] | null;
    finalSynthesis: FinalSynthesis | null;
    councilOutput: string;
    step1Failed: boolean;
    step2Failed: boolean;
    step3Failed: boolean;
    step4Failed: boolean;
  }>({ memberResults: null, presidentEvaluation: null, personaResults: null, finalSynthesis: null, councilOutput: '', step1Failed: false, step2Failed: false, step3Failed: false, step4Failed: false });
  // Snapshot of docs excluded (0-char extraction) for the pipeline-mode run, captured at run time
  // (or restored from a loaded session's input_data). Kept separate from live uploadedDocs so the
  // pipeline "excluded documents" banner survives session switch / "Sync from server" / new uploads —
  // the same reason CouncilResult carries excludedDocNames for standard-council mode.
  const [pipelineExcludedDocNames, setPipelineExcludedDocNames] = useState<string[]>([]);
  const [pipelinePrompt, setPipelinePrompt] = useState('');
  const [pipelineSessionId, setPipelineSessionId] = useState<number | null>(null);
  // Refs so polling interval always reads the latest sessionId without stale closures
  const pipelineSessionIdRef = useRef<number | null>(null);
  const activeSessionIdRef = useRef<number | null>(null);
  // Latest agreed slide structure for the executive presentation (persisted in
  // session summary). Restored on load so every export reflects the latest version.
  const [presentationStructure, setPresentationStructure] = useState<SlideOutlineItem[] | null>(null);
  const pipelineRef = useRef<HTMLDivElement>(null);

  const finalCouncilResponse =
    pipelineData?.finalSynthesis
    || pipelineData?.presidentEvaluation?.finalResponse
    || pipelineData?.councilOutput
    || '';
  const smartRouter = useSmartRouterStepSync({
    autoSyncSignal: finalCouncilResponse ? (pipelineSessionId || 'council-result') : null,
    autoSyncArtifact: finalCouncilResponse
      ? {
          kind: 'ai_council_session',
          council_session_id: pipelineSessionId,
          final_response: typeof finalCouncilResponse === 'string'
            ? finalCouncilResponse.slice(0, 4000)
            : null,
        }
      : undefined,
    autoSyncSummary: finalCouncilResponse
      ? `AI Council produced a synthesized opinion${pipelineSessionId ? ` (session #${pipelineSessionId})` : ''}.`
      : undefined,
  });
  const routerSession = useSmartRouterSessionContext();
  const goalPrefillRef = useRef(false);
  useEffect(() => {
    if (goalPrefillRef.current) return;
    if (!routerSession.inRouter || !routerSession.userGoal) return;
    goalPrefillRef.current = true;
    setPrompt((prev: string) => (prev.trim() ? prev : routerSession.userGoal!));
  }, [routerSession.inRouter, routerSession.userGoal]);

  useEffect(() => {
    if (!pptxGenerating) { setPptxPhaseStep(0); return; }
    const id = setInterval(() => {
      setPptxPhaseStep((s) => (s < PPTX_PHASE_LABELS.length - 1 ? s + 1 : s));
    }, 9000);
    return () => clearInterval(id);
  }, [pptxGenerating]);
  const docPrefillRef = useRef<number | null>(null);
  useEffect(() => {
    if (!routerSession.inRouter || routerSession.sessionId == null) return;
    if (routerSession.attachedDocuments.length === 0) return;
    if (docPrefillRef.current === routerSession.sessionId) return;
    docPrefillRef.current = routerSession.sessionId;
    setUploadedDocs((prev) => {
      const have = new Set(prev.map((d) => d.name));
      const incoming = routerSession.attachedDocuments
        .filter((d) => typeof d.text === 'string' && d.text.length > 0)
        .map((d) => ({ name: `${d.name.replace(/\.[^.]+$/, '')}.txt`, text: d.text }))
        .filter((d) => !have.has(d.name))
        .map((d) => ({
          name: d.name,
          text: d.text,
          file: new File([new Blob([d.text], { type: 'text/plain' })], d.name, { type: 'text/plain' }),
        }));
      return incoming.length > 0 ? [...prev, ...incoming] : prev;
    });
  }, [routerSession.inRouter, routerSession.sessionId, routerSession.attachedDocuments]);
  const [collapsedSteps, setCollapsedSteps] = useState<Set<number>>(new Set());
  const toggleStepCollapse = (step: number) =>
    setCollapsedSteps(prev => { const next = new Set(prev); next.has(step) ? next.delete(step) : next.add(step); return next; });
  const [formCollapsed, setFormCollapsed] = useState(false);

  // Track which pipeline result keys we've already auto-collapsed for. Auto-collapse should
  // only fire on null→present transitions (a step newly finishing), NOT when silent poll /
  // "Sync from server" reloads rewrite the same objects with new references — that was
  // collapsing expanded result panels every ~30s for no functional reason.
  const autoCollapsedForRef = useRef({
    memberResults: false,
    presidentEvaluation: false,
    personaResults: false,
    finalSynthesis: false,
  });
  const resetAutoCollapseTracking = () => {
    autoCollapsedForRef.current = {
      memberResults: false,
      presidentEvaluation: false,
      personaResults: false,
      finalSynthesis: false,
    };
  };

  // Auto-collapse previous step only when the next step's results first arrive
  useEffect(() => {
    if (!pipelineData.memberResults) {
      autoCollapsedForRef.current.memberResults = false;
      return;
    }
    if (!autoCollapsedForRef.current.memberResults) {
      autoCollapsedForRef.current.memberResults = true;
      setFormCollapsed(true);
    }
  }, [pipelineData.memberResults]);
  useEffect(() => {
    if (!pipelineData.presidentEvaluation) {
      autoCollapsedForRef.current.presidentEvaluation = false;
      return;
    }
    if (!autoCollapsedForRef.current.presidentEvaluation) {
      autoCollapsedForRef.current.presidentEvaluation = true;
      setCollapsedSteps(prev => { const next = new Set(prev); next.add(1); return next; });
    }
  }, [pipelineData.presidentEvaluation]);
  useEffect(() => {
    if (!pipelineData.personaResults) {
      autoCollapsedForRef.current.personaResults = false;
      return;
    }
    if (!autoCollapsedForRef.current.personaResults) {
      autoCollapsedForRef.current.personaResults = true;
      setCollapsedSteps(prev => { const next = new Set(prev); next.add(2); return next; });
    }
  }, [pipelineData.personaResults]);
  useEffect(() => {
    if (!pipelineData.finalSynthesis) {
      autoCollapsedForRef.current.finalSynthesis = false;
      return;
    }
    if (!autoCollapsedForRef.current.finalSynthesis) {
      autoCollapsedForRef.current.finalSynthesis = true;
      setCollapsedSteps(prev => { const next = new Set(prev); next.add(3); return next; });
    }
  }, [pipelineData.finalSynthesis]);

  // Load branding list once on mount
  useEffect(() => {
    api.get('/api/ai-council/branding-list').then(res => {
      if (res.data?.success && Array.isArray(res.data.data)) setBrandingList(res.data.data);
    }).catch(() => {});
  }, []);

  // Sources tab state
  const [sourceTab, setSourceTab] = useState<'upload' | 'paste' | 'project'>('upload');

  // Chat / refine state
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // ── Persist prompt/input/models to localStorage ───────────────────────────


  // ── Fetch available models on mount ──────────────────────────────────────────
  useEffect(() => {
    fetchModels();
    api.get('/api/ai-council/tenant-config').then(res => {
      setWebSearchTenantEnabled(res.data.data?.web_search_enabled !== false);
    }).catch(() => {});
  }, []);


  // ── Fetch session history when project changes ─────────────────────────────
  useEffect(() => {
    console.log('[AICouncil] currentProject effect fired | project id:', currentProject?.id, '| mountIntent:', mountIntentRef.current, '| intelIntent:', intelIntentRef.current);
    if (currentProject?.id) {
      const mountIntent = mountIntentRef.current as any;
      const intelIntent = intelIntentRef.current;

      if (mountIntent?.prompt) {
        // Mount-time sessionStorage intent (legacy path)
        console.log('[AICouncil] currentProject effect — restoring mountIntent');
        isFirstProjectLoadRef.current = false;
        setResult(null);
        setActiveSessionId(null);
        setPrompt(mountIntent.prompt);
        setInputData(mountIntent.inputData || '');
        mountIntentRef.current = null;
        fetchSessions(false);
      } else if (intelIntent) {
        // Intelligence page navigated here — data already set by location.key effect, don't clear it
        console.log('[AICouncil] currentProject effect — intelIntent present, preserving prompt/inputData');
        isFirstProjectLoadRef.current = false;
        intelIntentRef.current = null;
        setSourceTab('paste');
        fetchSessions(false);
      } else {
        // Normal navigation — clear state, load history list only (no auto-load)
        setResult(null);
        setActiveSessionId(null);
        if (isFirstProjectLoadRef.current) {
          // First fire = page refresh — keep the localStorage-restored prompt, don't wipe it
          isFirstProjectLoadRef.current = false;
          prevProjectIdRef.current = currentProject?.id;
        } else {
      
          const isRealSwitch = prevProjectIdRef.current != null && prevProjectIdRef.current !== currentProject?.id;
          if (isRealSwitch) {
         
            if (orchestratorCtx.inRouter) {
              const scope = orchestratorCtx.extraParams?.scope || '';
              const purpose = orchestratorCtx.extraParams?.purpose || '';
              const audience = orchestratorCtx.extraParams?.audience || '';
              const parts: string[] = [];
              if (scope) parts.push(scope);
              if (purpose) parts.push(`Purpose: ${purpose}.`);
              if (audience) parts.push(`Audience: ${audience}.`);
              setPrompt(parts.length > 0 ? parts.join('\n\n') : '');
              setInputData('');
            } else {
              setPrompt('');
              setInputData('');
            }
          }
          prevProjectIdRef.current = currentProject?.id;
        }
        fetchSessions(false);
      }
    } else {
      // No project selected — still load all sessions for this user
      fetchSessions(false);
    }
  }, [currentProject?.id]);

  useEffect(() => {
    if (orchestratorCtx.inRouter) return;
    if (!currentProject?.id) return;
    if (uploadedDocs.length > 0) return;

    let cancelled = false;
    (async () => {
      try {
        let documents: { name: string; text: string }[] = [];
        let sessionTitle: string | null = null;
        let sessionUpdatedAt: string | null = null;
        let sessionId: number | null = null;

        {
          const result = await getLatestSmartStartDocuments(currentProject?.id ?? null);
          if (cancelled) return;
          if (!Array.isArray(result.documents) || result.documents.length === 0) return;
          documents = result.documents.map(d => ({ name: d.name, text: d.text }));
          sessionTitle = result.session_title;
          sessionUpdatedAt = result.session_updated_at;
          sessionId = result.session_id;
        }

        if (sessionId && localStorage.getItem(`smartstart_prefill_seen_${sessionId}`)) return;

        setPendingSmartStartDocs({
          docs: documents,
          count: documents.length,
          sessionTitle,
          sessionUpdatedAt,
          sessionId,
        });
      } catch (err) {
        // Non-blocking — user can still upload manually if prefill fails.
        console.warn('[AICouncil] SmartStart docs prefill failed', err);
      }
    })();

    return () => { cancelled = true; };
  }, [currentProject?.id]);

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const res = await api.get('/api/ai-council/models');
      const { models, presidentModel: president, configuredPresidentModel, presidentModels } = res.data;
      setAvailableModels(models);
      const activePresident = configuredPresidentModel || president;
      setPresidentModel(activePresident);
      const presidentName = (presidentModels || []).find((m: { id: string; name: string }) => m.id === activePresident)?.name
        || models.find((m: CouncilModel) => m.id === activePresident)?.name
        || activePresident;
      if (presidentName) setPresidentModelName(presidentName);
     
      if (selectedModels.length === 0) {
        const defaults = ['claude-sonnet-4-6', 'gpt-4.1', 'gemini-2.5-pro'];
        const available = models.filter((m: CouncilModel) => m.available && !m.isPresident).map((m: CouncilModel) => m.id);
        setSelectedModels(defaults.filter(id => available.includes(id)));
      }
    } catch (err) {
      toast.error('Failed to load available models');
    } finally {
      setLoadingModels(false);
    }
  };

  // ── Fetch past sessions list — all sessions, no project filter ────────────
  // STRATEGIC_SUBTOOLS mode IDs — used to filter Intelligence sessions into the strategy tab
  const STRATEGY_MODE_IDS = new Set(STRATEGIC_SUBTOOLS.map(t => t.id));

  const fetchSessions = async (autoLoadLatest = false) => {
    setLoadingSessions(true);
    try {
      const modeIds = Array.from(STRATEGY_MODE_IDS).join(',');
      const [councilRes, intelRes] = await Promise.all([
        api.get('/api/ai-council/sessions'),
        api.get('/api/intelligence/sessions', { params: { mode_ids: modeIds, limit: 200 } }).catch(() => ({ data: { sessions: [] } })),
      ]);
      const list: SessionSummary[] = councilRes.data.sessions || [];
      setSessions(list);
      // Filter Intelligence sessions to only strategy-synthesis sub-tool modes
      const stratList = (intelRes.data.sessions || []).filter((s: any) => STRATEGY_MODE_IDS.has(s.mode_id));
      setStrategySessions(stratList);
      setLoadingSessions(false);
      if (autoLoadLatest && list.length > 0) {
        await loadSessionById(list[0].id);
      }
    } catch {
      setLoadingSessions(false);
    }
  };

  // Keep refs in sync so the polling interval always reads latest values without stale closures
  useEffect(() => { pipelineSessionIdRef.current = pipelineSessionId; }, [pipelineSessionId]);
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);
  const pipelineStepRef = useRef<number>(0);
  useEffect(() => { pipelineStepRef.current = pipelineStep; }, [pipelineStep]);
  const pipelineDataRef = useRef(pipelineData);
  useEffect(() => { pipelineDataRef.current = pipelineData; }, [pipelineData]);
  const isCollaboratorSessionRef = useRef<boolean>(false);
  useEffect(() => { isCollaboratorSessionRef.current = isCollaboratorSession; }, [isCollaboratorSession]);

  // ── Auto-refresh every 30 s — single persistent interval for ALL users ──────
  // Empty dep array = one interval for the component lifetime.
  // All live values read via refs — never stale. Works for owner AND collaborators.
  useEffect(() => {
    const interval = setInterval(async () => {
      const currentSid = pipelineSessionIdRef.current || activeSessionIdRef.current;
      if (!currentSid) return; // no session open — nothing to poll

      const currentStep = pipelineStepRef.current;
      const local = pipelineDataRef.current;

      fetchSessions(false);
      try {
        const res = await api.get(`/api/ai-council/sessions/${currentSid}`);
        const s = res.data.session;
        if (!s) return;
        const summary = s.summary || {};
        const hasMemberResults  = Array.isArray(s.member_results) && s.member_results.length > 0;
        const hasPresidentEval  = !!s.president_evaluation;
        const hasPersonaResults = Array.isArray(summary.personaResults) && summary.personaResults.length > 0;
        const hasFinalSynthesis = !!summary.finalSynthesis;
        const dbStep = summary.pipeline_step || 0;

        // Only reload when the server has progress we don't already have on screen.
        // The previous check (e.g. currentStep===2 && hasPresidentEval) was true on
        // every tick for completed sessions, forcing loadSessionById → collapse reset.
        if (hasNewPipelineProgress(
          local,
          { hasMemberResults, hasPresidentEval, hasPersonaResults, hasFinalSynthesis, dbStep },
          currentStep,
        )) {
          // Step done — restore state, then clear spinner and banner
          await loadSessionById(currentSid, true, false); // clearRunning=false — we clear manually below
          setPipelineRunning(false);
          setPipelineBackgroundProcessing(false);
        }
      } catch { /* silent */ }
    }, 30000);
    return () => clearInterval(interval);
  }, []); // single interval for component lifetime — all live values read via refs

  // Live push: when this user is added as a collaborator (or any other
  // notification arrives), refresh the session list immediately — without
  // this the "shared" badge and collaborator names wouldn't appear until the
  // next manual History reopen.
  const { subscribeToLiveNotifications } = useNotifications();
  useEffect(() => {
    return subscribeToLiveNotifications(() => fetchSessions());
  }, [subscribeToLiveNotifications]); // eslint-disable-line

  // Deep-link: open a specific session directly, e.g. from a collaborator-added
  // notification link ("/app/ai-council?session=123"). Depends on
  // location.search (not just mount) because clicking a notification while
  // already on this page is a client-side navigate, not a remount — without
  // this the effect only ever fired once and ignored subsequent clicks.
  useEffect(() => {
    const sessionParam = new URLSearchParams(location.search).get('session');
    if (sessionParam) {
      const sessionId = parseInt(sessionParam, 10);
      if (!Number.isNaN(sessionId)) loadSession(sessionId);
    }
  }, [location.search]); // eslint-disable-line

  // ── Load a past session by ID (internal — no toast on silent load) ────────
  const loadSessionById = async (id: number, silent = true, clearRunning = true) => {
    try {
      const res = await api.get(`/api/ai-council/sessions/${id}`);
      const s = res.data.session;
      console.log('[loadSessionById] id=', id, 'session_type=', s?.summary?.session_type, 'pipeline_step=', s?.summary?.pipeline_step, 'member_results=', Array.isArray(s?.member_results) ? s.member_results.length : s?.member_results, 'president_evaluation=', !!s?.president_evaluation, 'personaResults=', Array.isArray(s?.summary?.personaResults) ? s.summary.personaResults.length : null, 'finalSynthesis=', !!s?.summary?.finalSynthesis);
      setPrompt(s.prompt || '');
      setInputData(s.input_data || '');
      setActiveSessionId(id);
      setIsCollaboratorSession(!!s.is_collaborator);

      // Restore chat history
      const chatHistory = s.summary?.chatHistory;
      if (Array.isArray(chatHistory) && chatHistory.length > 0) {
        setChatMessages(chatHistory);
      } else {
        setChatMessages([]);
      }

      // Restore uploaded docs from input_data by parsing --- Document: filename --- markers.
      // A failed-extraction doc's block body is the literal "[EXCLUDED: ...]" marker (see
      // buildCombinedData) — detect that here and restore extractionFailed:true, otherwise a
      // reloaded/switched-to session loses the flag entirely and the excluded-documents banner
      // (which reads uploadedDocs, not input_data) silently stops appearing for a session that
      // genuinely excluded files at run time.
      let restoredExcludedDocNames: string[] = [];
      if (s.input_data) {
        const docRegex = /--- Document: (.+?) ---\n([\s\S]*?)(?=--- Document: |$)/g;
        const restoredDocs: { name: string; text: string; extractionFailed?: boolean }[] = [];
        let match;
        while ((match = docRegex.exec(s.input_data)) !== null) {
          const name = match[1].trim();
          const body = match[2].trim();
          const failed = body.startsWith('[EXCLUDED:');
          restoredDocs.push({ name, text: failed ? '' : body, extractionFailed: failed });
        }
        if (restoredDocs.length > 0) setUploadedDocs(restoredDocs);
        restoredExcludedDocNames = restoredDocs.filter(d => d.extractionFailed).map(d => d.name);
      }

      const isPipeline = s.summary?.session_type === 'pipeline';
      console.log('[loadSessionById] isPipeline=', isPipeline, 'summary=', JSON.stringify(s?.summary)?.slice(0, 200));

      if (isPipeline) {
        // Restore pipeline state
        setActiveMode('council');
        setResult(null);
        setStrategySynthesisResult(null);
        const summary = s.summary || {};
        const restoredMemberResults = s.member_results || null;
        const restoredPresidentEvaluation = s.president_evaluation || null;
        const restoredPersonaResults = summary.personaResults || null;
        const restoredFinalSynthesis = summary.finalSynthesis || null;
        const restoredCouncilOutput = restoredPresidentEvaluation?.finalResponse || s.president_result?.response || '';
        const restoredPrompt = s.prompt || '';
        setPipelinePrompt(restoredPrompt);
        setPipelineSessionId(id);
        setPresentationStructure(Array.isArray(summary.presentationStructure) ? summary.presentationStructure : null);
        setPipelineData({
          memberResults: restoredMemberResults,
          presidentEvaluation: restoredPresidentEvaluation,
          personaResults: restoredPersonaResults,
          finalSynthesis: restoredFinalSynthesis,
          councilOutput: restoredCouncilOutput,
          step1Failed: false,
          step2Failed: false,
          step3Failed: false,
          step4Failed: false,
        });
        // Restore the excluded-docs banner for a reloaded pipeline session (parsed from input_data's
        // [EXCLUDED: ...] markers above) — otherwise it silently vanishes on session switch/reload.
        setPipelineExcludedDocNames(restoredExcludedDocNames);
        // Set pipelineStep to last completed step — derive from actual data, not just summary.pipeline_step
        let completedStep: 0|1|2|3|4|5 = 0;
        if (restoredMemberResults) completedStep = 1;
        if (restoredPresidentEvaluation) completedStep = 2;
        if (restoredPersonaResults) completedStep = 3;
        if (restoredFinalSynthesis) completedStep = 5; // 5 = fully done
        // Fallback to DB-stored step if no data yet (e.g. step in progress)
        if (completedStep === 0 && summary.pipeline_step) completedStep = summary.pipeline_step as 0|1|2|3|4|5;
        console.log('[loadSessionById] setting pipelineStep=', completedStep, 'pipelineData.memberResults=', !!restoredMemberResults);
        setPipelineStep(completedStep);
        if (clearRunning) setPipelineRunning(false);
        // Collapse completed steps only when switching to a different session (or first
        // open). Same-session silent reloads (30s poll / Sync from server) must preserve
        // the user's expanded/collapsed accordion choices — otherwise result panels slam
        // shut every poll tick even though nothing functionally changed.
        if (shouldResetCollapsedStepsOnLoad(
          id,
          pipelineSessionIdRef.current,
          activeSessionIdRef.current,
        )) {
          const completedStepsToCollapse = new Set<number>();
          if (restoredMemberResults) completedStepsToCollapse.add(1);
          if (restoredPresidentEvaluation) completedStepsToCollapse.add(2);
          if (restoredPersonaResults) completedStepsToCollapse.add(3);
          if (restoredFinalSynthesis) completedStepsToCollapse.add(4);
          setCollapsedSteps(completedStepsToCollapse);
          setFormCollapsed(true);
          // Mark auto-collapse as already applied for restored keys so the transition
          // effects don't immediately re-collapse after the user expands a panel.
          autoCollapsedForRef.current = {
            memberResults: !!restoredMemberResults,
            presidentEvaluation: !!restoredPresidentEvaluation,
            personaResults: !!restoredPersonaResults,
            finalSynthesis: !!restoredFinalSynthesis,
          };
        }
      } else if (s.summary?.session_type === 'strategy-synthesis') {
        // Restore Strategy Synthesis session — switch to strategy-synthesis tab and
        // re-render the result card. The analysis is stored in s.summary.analysis or
        // s.analysis depending on how the intelligence route saved it.
        setActiveMode('strategy-synthesis');
        setResult(null);
        setPipelineData({ memberResults: null, presidentEvaluation: null, personaResults: null, finalSynthesis: null, councilOutput: '', step1Failed: false, step2Failed: false, step3Failed: false, step4Failed: false });
        setShowImplementationReview(false);
        setPressureTestDismissed(false);
        setPipelineStep(0);
        const subToolId = s.summary?.subToolId || s.summary?.mode || '';
        const subToolName = s.summary?.subToolName || s.summary?.modeName || s.title || 'Strategy Synthesis';
        const matchedSubTool = STRATEGIC_SUBTOOLS.find(t => t.id === subToolId) || null;
        setSelectedSubTool(matchedSubTool);
        setSynthesisPrompt(s.prompt || '');
        setStrategySynthesisResult({
          subToolId,
          subToolName,
          response: s.summary?.analysis || s.summary?.result || s.analysis || '',
        });
      } else {
        // Restore standard council result
        setActiveMode('council');
        setStrategySynthesisResult(null);
        setResult({
          prompt: s.prompt,
          hasInputData: s.has_input_data,
          memberResults: s.member_results,
          presidentModel: s.president_model,
          presidentResult: s.president_result,
          presidentEvaluation: s.president_evaluation,
          summary: s.summary,
          excludedDocNames: restoredExcludedDocNames,
        });
        setPipelineData({ memberResults: null, presidentEvaluation: null, personaResults: null, finalSynthesis: null, councilOutput: '', step1Failed: false, step2Failed: false, step3Failed: false, step4Failed: false });
        setShowImplementationReview(false);
        setPressureTestDismissed(false);
        setPipelineStep(0);
      }

      // Resize textareas after content is set
      setTimeout(() => {
        if (promptRef.current) {
          promptRef.current.style.height = 'auto';
          promptRef.current.style.height = `${promptRef.current.scrollHeight}px`;
        }
        if (inputDataRef.current) {
          inputDataRef.current.style.height = 'auto';
          inputDataRef.current.style.height = `${inputDataRef.current.scrollHeight}px`;
        }
      }, 50);

      if (!silent) {
        setShowHistory(false);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      }
    } catch {
      if (!silent) toast.error('Failed to load session');
    }
  };

  // ── Load a past session (user-triggered) ──────────────────────────────────
  const loadSession = (id: number) => loadSessionById(id, false);

  // ── Load a Strategy Synthesis session from the Intelligence table ──────────
  const loadStrategySession = async (id: number) => {
    try {
      const res = await api.get(`/api/intelligence/sessions/${id}`);
      const s = res.data.session;
      const subToolId = s.mode_id || '';
      const subToolName = s.mode_name || s.title || 'Strategy Synthesis';
      const matchedSubTool = STRATEGIC_SUBTOOLS.find(t => t.id === subToolId) || null;
      setActiveMode('strategy-synthesis');
      setResult(null);
      setPipelineData({ memberResults: null, presidentEvaluation: null, personaResults: null, finalSynthesis: null, councilOutput: '', step1Failed: false, step2Failed: false, step3Failed: false, step4Failed: false });
      setShowImplementationReview(false);
      setPressureTestDismissed(false);
      setPipelineStep(0);
      setActiveSessionId(null);
      setSelectedSubTool(matchedSubTool);
      setSynthesisPrompt(s.prompt || '');
      setStrategySynthesisResult({
        subToolId,
        subToolName,
        response: s.analysis || s.result || '',
      });
      setShowHistory(false);
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    } catch {
      toast.error('Failed to load session');
    }
  };

  // ── Delete a session ───────────────────────────────────────────────────────
  const deleteSession = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await api.delete(`/api/ai-council/sessions/${id}`);
      setSessions(prev => prev.filter(s => s.id !== id));
      if (activeSessionId === id) { setResult(null); setActiveSessionId(null); }
      toast.success('Session deleted');
    } catch {
      toast.error('Failed to delete session');
    }
  };

  // ── Model checkbox toggle ──────────────────────────────────────────────────
  const toggleModel = (modelId: string) => {
    setSelectedModels(prev =>
      prev.includes(modelId)
        ? prev.filter(id => id !== modelId)
        : prev.length >= 6
          ? (toast.error('Maximum 6 council members allowed'), prev)
          : [...prev, modelId]
    );
  };

  // ── Summarize process map data — strips heavy metadata, keeps only essentials ──
  const summarizeProcessMap = (rawData: any): string => {
    const maps = rawData?.process_maps || rawData?.processMaps || rawData?.data || rawData || [];
    const arr = Array.isArray(maps) ? maps : [maps];
    const summary = arr.map((m: any) => {
      const processes = m?.map_data?.elements?.processes || [];
      const steps = processes.map((p: any) => ({
        step: p.step_number,
        name: p.step_name,
        assignee: p.assignee,
        description: p.detailed_description || p.hybrid_description,
        inputs: p.inputs,
        outputs: p.outputs,
        phase: p.phase,
      }));
      return {
        title: m.title || m.process_name,
        steps,
        roles: Object.keys(m?.map_data?.elements?.stakeholders || {}),
      };
    });
    return JSON.stringify(summary, null, 2);
  };

  // ── Summarize risk data — keeps only meaningful fields ────────────────────
  const summarizeRiskData = (rawData: any): string => {
    const summary = {
      overall_risk_score: rawData?.risk_metrics?.overall_risk_score,
      control_coverage: rawData?.risk_metrics?.control_coverage,
      risk_assessments: (rawData?.risk_assessments || []).map((r: any) => ({
        name: r.name || r.risk_name,
        category: r.category,
        likelihood: r.likelihood,
        impact: r.impact,
        risk_level: r.risk_level,
        controls: r.controls,
      })),
      recommendations: rawData?.recommendations,
    };
    return JSON.stringify(summary, null, 2);
  };

  // ── Toggle project data on/off — fetches when turned ON, clears when OFF ────
  const toggleProjectData = async (type: 'info' | 'risks' | 'processes' | 'interviews') => {
    const isCurrentlyOn = toggles[type];

    // Turn OFF — clear cache
    if (isCurrentlyOn) {
      setToggles(prev => ({ ...prev, [type]: false }));
      setProjectCache(prev => ({ ...prev, [type]: '' }));
      return;
    }

    // Turn ON — fetch data
    if (!currentProject) {
      toast.error('No project selected — choose a project above first');
      return;
    }

    setToggleLoading(prev => ({ ...prev, [type]: true }));
    try {
      if (type === 'info') {
        const data = `Project: ${currentProject.name}\nDescription: ${(currentProject as any).description || 'N/A'}`;
        setProjectCache(prev => ({ ...prev, info: data }));
      } else if (type === 'risks') {
        const res = await api.get(`/api/risk-mapping/${currentProject.id}`);
        setProjectCache(prev => ({ ...prev, risks: summarizeRiskData(res.data) }));
      } else if (type === 'processes') {
        const res = await api.get(`/api/process-maps/project/${currentProject.id}`);
        setProjectCache(prev => ({ ...prev, processes: summarizeProcessMap(res.data) }));
      } else if (type === 'interviews') {
        const res = await api.get('/api/intelligence/interview-insights', {
          params: { projectId: currentProject.id },
        });
        const d = res.data?.data;
        // "Nothing ingested" is the ordinary state for most projects, not an error — say so
        // plainly and leave the toggle off rather than switching on an empty source.
        if (!d?.available || !d?.text) {
          toast.error(d?.reason || 'This project has no interviews ingested yet.');
          return;
        }
        setProjectCache(prev => ({ ...prev, interviews: d.text }));
        // Size in the toast, not just the count. A full corpus runs to tens of thousands of
        // characters and every council member is billed for all of it — the consultant should
        // see what they just added to the prompt before they run it, not after.
        toast.success(
          `Loaded ${countOf(d.meta?.interview_count, 'interview')} (${(d.text.length / 1000).toFixed(1)}k chars)`
        );
      }
      setToggles(prev => ({ ...prev, [type]: true }));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || `Failed to load ${type} data`);
    } finally {
      setToggleLoading(prev => ({ ...prev, [type]: false }));
    }
  };

  // ── Generate prompt from plain description ────────────────────────────────
  const handleGeneratePrompt = async () => {
    if (!promptDescription.trim()) {
      toast.error('Please describe what you want to do');
      return;
    }
    setGeneratingPrompt(true);
    // Don't clear preview if regenerating — keep showing old prompt until new one arrives
    if (!generatedPromptPreview) setGeneratedPromptPreview('');
    try {
      const res = await api.post('/api/ai-council/generate-prompt', { description: promptDescription, selectedModels });
      setGeneratedPromptPreview(res.data.prompt);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to generate prompt');
    } finally {
      setGeneratingPrompt(false);
    }
  };

  const handleUseGeneratedPrompt = () => {
    setPrompt(generatedPromptPreview);
    setShowPromptHelper(false);
    setPromptDescription('');
    setGeneratedPromptPreview('');
    setTimeout(() => {
      if (promptRef.current) {
        promptRef.current.style.height = 'auto';
        promptRef.current.style.height = `${promptRef.current.scrollHeight}px`;
      }
    }, 50);
  };

  const handleCloseHelper = () => {
    setShowPromptHelper(false);
    setPromptDescription('');
    setGeneratedPromptPreview('');
  };

  // ── Upload document ────────────────────────────────────────────────────────
  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (fileInputRef.current) fileInputRef.current.value = '';

    const toUpload = files;

    // Block .xls files — ExcelJS cannot read them, would cause silent data loss
    const xlsFiles = toUpload.filter(f => f.name.toLowerCase().endsWith('.xls'));
    const validFiles = toUpload.filter(f => !f.name.toLowerCase().endsWith('.xls'));
    if (xlsFiles.length > 0) setXlsWarning(prev => Array.from(new Set([...prev, ...xlsFiles.map(f => f.name)])));

    setUploadingDoc(true);
    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/api/ai-council/upload-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const extractionFailed = !!res.data.extractionFailed;
        setUploadedDocs(prev => [...prev, {
          name: file.name,
          text: res.data.text,
          file,
          extractionFailed,
          extractionError: res.data.extractionError || null,
        }]);
        // A failed extraction is not success — it silently produces a document with
        // zero content that the LLM will never see (see buildCombinedData), so the
        // model has no way to know the file existed. Surface it as an error here
        // instead of a false "extracted successfully" toast, so the user knows
        // before they run the Council, not after reading a fabricated citation.
        if (extractionFailed) {
          toast.error(`"${file.name}" could not be read — ${res.data.extractionError || 'no text extracted'}. It will NOT be included in the Council's analysis unless you remove and re-upload a working copy.`, { duration: 8000 });
        } else {
          toast.success(`"${file.name}" extracted successfully`);
        }
        // Show Smart Data Analyst banner if Excel/CSV uploaded
        if (/\.(xlsx?|csv)$/i.test(file.name)) {
          setShowDataAnalystBanner(true);
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.error || `Failed to process "${file.name}"`);
      }
    }
    setUploadingDoc(false);
  };

  // ── Save chat history to DB ────────────────────────────────────────────────
  const saveChatHistory = async (messages: { role: string; content: string }[]) => {
    const sessionId = pipelineSessionId || activeSessionId;
    if (!sessionId) return;
    // Keep server URLs, strip ephemeral blob URLs
    const persistable = messages.map(m => {
      const isServerUrl = (url: string) => url.startsWith('/api/');
      if (m.content.includes('\n\n__EXCEL__|||')) {
        const [text, excelPart] = m.content.split('\n\n__EXCEL__|||');
        const [url] = excelPart.split('|||');
        if (isServerUrl(url)) return m; // keep server URL as-is
        return { ...m, content: text + '\n\n*(Excel file was generated — re-generate to download again)*' };
      }
      if (m.content.startsWith('__EXCEL__|||') || m.content.startsWith('__PPTX__|||')) {
        const url = m.content.split('|||')[1];
        if (isServerUrl(url)) return m; // keep server URL
        return { ...m, content: '*(File was generated — re-generate to download again)*' };
      }
      return m;
    });
    try {
      await api.patch(`/api/ai-council/sessions/${sessionId}`, {
        summary: { chatHistory: persistable },
      });
    } catch (e) { console.error('[ChatHistory] Save failed:', e); }
  };

  const handleNewChatWithSummary = async () => {
    if (chatMessages.length === 0 || chatHandoffLoading) return;
    setChatHandoffLoading(true);
    try {
      const res = await api.post('/api/ai-council/chat/summarize', {
        messages: chatMessages,
        originalPrompt: result?.prompt || prompt,
      });
      const summary = res.data?.data?.summary;
      if (!summary) throw new Error('No summary returned');
      const seeded = [{ role: 'assistant' as const, content: `Continuing from the previous chat.\n\n${summary}` }];
      setChatMessages(seeded);
      saveChatHistory(seeded);
      setChatContextMeta(null);
      setChatWebSources([]);
      toast.success('Started a fresh chat with a summary of the previous thread');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not summarize the chat');
    } finally {
      setChatHandoffLoading(false);
    }
  };

  // ── Download helper — handles both blob URLs and server URLs (with auth) ──
  const handleFileDownload = async (url: string, filename: string) => {
    if (url.startsWith('blob:')) {
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } else {
      setDownloadingUrl(url);
      try {
        const res = await api.get(url, { responseType: 'blob' });
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      } catch (err: any) {
        toast.error('File expired — please re-generate');
      } finally {
        setDownloadingUrl(null);
      }
    }
  };

  // ── Chat panel file attachment ────────────────────────────────────────────
  const handleChatFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (chatFileInputRef.current) chatFileInputRef.current.value = '';
    if (chatImageInputRef.current) chatImageInputRef.current.value = '';
    setChatAttachLoading(true);
    try {
      await Promise.all(files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/api/ai-council/upload-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setChatAttachments(prev => [...prev, { name: file.name, text: res.data.text }]);
      }));
      toast.success(files.length === 1 ? `"${files[0].name}" attached` : `${files.length} files attached`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to attach file(s)');
    } finally {
      setChatAttachLoading(false);
    }
  };

  // ── Chat panel PPTX generation ────────────────────────────────────────────
  const persistPresentationStructure = (s: SlideOutlineItem[]) => {
    setPresentationStructure(s);
    const sid = pipelineSessionId || activeSessionId;
    if (sid) api.patch(`/api/ai-council/sessions/${sid}`, { summary: { presentationStructure: s } }).catch(() => {});
  };

  const buildCoverageMarker = (coverage: any, lowConfidence?: boolean, fixAttempt = false): string | null => {
    if (!coverage || typeof coverage.score !== 'number' || !Array.isArray(coverage.items)) return null;
    const payload = {
      score: Math.round(coverage.score * 100),
      covered: coverage.covered,
      total: coverage.total_required,
      missing: coverage.missing,
      items: coverage.items.map((i: any) => ({ label: i.label, status: i.status })),
      ragMissing: coverage.rag_fidelity && Array.isArray(coverage.rag_fidelity.missing) ? coverage.rag_fidelity.missing : [],
      lowConfidence: !!lowConfidence,
      fixAttempt,
    };
    return `__COVERAGE__|||${JSON.stringify(payload)}`;
  };

  const buildCoverageMarkerFromHeaders = (headerScore?: string, headerMissing?: string, headerCovered?: string, headerTotal?: string, lowConfidence?: boolean, fixAttempt = false): string | null => {
    if (headerScore === undefined) return null;
    const score = parseInt(headerScore, 10);
    if (!Number.isFinite(score)) return null;
    const missing = parseInt(headerMissing || '', 10);
    const covered = parseInt(headerCovered || '', 10);
    const total = parseInt(headerTotal || '', 10);
    const payload = {
      score,
      covered: Number.isFinite(covered) ? covered : null,
      total: Number.isFinite(total) ? total : null,
      missing: Number.isFinite(missing) ? missing : 0,
      items: [],
      ragMissing: [],
      lowConfidence: !!lowConfidence,
      fixAttempt,
    };
    return `__COVERAGE__|||${JSON.stringify(payload)}`;
  };

  const handleChatGeneratePptx = async (userRequest: string, _showUserMessage = true, isFixAttempt = false) => {
    const presidentSynthesis =
      pipelineData.presidentEvaluation?.finalResponse ||
      result?.presidentEvaluation?.finalResponse || '';
    const memberResultsText = (pipelineData.memberResults || result?.memberResults || [])
      .filter((r: any) => r.status === 'success')
      .map((r: any) => `### ${r.modelId}\n${r.response}`)
      .join('\n\n---\n\n');
    const contextToUse = presidentSynthesis || memberResultsText;
    if (!contextToUse) {
      toast.error('No council output available yet — run at least Step 1 first');
      return;
    }
    const effectiveRequest = userRequest.trim() || 'Create a complete presentation covering all sections';
    const sid = pipelineSessionId || activeSessionId;
    // "Fix Missing Items" depends on the outline/coverage step, so it always
    // runs the standard pipeline regardless of the toggle — enhanced mode
    // hands slide structure to Claude's own code and has no coverage to fix.
    const useEnhanced = enhancedVisualization && !isFixAttempt;
    setPptxGenerating(true);
    const convo = [...chatMessages, { role: 'user' as const, content: effectiveRequest }];
    setChatMessages(convo);
    try {
      let workingOutline: SlideOutlineItem[] = [];
      let outlineCoverage: any;
      let outlineLowConfidence: any;
      if (!useEnhanced) {
        const outlineRes = await api.post('/api/ai-council/preview-outline', {
          originalPrompt: prompt,
          councilOutput: contextToUse,
          finalSynthesis: pipelineData.finalSynthesis || result?.finalSynthesis || { confirmedStrengths: contextToUse },
          messages: convo,
          currentOutline: presentationStructure || undefined,
          sessionId: sid || undefined,
        }, { timeout: 720000 });
        workingOutline = (outlineRes.data?.outline || []).map((s: any, i: number) => ({ ...s, slideNum: i + 1 }));
        if (workingOutline.length > 0) persistPresentationStructure(workingOutline);
        outlineCoverage = outlineRes.data?.coverage;
        outlineLowConfidence = outlineRes.data?.manifest_low_confidence;
      }

      const res = await api.post('/api/ai-council/chat/generate-pptx', {
        sessionPrompt: prompt,
        presidentSynthesis: contextToUse,
        userRequest: effectiveRequest,
        messages: convo,
        slideOutline: workingOutline.length > 0 ? workingOutline : undefined,
        brandingTagId: selectedBrandingTagId,
        sessionId: sid || undefined,
        enhanced: useEnhanced,
      }, { responseType: 'blob', timeout: 720000 });
      const token = res.headers['x-file-token'];
      const filename = res.headers['x-file-name'] || `Council_Briefing_${Date.now()}.pptx`;
      const downloadUrl = token ? `/api/ai-council/temp-files/${token}` : window.URL.createObjectURL(new Blob([res.data]));
      const coverageMarker = buildCoverageMarker(outlineCoverage, outlineLowConfidence, isFixAttempt)
        || buildCoverageMarkerFromHeaders(
          res.headers['x-coverage-score'],
          res.headers['x-coverage-missing'],
          res.headers['x-coverage-covered'],
          res.headers['x-coverage-total'],
          outlineLowConfidence,
          isFixAttempt,
        );
      setChatMessages(prev => {
        const messages = [...prev, { role: 'assistant' as const, content: `__PPTX__|||${downloadUrl}|||${filename}` }];
        const note = coverageMarker;
        const updated = note ? [...messages, { role: 'assistant' as const, content: note }] : messages;
        if (token) saveChatHistory(updated);
        return updated;
      });
      toast.success('Presentation ready!');
    } catch (err: any) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: '❌ Failed to generate presentation. Please try again.' }]);
      toast.error(err?.response?.data?.error || 'Failed to generate presentation');
    } finally {
      setPptxGenerating(false);
    }
  };

  // ── Chat panel Excel generation ────────────────────────────────────────────
  const handleChatGenerateExcel = async (userRequest: string, showUserMessage = true) => {
    const presidentSynthesis =
      pipelineData.presidentEvaluation?.finalResponse ||
      result?.presidentEvaluation?.finalResponse || '';
    // Fall back to member results if president synthesis not yet available (Step 1 only)
    const memberResultsText = (pipelineData.memberResults || result?.memberResults || [])
      .filter((r: any) => r.status === 'success')
      .map((r: any) => `### ${r.modelId}\n${r.response}`)
      .join('\n\n---\n\n');
    const contextToUse = presidentSynthesis || memberResultsText;
    if (!contextToUse) {
      toast.error('No council output available yet — run at least Step 1 first');
      return;
    }
    setExcelGenerating(true);
    if (showUserMessage) setChatMessages(prev => [...prev, { role: 'user', content: userRequest }]);
    try {
      const res = await api.post('/api/ai-council/chat/generate-excel', {
        sessionPrompt: prompt,
        presidentSynthesis: contextToUse,
        userRequest,
      }, { responseType: 'blob', timeout: 120000 });
      const token = res.headers['x-file-token'];
      const serverFilename = res.headers['x-file-name'];
      const stopWords = new Set(['give','me','a','an','the','of','by','from','for','in','on','with','about','and','or','to','is','are','that','this','these','those','all','any','some','get','make','create','show','list','generate','table','please','can','you','i','we','us','our']);
      const meaningful = (userRequest || prompt || 'council_data')
        .toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/)
        .filter((w: string) => w.length > 2 && !stopWords.has(w))
        .slice(0, 4).join('_') || 'council_data';
      const filename = serverFilename || `${meaningful}.xlsx`;
      const downloadUrl = token ? `/api/ai-council/temp-files/${token}` : window.URL.createObjectURL(new Blob([res.data]));
      const newMsg = { role: 'assistant' as const, content: `__EXCEL__|||${downloadUrl}|||${filename}` };
      setChatMessages(prev => {
        const updated = [...prev, newMsg];
        if (token) saveChatHistory(updated);
        return updated;
      });
      toast.success('Excel file ready!');
    } catch (err: any) {
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: '❌ Failed to generate Excel file. Please try again.' },
      ]);
      toast.error(err?.response?.data?.error || 'Failed to generate Excel');
    } finally {
      setExcelGenerating(false);
    }
  };

  // ── Chat panel Word generation ─────────────────────────────────────────────
  // Reuses /api/intelligence/refine-chat/generate-word — deterministic markdown
  // → docx so the download mirrors the council output exactly (client's
  // download-fidelity complaint). Passes the president synthesis (or member
  // results fallback) as `analysisText`.
  const handleChatGenerateWord = async (userRequest: string) => {
    const presidentSynthesis =
      pipelineData.presidentEvaluation?.finalResponse ||
      result?.presidentEvaluation?.finalResponse || '';
    const memberResultsText = (pipelineData.memberResults || result?.memberResults || [])
      .filter((r: any) => r.status === 'success')
      .map((r: any) => `### ${r.modelId}\n${r.response}`)
      .join('\n\n---\n\n');
    const contextToUse = presidentSynthesis || memberResultsText;
    if (!contextToUse) {
      toast.error('No council output available yet — run at least Step 1 first');
      return;
    }
    setWordGenerating(true);
    setChatMessages(prev => [...prev, { role: 'user', content: userRequest || 'Download as Word' }]);
    try {
      const res = await api.post('/api/intelligence/refine-chat/generate-word', {
        mode: 'council',
        sessionPrompt: prompt,
        analysisText: contextToUse,
        userRequest,
      }, { responseType: 'blob', timeout: 120000 });
      const serverFilename = res.headers['x-file-name'];
      const filename = serverFilename || `Council_Briefing_${Date.now()}.docx`;
      const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
      const coverage = buildCoverageMarkerFromHeaders(
        res.headers['x-coverage-score'],
        res.headers['x-coverage-missing'],
        res.headers['x-coverage-covered'],
        res.headers['x-coverage-total'],
      );
      setChatMessages(prev => {
        const messages = [...prev, { role: 'assistant' as const, content: `__WORD__|||${downloadUrl}|||${filename}` }];
        return coverage ? [...messages, { role: 'assistant' as const, content: coverage }] : messages;
      });
      toast.success('Word file ready!');
    } catch (err: any) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: '❌ Failed to generate Word file. Please try again.' }]);
      toast.error(err?.response?.data?.error || 'Failed to generate Word');
    } finally {
      setWordGenerating(false);
    }
  };

  // ── Implementation persona management ──────────────────────────────────────
  const addImplementationPersona = () => {
    setImplementationPersonas(prev => [...prev, {
      id: Date.now().toString(),
      name: 'New Specialist',
      emoji: '💼',
      description: 'Describe this specialist and what implementation lens they should apply.',
    }]);
  };

  const updateImplementationPersona = (id: string, field: 'name' | 'emoji' | 'description', value: string) => {
    setImplementationPersonas(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removeImplementationPersona = (id: string) => {
    setImplementationPersonas(prev => prev.filter(p => p.id !== id));
  };

  const resetImplementationPersonas = () => setImplementationPersonas(DEFAULT_IMPLEMENTATION_PERSONAS);

  // ── Run Implementation Review (Second Council) ────────────────────────────
  const handleRunImplementationReview = async () => {
    // Pull the council's recommendation
    const councilOutput = result?.presidentEvaluation?.finalResponse;

    if (!councilOutput) {
      toast.error('No council output to review — run the AI Council first');
      return;
    }
    if (implementationPersonas.length === 0) {
      toast.error('Please add at least one implementation specialist');
      return;
    }

    setRunningImplementation(true);
    setImplementationResult(null);
    try {
      const res = await api.post('/api/ai-council/implementation-review', {
        councilOutput,
        originalPrompt: prompt,
        personas: implementationPersonas.map(p => ({ name: p.name, description: p.description, emoji: p.emoji })),
        modelId: implementationModel,
      }, { timeout: 600000 });
      setImplementationResult(res.data.data);
      toast.success('Implementation review complete!');
      setTimeout(() => implementationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Implementation review failed');
    } finally {
      setRunningImplementation(false);
    }
  };

  // ── Build combined input data ─────────────────────────────────────────────
  const buildCombinedData = () => {
    const parts: string[] = [];
    if (inputData.trim()) parts.push(`--- Manual Input ---\n${inputData.trim()}`);
    // Previously `if (doc.text)` silently omitted failed-extraction files — including
    // their filename — from the prompt entirely. The model then had no way to know
    // the file existed and would fabricate plausible-sounding content for it when
    // asked to analyze "all uploaded documents." A failed doc must still appear in
    // the prompt, as an explicit EXCLUDED marker the model is instructed (in
    // aiCouncilService.js's system prompt) to never treat as read or citable content.
    const failedDocNames: string[] = [];
    uploadedDocs.forEach(doc => {
      if (doc.extractionFailed) {
        failedDocNames.push(doc.name);
        parts.push(`--- Document: ${doc.name} ---\n[EXCLUDED: This file failed text extraction and its content is NOT available. Do not reference, cite, summarize, or infer the content of this document under any circumstances. If asked about it, state explicitly that it could not be read.]`);
      } else if (doc.text) {
        parts.push(`--- Document: ${doc.name} ---\n${doc.text}`);
      }
    });
    if (toggles.info && projectCache.info) parts.push(`--- Project Info ---\n${projectCache.info}`);
    if (toggles.risks && projectCache.risks) parts.push(`--- Risk Data ---\n${projectCache.risks}`);
    if (toggles.processes && projectCache.processes) parts.push(`--- Process Maps ---\n${projectCache.processes}`);
    if (toggles.interviews && projectCache.interviews) parts.push(`--- Interview Intelligence ---\n${projectCache.interviews}`);
    if (failedDocNames.length > 0) {
      parts.unshift(`--- IMPORTANT: EXCLUDED DOCUMENTS ---\nThe following ${failedDocNames.length} document(s) failed text extraction and are NOT part of your source material: ${failedDocNames.join(', ')}. You have NOT read these documents. Do not cite them, quote them, reference specific sections/clauses/numbers from them, or imply you have reviewed their contents. If your analysis would normally rely on one of these documents, explicitly flag that as a gap instead of inferring or fabricating what it might contain.`);
    }
    return parts.join('\n\n');
  };

  // ── Friendly error message helper ────────────────────────────────────────
  const getApiError = (err: any): string => {
    const raw: string = err?.response?.data?.error || err?.message || '';
    if (/credit balance is too low|insufficient.*credit|billing|upgrade or purchase/i.test(raw))
      return '💳 Anthropic API credit balance is too low. Please add credits in Plans & Billing to continue.';
    if (/rate.?limit/i.test(raw))
      return '⏳ API rate limit reached — please wait a moment and try again.';
    return raw || 'Something went wrong. Please try again.';
  };

  // ── Guard against malformed/failed step responses before destructuring ──
  // The council step endpoints use a keep-alive streaming response for long-running
  // LLM calls; error responses sent after streaming begins carry {success:false, error}
  // with no `data` key, but the wire-level status stays 200 (axios never throws). Without
  // this guard, destructuring res.data.data crashes with a raw "Cannot read properties of
  // undefined" instead of surfacing the actual failure reason.
  const unwrapStepData = <T,>(res: any, fallbackMessage: string): T => {
    if (!res?.data?.success || !res?.data?.data) {
      throw new Error(res?.data?.error || fallbackMessage);
    }
    return res.data.data as T;
  };

  // ── Input size thresholds ─────────────────────────────────────────────────
  // Warn at 150k chars (~37k tokens). Hard block above what our largest-context
  // members can accept (~4M chars ≈ 1M tokens).
  const INPUT_WARN_CHARS = 150_000;
  const INPUT_BLOCK_CHARS = 4_000_000;

  // Per-model context window (chars), keyed by model id, from the /models API.
  const modelContextChars = (modelId: string): number | undefined =>
    availableModels.find(m => m.id === modelId)?.contextWindowChars;

  // ── Step 1: Council Members ───────────────────────────────────────────────
  const runStep1 = async (enrichedPrompt?: string) => {
    const finalPrompt = enrichedPrompt || prompt;
    // For collaborator sessions, keep the shared session reference so all steps write to the same session
    const sharedSessionId = isCollaboratorSession ? (activeSessionId || pipelineSessionId) : null;
    setPipelinePrompt(finalPrompt);
    setPipelineRunning(true);
    setPipelineStep(1);
    setPipelineData({ memberResults: null, presidentEvaluation: null, personaResults: null, finalSynthesis: null, councilOutput: '', step1Failed: false, step2Failed: false, step3Failed: false, step4Failed: false });
    setShowImplementationReview(false);
    setPressureTestDismissed(false);
    // Snapshot excluded docs NOW, at run time — uploadedDocs can change before the result renders.
    setPipelineExcludedDocNames(uploadedDocs.filter(d => d.extractionFailed).map(d => d.name));
    if (!isCollaboratorSession) setPipelineSessionId(null); // preserve shared session for collaborators

    let keepSpinning = false;
    try {
      const res = await api.post('/api/ai-council/steps/members', {
        prompt: finalPrompt, inputData: buildCombinedData(), selectedModels, project_id: currentProject?.id || null, web_search: webSearch,
        sessionId: sharedSessionId || undefined,
      }, { timeout: 900000 }); // 15 min — matches server-side council step timeout
      const stepData = unwrapStepData<{ memberResults: any[]; sessionId: number; web_sources?: any[] }>(
        res, 'Step 1 failed — no council members responded. Please try again.'
      );
      const { memberResults, sessionId: returnedSessionId } = stepData;
      console.log('[AICouncil Step1] sharedSessionId sent:', sharedSessionId, '| sessionId returned:', returnedSessionId);
      setWebSources(stepData.web_sources || []);
      setPipelineData(prev => ({ ...prev, memberResults }));
      if (isCollaboratorSession) {
        // Keep the shared session reference — don't switch to a new one
        if (sharedSessionId) { setPipelineSessionId(sharedSessionId); setActiveSessionId(sharedSessionId); }
      } else {
        if (returnedSessionId) { setPipelineSessionId(returnedSessionId); setActiveSessionId(returnedSessionId); }
      }
      fetchSessions(false);
      setPipelineStep(1);
      setTimeout(() => pipelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      const failedMembers = (memberResults || []).filter((m: any) => m?.status === 'error');
      const allMembersFailed = (memberResults || []).length > 0 && failedMembers.length === memberResults.length;
      setPipelineData(prev => ({ ...prev, step1Failed: allMembersFailed }));
      if (allMembersFailed) {
        toast.error(`Step 1 failed — every council member errored (${failedMembers[0]?.error || 'no response'})`, { duration: 8000 });
      } else if (failedMembers.length) {
        toast(`Step 1 complete — ${failedMembers.length} of ${memberResults.length} members failed: ${failedMembers.map((m: any) => m.modelId).join(', ')}`, { icon: '⚠️', duration: 8000 });
      } else {
        toast.success('Step 1 complete — council members responded!');
      }
    } catch (err: any) {
      const isNetworkDrop = !err?.response && (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('Network'));
      if (isCollaboratorSession && isNetworkDrop && sharedSessionId) {
        // Backend is still running — don't reset. Poller will pick up results in ≤30s.
        console.warn('[AICouncil Step1] Network drop during collaborator run — backend still processing. Poller will update.', err?.message);
        toast('Step 1 still processing in background — results will appear automatically within 30s.', { icon: '⏳', duration: 8000 });
        keepSpinning = true;
        setPipelineBackgroundProcessing(true);
      } else {
        toast.error(getApiError(err));
        setPipelineStep(0);
      }
    } finally {
      if (!keepSpinning) setPipelineRunning(false);
    }
  };

  const maybeWarnTruncationThen = (proceed: () => void) => {
    const totalChars = buildCombinedData().length + prompt.length;
    const affected = selectedModels.filter(m => {
      const cap = modelContextChars(m);
      return cap !== undefined && totalChars > cap;
    });
    if (affected.length === 0) { proceed(); return; }
    const largeModels = selectedModels.filter(m => !affected.includes(m));
    const limitChars = Math.min(...affected.map(m => modelContextChars(m) as number));
    setTruncationWarning({
      models: affected,
      largeModels,
      totalChars,
      limitChars,
      onConfirm: () => {
        setTruncationWarning(null);
        proceed();
      },
      onDropSmall: largeModels.length > 0
        ? () => {
            setSelectedModels(largeModels);
            setTruncationWarning(null);
            setTimeout(proceed, 0);
          }
        : null,
    });
  };

  const handleRunPipelineStep1 = async () => {
    if (!prompt.trim()) { toast.error('Please enter a prompt'); return; }
    if (selectedModels.length === 0) { toast.error('Please select at least one council member'); return; }

    // Hard gate: any uploaded document that failed extraction (0 chars) must be
    // explicitly acknowledged before the Council can run. Without this, the LLM
    // receives no content AND no filename for the failed file (see buildCombinedData's
    // EXCLUDED marker below) and has historically fabricated confident-sounding
    // citations for documents it never actually read — a client-defensibility issue
    // for compliance/RCSA output. Typed confirmation (not a checkbox) is required.
    // Checked against acknowledgedFailedDocsRef (not the modal's own state, which is
    // cleared on confirm) so re-invoking this function after confirmation proceeds
    // instead of re-showing the same modal in a loop.
    const failedDocs = uploadedDocs.filter(d => d.extractionFailed);
    const unacknowledged = failedDocs.filter(d => !acknowledgedFailedDocsRef.current.has(d.name));
    if (unacknowledged.length > 0) {
      setFailedExtractionConfirmText('');
      setFailedExtractionWarning({
        failedDocNames: failedDocs.map(d => d.name),
        onConfirm: () => {
          failedDocs.forEach(d => acknowledgedFailedDocsRef.current.add(d.name));
          setFailedExtractionWarning(null);
          handleRunPipelineStep1();
        },
      });
      return;
    }

    // Pre-flight input size check
    const combinedInput = buildCombinedData();
    const totalChars = combinedInput.length + prompt.length;
    if (totalChars > INPUT_BLOCK_CHARS) {
      toast.error(`Input too large (${(totalChars / 1000).toFixed(0)}k chars). Remove some documents or reduce input to under ${INPUT_BLOCK_CHARS / 1000}k chars.`, { duration: 8000 });
      return;
    }
    if (totalChars > INPUT_WARN_CHARS) {
      setLargeInputWarning({
        totalChars,
        fileCount: uploadedDocs.length,
        onConfirm: () => {
          setLargeInputWarning(null);
          maybeWarnTruncationThen(startClarifyFlow);
        },
      });
      return;
    }

    maybeWarnTruncationThen(() => {
      const clarifyKeywords = [
        'ask me', 'ask any', 'any questions', 'clarifying questions',
        'what do you need', 'what do you need to know', 'questions you need',
        'clarify', 'before you begin', 'before you start', 'before analyzing',
        'before you analyze', 'questions first', 'ask first',
      ];
      const promptLower = prompt.toLowerCase();
      const wantsClarification = clarifyKeywords.some(kw => promptLower.includes(kw));

      if (wantsClarification) {
        startClarifyFlow();
      } else {
        runStep1();
      }
    });
  };

  const startClarifyFlow = async () => {
    setClarifyLoading(true);
    setClarifyQuestions([]);
    setClarifyAnswers({});
    try {
      const res = await api.post('/api/ai-council/clarify', {
        prompt, inputData: buildCombinedData().slice(0, 3000),
      });
      if (res.data?.success && res.data.questions?.length > 0) {
        setClarifyQuestions(res.data.questions);
        setShowClarifyPanel(true);
      } else {
        runStep1();
      }
    } catch {
      runStep1();
    } finally {
      setClarifyLoading(false);
    }
  };

  const handleClarifySubmit = () => {
    const answered = Object.entries(clarifyAnswers).filter(([, v]) => v.trim());
    let enrichedPrompt = prompt;
    if (answered.length > 0) {
      const qaBlock = answered
        .map(([idx, ans]) => `Q: ${clarifyQuestions[Number(idx)]}\nA: ${ans.trim()}`)
        .join('\n\n');
      enrichedPrompt = `${prompt}\n\n---\nCLARIFICATION ANSWERS:\n${qaBlock}`;
    }
    setShowClarifyPanel(false);
    runStep1(enrichedPrompt);
  };

  // ── Step 2: First Synthesis (President) ──────────────────────────────────
  const handleRunPipelineStep2 = async () => {
    if (!pipelineData.memberResults?.length) return;
    setPipelineRunning(true);
    setPipelineStep(2);
    const step2SessionId = pipelineSessionId || (isCollaboratorSession ? activeSessionId : undefined);
    console.log('[AICouncil Step2] pipelineSessionId:', pipelineSessionId, '| isCollaboratorSession:', isCollaboratorSession, '| activeSessionId:', activeSessionId, '| sending:', step2SessionId);
    let keepSpinning2 = false;
    try {
      const res = await api.post('/api/ai-council/steps/first-synthesis', {
        prompt: pipelinePrompt, inputData: buildCombinedData(), memberResults: pipelineData.memberResults, sessionId: step2SessionId,
      }, { timeout: 600000 });
      const stepData = unwrapStepData<{ presidentEvaluation: any; presidentResult?: any; truncated: boolean }>(
        res, 'Step 2 failed — synthesis did not complete. Please try again.'
      );
      const { presidentEvaluation, truncated: step2Truncated } = stepData;
      const presidentError = stepData.presidentResult?.status === 'error'
        ? (stepData.presidentResult?.error || 'the president model returned an error')
        : null;
      const councilOutput: string = presidentEvaluation?.finalResponse || stepData.presidentResult?.response || '';
      setPipelineData(prev => ({ ...prev, presidentEvaluation, councilOutput, step2Failed: !!presidentError || !presidentEvaluation || !!step2Truncated }));
      if (presidentError) toast.error(`Step 2 failed — ${presidentError}`, { duration: 8000 });
      else if (step2Truncated) toast.error('Step 2 response was cut off — please retry for a complete synthesis.');
      else if (!presidentEvaluation) toast.error('Step 2 produced no synthesis — please retry.');
      else toast.success('Step 2 complete — first synthesis done!');
    } catch (err: any) {
      const isNetworkDrop = !err?.response && (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('Network'));
      if (isCollaboratorSession && isNetworkDrop) {
        console.warn('[AICouncil Step2] Network drop — backend still processing.', err?.message);
        toast('Step 2 still processing in background — results will appear automatically within 30s.', { icon: '⏳', duration: 8000 });
        keepSpinning2 = true;
        setPipelineBackgroundProcessing(true);
      } else {
        toast.error(getApiError(err));
        setPipelineData(prev => ({ ...prev, step2Failed: true }));
        setPipelineStep(1);
      }
    } finally {
      if (!keepSpinning2) setPipelineRunning(false);
    }
  };

  // ── Step 3: Implementation Specialists ───────────────────────────────────
  const handleRunPipelineStep3 = async () => {
    if (!pipelineData.councilOutput) return;
    setPipelineRunning(true);
    setPipelineStep(3);
    const step3SessionId = pipelineSessionId || (isCollaboratorSession ? activeSessionId : undefined);
    console.log('[AICouncil Step3] pipelineSessionId:', pipelineSessionId, '| isCollaboratorSession:', isCollaboratorSession, '| activeSessionId:', activeSessionId, '| sending:', step3SessionId);
    let keepSpinning3 = false;
    try {
      const res = await api.post('/api/ai-council/steps/specialists', {
        councilOutput: pipelineData.councilOutput,
        originalPrompt: pipelinePrompt,
        personas: implementationPersonas.map(p => ({ name: p.name, description: p.description, emoji: p.emoji })),
        modelId: implementationModel,
        sessionId: step3SessionId,
      }, { timeout: 600000 });
      const stepData = unwrapStepData<{ personaResults: ImplementationPersonaResult[] }>(
        res, 'Step 3 failed — specialist review did not complete. Please try again.'
      );
      const personaResults: ImplementationPersonaResult[] = stepData.personaResults;
      const failedSpecialists = (personaResults || []).filter((p: any) => !p?.response || String(p.response).startsWith('Error:'));
      const allSpecialistsFailed = (personaResults || []).length > 0 && failedSpecialists.length === personaResults.length;
      setPipelineData(prev => ({ ...prev, personaResults, step3Failed: allSpecialistsFailed }));
      if (allSpecialistsFailed) {
        toast.error('Step 3 failed — every specialist errored. Please retry.', { duration: 8000 });
      } else if (failedSpecialists.length) {
        toast(`Step 3 complete — ${failedSpecialists.length} of ${personaResults.length} specialists failed: ${failedSpecialists.map((p: any) => p.personaName).join(', ')}`, { icon: '⚠️', duration: 8000 });
      } else {
        toast.success('Step 3 complete — specialists done!');
      }
    } catch (err: any) {
      const isNetworkDrop = !err?.response && (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('Network'));
      if (isCollaboratorSession && isNetworkDrop) {
        console.warn('[AICouncil Step3] Network drop — backend still processing.', err?.message);
        toast('Step 3 still processing in background — results will appear automatically within 30s.', { icon: '⏳', duration: 8000 });
        keepSpinning3 = true;
        setPipelineBackgroundProcessing(true);
      } else {
        toast.error(getApiError(err));
        setPipelineStep(2);
      }
    } finally {
      if (!keepSpinning3) setPipelineRunning(false);
    }
  };

  // ── Step 4: Final Synthesis ───────────────────────────────────────────────
  const handleRunPipelineStep4 = async () => {
    if (!pipelineData.councilOutput) return;
    setPipelineRunning(true);
    setPipelineStep(4);
    const step4SessionId = pipelineSessionId || (isCollaboratorSession ? activeSessionId : undefined);
    console.log('[AICouncil Step4] pipelineSessionId:', pipelineSessionId, '| isCollaboratorSession:', isCollaboratorSession, '| activeSessionId:', activeSessionId, '| sending:', step4SessionId);
    let keepSpinning4 = false;
    try {
      const res = await api.post('/api/ai-council/steps/final-synthesis', {
        councilOutput: pipelineData.councilOutput,
        originalPrompt: pipelinePrompt,
        inputData: buildCombinedData(),
        personaResults: pipelineData.personaResults,
        sessionId: step4SessionId,
        selectedModels,
        project_id: currentProject?.id || null,
      }, { timeout: 600000 });
      const stepData = unwrapStepData<{ finalSynthesis: any; sessionId: number; truncated: boolean }>(
        res, 'Step 4 failed — final synthesis did not complete. Please try again.'
      );
      const { finalSynthesis, sessionId: returnedStep4SessionId, truncated: step4Truncated } = stepData;
      setPipelineData(prev => ({ ...prev, finalSynthesis, step4Failed: !finalSynthesis || !!step4Truncated }));
      console.log('[AICouncil Step4] sent:', step4SessionId, '| returned:', returnedStep4SessionId);
      if (!isCollaboratorSession && returnedStep4SessionId) setActiveSessionId(returnedStep4SessionId);
      setPipelineStep(5);
      fetchSessions(false);
      // Scroll the completed pipeline into view — the toast below is transient, and without this the
      // "Pipeline Complete" card can finish off-screen (if the user scrolled up during the long run),
      // making a completed run look like it never ran and forcing a History-navigation + refresh to
      // find it. Mirrors the scroll on Step 1 (pipelineRef) and the standard run (resultsRef).
      setTimeout(() => pipelineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      if (step4Truncated) toast.error('Step 4 response was cut off — please retry for a complete synthesis.');
      else if (!finalSynthesis) toast.error('Step 4 produced no final synthesis — please retry.');
      else toast.success('Pipeline complete — all 4 steps done!');
    } catch (err: any) {
      const isNetworkDrop = !err?.response && (err?.code === 'ECONNABORTED' || err?.message?.includes('timeout') || err?.message?.includes('Network'));
      if (isCollaboratorSession && isNetworkDrop) {
        console.warn('[AICouncil Step4] Network drop — backend still processing.', err?.message);
        toast('Step 4 still processing in background — results will appear automatically within 30s.', { icon: '⏳', duration: 8000 });
        keepSpinning4 = true;
        setPipelineBackgroundProcessing(true);
      } else {
        toast.error(getApiError(err));
        setPipelineData(prev => ({ ...prev, step4Failed: true }));
        setPipelineStep(3);
      }
    } finally {
      if (!keepSpinning4) setPipelineRunning(false);
    }
  };

  // Keep old handleRunPipeline for backward compat (unused now)
  const handleRunPipeline = handleRunPipelineStep1;

  const applyAnalyzePayload = (tool: StrategicSubTool, data: any, extra: Partial<StrategySynthesisResult> = {}) => {
    setStrategySynthesisResult({
      subToolId: tool.id,
      subToolName: tool.name,
      response: data?.analysis || data?.result || '',
      source: data?.source,
      structured: data?.structured,
      promotion: data?.promotion,
      interviewContextUsed: data?.interview_context?.used,
      interviewContextCount: data?.interview_context?.interview_count ?? data?.promotion?.interview_count,
      ...extra,
    });
  };

  const runUrsAnalyze = useCallback(async (opts: { preliminary?: boolean } = {}) => {
    if (!selectedSubTool || selectedSubTool.id !== 'user-research-synthesis') return false;
    if (!currentProject?.id) {
      toast.error('Open a project before running User Research Synthesis.');
      return false;
    }
    const formData = new FormData();
    formData.append('mode', 'user-research-synthesis');
    formData.append('prompt', synthesisPrompt);
    formData.append('projectId', String(currentProject.id));
    if (selectedSourceIds.length) formData.append('sourceIds', selectedSourceIds.join(','));
    const res = await api.post('/api/intelligence/analyze', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    applyAnalyzePayload(selectedSubTool, res.data?.data, { preliminary: !!opts.preliminary });
    return true;
  }, [currentProject?.id, selectedSourceIds, selectedSubTool, synthesisPrompt]);

  const loadPreliminaryReport = useCallback(async () => {
    if (!currentProject?.id || !selectedSubTool || selectedSubTool.id !== 'user-research-synthesis') return;
    try {
      const res = await api.get('/api/intelligence/interview-insights', {
        params: { projectId: currentProject.id },
      });
      const d = res.data?.data;
      if (!d?.available || !d?.text) return;
      setStrategySynthesisResult({
        subToolId: selectedSubTool.id,
        subToolName: selectedSubTool.name,
        response: d.text,
        source: 'interview-intelligence',
        structured: {
          json: {},
          citations: [],
          run_count: d.meta?.interview_count ?? 0,
          ingestion_completeness: d.meta?.ingestion_completeness ?? 0,
          pending_merge_count: d.meta?.pending_merge_count ?? 0,
        },
        promotion: {
          interview_count: d.meta?.interview_count ?? 0,
          eligible: (d.meta?.interview_count ?? 0) > 1,
        },
        preliminary: true,
      });
    } catch { /* a missing preliminary report is not an error */ }
  }, [currentProject?.id, selectedSubTool]);

  const lastPreliminaryCount = useRef(0);
  useEffect(() => {
    if (selectedSubTool?.id !== 'user-research-synthesis') return;
    const n = ursCorpus.usable.length;
    if (n === 0 || n === lastPreliminaryCount.current) return;
    lastPreliminaryCount.current = n;
    if (projectIngest.ingesting) void loadPreliminaryReport();
  }, [ursCorpus.usable.length, projectIngest.ingesting, selectedSubTool?.id, loadPreliminaryReport]);

  const onIngestSettled = useCallback(async (snap: {
    state: string;
    ingested: number | null;
    ingestErrors: string[];
    ingestSkipped: string[];
    justFinished?: boolean;
  }) => {
    await refreshInterviewIssues();
    const errs = Array.isArray(snap.ingestErrors) ? snap.ingestErrors : [];
    const skipped = Array.isArray(snap.ingestSkipped) ? snap.ingestSkipped : [];
    if (snap.justFinished) {
      errs.forEach((e) => toast.error(e, { duration: 8000 }));
      if (skipped.length) {
        toast.success(`${skipped.length} already in this project (skipped, not re-extracted).`);
      }
    }
    const ingested = typeof snap.ingested === 'number' ? snap.ingested : 0;
    if (snap.justFinished && snap.state === 'completed' && ingested === 0 && errs.length > 0 && skipped.length === 0) {
      toast.error('Nothing ingested — the report was not changed.');
      return;
    }
    if (snap.justFinished && snap.state === 'cancelled') toast('Ingest stopped. Finished interviews stay in the project.');
    if (snap.justFinished && snap.state === 'failed') toast.error('Ingestion failed.');
    const projectId = currentProject?.id;
    let shouldAnalyze = thenAnalyzeRef.current;
    if (!shouldAnalyze && projectId) {
      try { shouldAnalyze = sessionStorage.getItem(`urs_then_analyze_${projectId}`) === '1'; } catch { /* ignore */ }
    }
    if (shouldAnalyze && snap.state === 'completed') {
      thenAnalyzeRef.current = false;
      if (projectId) {
        try { sessionStorage.removeItem(`urs_then_analyze_${projectId}`); } catch { /* ignore */ }
      }
      try {
        setRunningStrategySynthesis(true);
        const ok = await runUrsAnalyze({ preliminary: false });
        if (ok) {
          toast.success('User Research Synthesis complete!');
          fetchSessions(false);
        }
      } catch (err: any) {
        toast.error(err?.response?.data?.error || 'Analysis failed');
      } finally {
        setRunningStrategySynthesis(false);
      }
      return;
    }
    if (snap.justFinished && (snap.state === 'completed' || snap.state === 'cancelled')) {
      void loadPreliminaryReport();
      if (snap.state === 'completed') {
        toast.success('Interviews ready — Run User Research Synthesis to generate the report.');
      }
    }
  }, [currentProject?.id, loadPreliminaryReport, refreshInterviewIssues, runUrsAnalyze]);

  // ── Run Strategy Synthesis ────────────────────────────────────────────────
  const handleRunStrategySynthesis = async () => {
    if (!selectedSubTool) { toast.error('Please select a strategic tool'); return; }
    if (!promptOptionalForTool && !synthesisPrompt.trim()) { toast.error('Please enter a prompt'); return; }

    const isInterviewSynthesis = selectedSubTool.id === 'user-research-synthesis';

    if (isInterviewSynthesis && !currentProject?.id) {
      toast.error('Open a project before uploading transcripts — interviews are ingested into one.');
      return;
    }

    if (isInterviewSynthesis && !projectIngest.ingesting && ursCorpus.usable.length === 0) {
      toast.error('Add transcripts first — Run reads stored interviews and does not re-extract.');
      return;
    }

    if (isInterviewSynthesis && projectIngest.ingesting) {
      thenAnalyzeRef.current = true;
      if (currentProject?.id) {
        try { sessionStorage.setItem(`urs_then_analyze_${currentProject.id}`, '1'); } catch { /* ignore */ }
      }
      toast('Ingest is still running — the report will generate when it finishes.');
      return;
    }

    setRunningStrategySynthesis(true);
    if (!isInterviewSynthesis) setStrategySynthesisResult(null);
    try {
      if (isInterviewSynthesis) {
        refreshInterviewIssues();
        const ok = await runUrsAnalyze({ preliminary: false });
        if (!ok) return;
        toast.success(`${selectedSubTool.name} complete!`);
        fetchSessions(false);
        setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        return;
      }

      const formData = new FormData();
      formData.append('mode', selectedSubTool.id);
      formData.append('prompt', synthesisPrompt);
      if (currentProject?.id) formData.append('projectId', String(currentProject.id));
      if (skipInterviewContext) formData.append('skipInterviewContext', '1');
      // Interview transcripts have just been ingested; sending them again would put the
      // whole document back into the prompt, which is the path this milestone retires.
      if (!isInterviewSynthesis) synthesisFiles.forEach(f => formData.append('files', f));

      const res = await api.post('/api/intelligence/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      applyAnalyzePayload(selectedSubTool, res.data.data);
      toast.success(`${selectedSubTool.name} complete!`);
      fetchSessions(false);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Analysis failed');
    } finally {
      setRunningStrategySynthesis(false);
      setSynthesisIngestStatus(null);
    }
  };

  // ── New session — clear everything ────────────────────────────────────────
  const handleNewSession = () => {
    setPrompt('');
    setInputData('');
    setResult(null);
    setStrategySynthesisResult(null);
    setImplementationResult(null);
    setShowImplementationConfig(false);
    setActiveSessionId(null);
    setToggles({ info: false, risks: false, processes: false, interviews: false });
    setProjectCache({ info: '', risks: '', processes: '' });
    setUploadedDocs([]);
    setChatMessages([]);
    setChatInput('');
    // Reset banner + warning state
    setShowDataAnalystBanner(false);
    setXlsWarning([]);
    // Reset pipeline state
    setPipelineStep(0);
    setPipelineRunning(false);
    setPipelineData({ memberResults: null, presidentEvaluation: null, personaResults: null, finalSynthesis: null, councilOutput: '', step1Failed: false, step2Failed: false, step3Failed: false, step4Failed: false });
    setShowImplementationReview(false);
    setPressureTestDismissed(false);
    setPipelinePrompt('');
    setPipelineSessionId(null);
    setCollapsedSteps(new Set());
    setFormCollapsed(false);
    resetAutoCollapseTracking();
    // Reset textarea heights
    if (promptRef.current) promptRef.current.style.height = 'auto';
    if (inputDataRef.current) inputDataRef.current.style.height = 'auto';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Run the council ────────────────────────────────────────────────────────
  const handleRun = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    if (selectedModels.length === 0) {
      toast.error('Please select at least one council member');
      return;
    }

    // Same hard gate as handleRunPipelineStep1 — see that function's comment for
    // the full rationale. Both entry points share buildCombinedData's EXCLUDED
    // marker logic (see below), but each needs its own pre-run confirmation gate
    // since a user can reach either path independently.
    const failedDocs = uploadedDocs.filter(d => d.extractionFailed);
    const unacknowledged = failedDocs.filter(d => !acknowledgedFailedDocsRef.current.has(d.name));
    if (unacknowledged.length > 0) {
      setFailedExtractionConfirmText('');
      setFailedExtractionWarning({
        failedDocNames: failedDocs.map(d => d.name),
        onConfirm: () => {
          failedDocs.forEach(d => acknowledgedFailedDocsRef.current.add(d.name));
          setFailedExtractionWarning(null);
          handleRun();
        },
      });
      return;
    }

    // Combine manual inputData + uploaded docs + any active project toggles.
    // Uses the same EXCLUDED-marker logic as buildCombinedData so a failed-extraction
    // file's name still reaches the model (with an explicit do-not-cite instruction)
    // instead of being silently omitted — see buildCombinedData's comment for why.
    const combinedData = buildCombinedData();
    // Snapshot NOW, at run time — not read later from live uploadedDocs state, which can
    // change (session switch, "Sync from server") before this result re-renders and would
    // otherwise make the excluded-documents banner silently vanish for a run that genuinely
    // excluded files (see CouncilResult.excludedDocNames doc comment).
    const excludedDocNamesAtRunTime = uploadedDocs.filter(d => d.extractionFailed).map(d => d.name);

    setRunning(true);
    setResult(null);

    try {
      const res = await api.post('/api/ai-council/run', {
        prompt,
        inputData: combinedData,
        selectedModels,
        project_id: currentProject?.id || null,
        verify: verificationMode !== 'off',
        verification_mode: verificationMode,
        web_search: webSearch,
        sessionId: isCollaboratorSession ? activeSessionId : undefined,
      }, { timeout: 600000 });
      const runData = unwrapStepData<CouncilResult & { sessionId: number; verification_metadata?: any; web_sources?: any[] }>(
        res, 'Council session failed — please try again.'
      );
      setResult({ ...runData, excludedDocNames: excludedDocNamesAtRunTime });
      setVerificationMetadata(runData.verification_metadata || null);
      setWebSources(runData.web_sources || []);
      setActiveSessionId(runData.sessionId || null);
      toast.success('Council session complete!');
      fetchSessions(); // refresh history list
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch (err: any) {
      toast.error(getApiError(err));
    } finally {
      setRunning(false);
    }
  };

  // ── Chat / refine after council ────────────────────────────────────────────
  const handleChat = async () => {
    if (chatLoading) return;
    if (chatMode !== 'word' && chatMode !== 'pptx' && !chatInput.trim() && chatAttachments.length === 0) return;

    // PPTX mode — explicit mode toggle or keyword detection
    const pptxKeywords = ['ppt', 'pptx', 'slide', 'slides', 'presentation', 'powerpoint', 'deck'];
    const excelDirectKeywords = ['excel', 'xlsx', 'spreadsheet', 'csv', 'in a sheet', 'as a sheet', 'as an excel', 'in excel', 'excel file', 'excel format', 'downloadable excel', 'export to excel', 'export as excel'];
    const tableKeywords = ['table'];
    const inputLower = chatInput.toLowerCase();
    if (chatMode === 'pptx' || pptxKeywords.some(kw => inputLower.includes(kw))) {
      const userRequest = chatInput.trim();
      setChatInput('');
      handleChatGeneratePptx(userRequest);
      return;
    }
    if (chatMode === 'excel' || excelDirectKeywords.some(kw => inputLower.includes(kw))) {
      const userRequest = chatInput.trim();
      setChatInput('');
      handleChatGenerateExcel(userRequest);
      return;
    }
    if (chatMode === 'word') {
      const userRequest = chatInput.trim();
      setChatInput('');
      handleChatGenerateWord(userRequest);
      return;
    }
    // In chat mode — show message + loading immediately, then classify intent
    const userRequest = chatInput.trim();
    const attachmentHeader = chatAttachments.length > 0
      ? chatAttachments.map(a => `📎 ${a.name}`).join('\n') + '\n\n'
      : '';
    const displayMsg = { role: 'user' as const, content: `${attachmentHeader}${userRequest}` };
    setChatMessages(prev => [...prev, displayMsg]);
    setChatInput('');
    setChatAttachments([]);
    setChatLoading(true);
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    try {
      // Detect last assistant output type to give context for "do it again" style requests
      const lastAssistantMsg = [...chatMessages].reverse().find(m => m.role === 'assistant' && !m.content.startsWith('__COVERAGE__'));
      const lastAssistantType = lastAssistantMsg?.content.startsWith('__EXCEL__') || lastAssistantMsg?.content.includes('\n\n__EXCEL__|||')
        ? 'EXCEL'
        : lastAssistantMsg?.content.startsWith('__PPTX__') || lastAssistantMsg?.content.includes('\n\n__PPTX__|||')
          ? 'PPTX'
          : null;
      const intentRes = await api.post('/api/ai-council/chat/classify-intent', { message: userRequest, lastAssistantType });
      const intent = intentRes.data?.intent;
      if (intent === 'PPTX') {
        setChatLoading(false);
        // If redo request, use the original user request that triggered the last PPTX
        const effectiveRequest = lastAssistantType === 'PPTX'
          ? ([...chatMessages].reverse().find((m, i, arr) => m.role === 'user' && arr.slice(0, i).some(a => a.role === 'assistant' && (a.content.startsWith('__PPTX__') || a.content.includes('\n\n__PPTX__|||'))))?.content || userRequest)
          : userRequest;
        handleChatGeneratePptx(effectiveRequest, false);
        return;
      }
      if (intent === 'EXCEL') {
        setChatLoading(false);
        // If redo request, use the original user request that triggered the last Excel
        const effectiveRequest = lastAssistantType === 'EXCEL'
          ? ([...chatMessages].reverse().find((m, i, arr) => m.role === 'user' && arr.slice(0, i).some(a => a.role === 'assistant' && (a.content.startsWith('__EXCEL__') || a.content.includes('\n\n__EXCEL__|||'))))?.content || userRequest)
          : userRequest;
        handleChatGenerateExcel(effectiveRequest, false);
        return;
      }
    } catch {
      // classification failed — fall through to regular chat
    }
    const isTableRequest = tableKeywords.some(kw => inputLower.includes(kw));
    if (isTableRequest) {
      // Will auto-trigger Excel after chat response — flag set below
    }
    const messageContent = chatAttachments.length > 0
      ? `${userRequest}\n\n${chatAttachments.map(a => `[Attached document: ${a.name}]\n${a.text}`).join('\n\n')}`
      : userRequest;
    const userMsg = { role: 'user' as const, content: messageContent };
    const newMessages = [...chatMessages, userMsg];
    try {
      const finalResponse =
        pipelineData.presidentEvaluation?.finalResponse ||
        result?.presidentEvaluation?.finalResponse || '';
      const memberResults =
        pipelineData.memberResults ||
        result?.memberResults || [];
      const res = await api.post('/api/ai-council/chat', {
        messages: newMessages,
        finalResponse,
        memberResults,
        originalPrompt: result?.prompt || prompt,
        inputData: buildCombinedData(),
        modelId: presidentModel || 'claude-opus-4-6',
        web_search: webSearch,
        sessionId: pipelineSessionId || activeSessionId || undefined,
      });
      if (res.data.success) {
        // Strip any hallucinated protocol tokens the AI may have generated
        const textReply = (res.data.data.reply as string)
          .replace(/__EXCEL__\|\|\|[^\n]*/g, '')
          .replace(/__PPTX__\|\|\|[^\n]*/g, '')
          .replace(/\n\n__EXCEL__\|\|\|[^\n]*/g, '')
          .replace(/\n\n__PPTX__\|\|\|[^\n]*/g, '')
          .trim();
        setChatWebSources(res.data.data?.web_sources || []);
        if (res.data.data?.meta) setChatContextMeta(res.data.data.meta);
        if (isTableRequest) {
          // Add text first, then generate Excel and update same message
          setChatMessages(prev => [...prev, { role: 'assistant', content: textReply }]);
          setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          // Generate Excel and append to same message
          const contextToUse =
            pipelineData.presidentEvaluation?.finalResponse ||
            result?.presidentEvaluation?.finalResponse ||
            (pipelineData.memberResults || result?.memberResults || [])
              .filter((r: any) => r.status === 'success')
              .map((r: any) => `### ${r.modelId}\n${r.response}`)
              .join('\n\n---\n\n');
          if (contextToUse) {
            setChatLoading(false); // stop "Thinking..." before Excel generation starts
            setExcelGenerating(true);
            try {
              const excelRes = await api.post('/api/ai-council/chat/generate-excel', {
                sessionPrompt: prompt, presidentSynthesis: contextToUse, userRequest: chatInput || userRequest,
              }, { responseType: 'blob', timeout: 120000 });
              const excelToken = excelRes.headers['x-file-token'];
              const excelServerFilename = excelRes.headers['x-file-name'];
              const stopWords = new Set(['give','me','a','an','the','of','by','from','for','in','on','with','about','and','or','to','is','are','that','this','these','those','all','any','some','get','make','create','show','list','generate','table','please','can','you','i','we','us','our']);
              const meaningful = (userRequest || prompt || 'council_data')
                .toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/)
                .filter((w: string) => w.length > 2 && !stopWords.has(w))
                .slice(0, 4).join('_') || 'council_data';
              const filename = excelServerFilename || `${meaningful}.xlsx`;
              const downloadUrl = excelToken
                ? `/api/ai-council/temp-files/${excelToken}`
                : window.URL.createObjectURL(new Blob([excelRes.data]));
              // Append Excel card to the last assistant message
              setChatMessages(prev => {
                const lastAssistantIdx = [...prev].reverse().findIndex(m => m.role === 'assistant');
                const idx = prev.length - 1 - lastAssistantIdx;
                const updated = prev.map((msg, i) =>
                  i === idx ? { ...msg, content: `${msg.content}\n\n__EXCEL__|||${downloadUrl}|||${filename}` } : msg
                );
                if (excelToken) saveChatHistory(updated);
                return updated;
              });
              setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
            } catch (err: any) {
              toast.error('Excel generation failed — text response saved above');
            } finally {
              setExcelGenerating(false);
            }
          }
        } else {
          const updatedMsgs = [...chatMessages, displayMsg, { role: 'assistant' as const, content: textReply }];
          setChatMessages(prev => [...prev, { role: 'assistant', content: textReply }]);
          setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
          saveChatHistory(updatedMsgs);
        }
      } else {
        toast.error(res.data.error || 'Chat failed');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Chat failed');
    } finally {
      setChatLoading(false);
    }
  };

  // ── Selectable models (not president) ─────────────────────────────────────
  const selectableModels = availableModels.filter(m => !m.isPresident);

  // ── Cost estimation ─────────────────────────────────────────────────────
  const MODEL_TOOLTIPS: Record<string, string> = {
    'claude-sonnet-4-6':  'Latest Claude — best all-round performance',
    'gpt-4.1':            'Strong analytical depth, large context window',
    'gpt-4.1-mini':       'Fast & cost-efficient GPT variant',
    'gpt-4o':             'Multimodal GPT — great for reasoning',
    'gpt-4o-mini':        'Fastest GPT — ideal for quick tasks',
    'o1':                 'Advanced reasoning for complex problems',
    'o3':                 'Most capable OpenAI reasoning model',
    'o3-mini':            'Lightweight reasoning model',
    'o1-mini':            'Lightweight O1 for faster reasoning',
    'mistral-large-3':    'European AI — strong at structured tasks',
    'gemini-1.5-pro':     'Best for long documents & large context',
    'gemini-1.5-flash':   'Fast & efficient Gemini variant',
    'gemini-pro':         'Google general-purpose model',
    'gemini-ultra':       'Most capable Gemini model',
  };

  // Prices in USD per 1M tokens. MUST match the backend pricing seed at
  // backend/src/config/pricing/modelPricingSeed.js — frontend used to ship a
  // stale copy where Opus was listed at $5/$25 (actual: $15/$75), which is
  // why pre-run estimates were ~3x lower than actuals on Opus-heavy runs.
  const MODEL_PRICING: Record<string, { input: number; output: number }> = {
    // Anthropic — Claude 4.x
    'claude-opus-4-7': { input: 15.00, output: 75.00 },
    'claude-opus-4-6': { input: 15.00, output: 75.00 },
    'claude-opus-4-5': { input: 15.00, output: 75.00 },
    'claude-opus-4':   { input: 15.00, output: 75.00 },
    'claude-sonnet-4-6': { input: 3.00, output: 15.00 },
    'claude-sonnet-4-5': { input: 3.00, output: 15.00 },
    'claude-sonnet-4':   { input: 3.00, output: 15.00 },
    'claude-haiku-4-5-20251001': { input: 1.00, output: 5.00 },
    // OpenAI
    'gpt-4.1':      { input: 2.00,  output: 8.00 },
    'gpt-4.1-mini': { input: 0.40,  output: 1.60 },
    'gpt-4o':       { input: 2.50,  output: 10.00 },
    'gpt-4o-mini':  { input: 0.15,  output: 0.60 },
    'o1':           { input: 15.00, output: 60.00 },
    'o1-mini':      { input: 3.00,  output: 12.00 },
    'o3':           { input: 2.00,  output: 8.00 },
    'o3-mini':      { input: 1.10,  output: 4.40 },
    // Google
    'gemini-1.5-pro':   { input: 1.25, output: 5.00 },
    'gemini-1.5-flash': { input: 0.075, output: 0.30 },
    'gemini-pro':       { input: 0.50, output: 1.50 },
    'gemini-pro-vision':{ input: 0.50, output: 1.50 },
    'gemini-ultra':     { input: 5.00, output: 15.00 },
    // Mistral
    'mistral-large-3':     { input: 2.00, output: 6.00 },
    'mistral-large-latest':{ input: 2.00, output: 6.00 },
  };

  const getModelCost = (modelId: string) => MODEL_PRICING[modelId] || { input: 3.00, output: 15.00 };

  const documentChars = uploadedDocs.reduce((sum, d) => sum + d.text.length, 0);
  const totalInputChars = prompt.length + inputData.length + documentChars
    + (toggles.info ? projectCache.info.length : 0)
    + (toggles.risks ? projectCache.risks.length : 0)
    + (toggles.processes ? projectCache.processes.length : 0)
    + (toggles.interviews ? projectCache.interviews.length : 0);
  // Anthropic counts ~3.5 chars/token (English text). Old formula used 4, which
  // underestimates by ~14%. We also factor in the deep-reasoning system prompt
  // overhead (~1500 tokens) that every council member receives.
  const CHARS_PER_TOKEN = 3.5;
  const SYSTEM_PROMPT_TOKENS = 1500; // deep-reasoning instructions + persona frame
  const estimatedInputTokens = (totalInputChars / CHARS_PER_TOKEN) + SYSTEM_PROMPT_TOKENS;

  // Realistic per-step output sizes, calibrated against live usage_events data.
  // Members run deep reasoning → routinely 4-6k output tokens (was 2k).
  // Step 2 first synthesis routinely emits 5-7k tokens (was 4k).
  // Step 3 specialists hit ~8k each (matches old assumption — kept).
  // Step 4 final synthesis hits ~6-8k (was 8k — close enough).
  // Step 5 closing summary ~3k.
  const MEMBER_OUTPUT_TOKENS = 5000;
  const STEP2_OUTPUT_TOKENS  = 6000;
  const STEP3_OUTPUT_TOKENS  = 8000;
  const STEP4_OUTPUT_TOKENS  = 7000;
  const STEP5_OUTPUT_TOKENS  = 3000;

  const PERSONA_PROMPT_TOKENS = 2000;
  const PROMPT_OVERHEAD_TOKENS = (prompt.length / CHARS_PER_TOKEN) + SYSTEM_PROMPT_TOKENS;
  const STEP4_DOC_EXCERPT_CHARS = 8000;
  const step4DocTokens = Math.min(totalInputChars, STEP4_DOC_EXCERPT_CHARS) / CHARS_PER_TOKEN;

  const councilCostBreakdown = (() => {
    const presidentPricing = getModelCost(presidentModel);
    const implPricing = getModelCost(implementationModel || presidentModel);
    const numPersonas = implementationPersonas.length || 6;
    const sonnetPricing = getModelCost('claude-sonnet-4-6');
    const allMemberOutputTokens = MEMBER_OUTPUT_TOKENS * selectedModels.length;

    const step1 = selectedModels.reduce((sum, modelId) => {
      const p = getModelCost(modelId);
      return sum + (estimatedInputTokens * p.input) / 1_000_000 + (MEMBER_OUTPUT_TOKENS * p.output) / 1_000_000;
    }, 0);

    const step2InputTokens = PROMPT_OVERHEAD_TOKENS + allMemberOutputTokens
      + (uploadedDocs.length > 0 ? (totalInputChars / CHARS_PER_TOKEN) : 0);
    const step2 = (step2InputTokens * presidentPricing.input) / 1_000_000
                + (STEP2_OUTPUT_TOKENS * presidentPricing.output) / 1_000_000;

    const step3InputTokens = STEP2_OUTPUT_TOKENS + PERSONA_PROMPT_TOKENS + PROMPT_OVERHEAD_TOKENS;
    const step3 = (step3InputTokens * implPricing.input * numPersonas) / 1_000_000
                + (STEP3_OUTPUT_TOKENS * implPricing.output * numPersonas) / 1_000_000;

    const step4InputTokens = STEP2_OUTPUT_TOKENS + PROMPT_OVERHEAD_TOKENS + step4DocTokens
      + (STEP3_OUTPUT_TOKENS * numPersonas);
    const step4 = (step4InputTokens * presidentPricing.input) / 1_000_000
                + (STEP4_OUTPUT_TOKENS * presidentPricing.output) / 1_000_000;

    const step5 = (4000 * sonnetPricing.input) / 1_000_000
                + (STEP5_OUTPUT_TOKENS * sonnetPricing.output) / 1_000_000;

    return { step1, step2, step3, step4, step5, total: step1 + step2 + step3 + step4 + step5 };
  })();

  const councilCostEstimate = councilCostBreakdown.total;


  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  // ── Loading state while models + last session are being fetched ───────────
  if (loadingModels) {
    return (
      <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 120px)' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="p-4 bg-purple-100 rounded-2xl">
            <Crown className="w-8 h-8 text-purple-700" />
          </div>
          <Loader2 className="w-7 h-7 text-purple-600 animate-spin" />
          <p className="text-sm text-gray-500 font-medium">Loading AI Council...</p>
        </div>
      </div>
    );
  }

  const orchestratorBanner = orchestratorCtx.inRouter ? (
    <>
    <OrchestratorProjectMismatchBanner />
    <SmartRouterBanner
      stepLabel="AI Council"
      canFinish={!!finalCouncilResponse}
      disabledReason="Run the council and let it finish before returning to Smart Router."
      buildCompletionPayload={() => {
        if (!finalCouncilResponse) return null;
        const finalText = typeof finalCouncilResponse === 'string'
          ? finalCouncilResponse
          : '';
        return {
          artifact: {
            refs: [
              {
                kind: 'ai_council_session',
                session_id: pipelineSessionId,
                label: 'AI Council session',
              },
            ],
            output_payload: {
              kind: 'ai_council_session',
              council_session_id: pipelineSessionId,
              final_response: finalText,
              analysis_narrative: finalText,
              president_evaluation: pipelineData?.presidentEvaluation || null,
              convergence: pipelineData?.presidentEvaluation?.convergence || null,
              divergence: pipelineData?.presidentEvaluation?.divergence || null,
              quality_assessment: pipelineData?.presidentEvaluation?.qualityAssessment || null,
              member_results: Array.isArray(pipelineData?.memberResults)
                ? pipelineData.memberResults
                    .filter((m: any) => m && (m.modelName || m.modelId))
                    .map((m: any) => ({
                      model: m.modelName || m.modelId,
                      response: typeof m.response === 'string' ? m.response : '',
                    }))
                : [],
            },
            verifiable_text: finalText,
          },
          summary: 'AI Council produced a synthesized compliance narrative.',
        };
      }}
    />
    </>
  ) : null;

  return (
    <>
    {orchestratorBanner}
    {/* Rendered at the root so the overlay is positioned against the viewport rather than
        against whichever panel happens to enclose the button that opened it. */}
    <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />
    <div className="min-h-screen bg-gray-50 p-6">

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Crown className="w-6 h-6 text-purple-700" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">AI Council</h1>
          </div>
          <p className="text-gray-500 ml-14 text-sm">
            Submit a prompt and data to multiple AI models simultaneously. A Council President ({presidentModelName}) evaluates all responses — finding convergence, divergence, and synthesizing the best combined answer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewSession}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border bg-purple-700 border-purple-700 text-white hover:bg-purple-800 transition-all whitespace-nowrap"
          >
            <PlusCircle className="w-4 h-4" />
            New Session
          </button>
          <SessionCollaboratorsList
            variant="dropdown"
            accentColor="purple"
            sessionId={pipelineSessionId || activeSessionId}
            isOwner={!isCollaboratorSession}
            getCollaborators={getAICouncilCollaborators}
            getAvailable={getAvailableAICouncilCollaborators}
            getAllUsers={getTenantUsers}
            addCollaborator={addAICouncilCollaborator}
            removeCollaborator={removeAICouncilCollaborator}
            onError={msg => toast.error(msg)}
            onCollaboratorsChanged={() => fetchSessions()}
          />
          <button
            onClick={() => { setShowHistory(p => !p); if (!showHistory) fetchSessions(); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
              showHistory ? 'bg-purple-100 border-purple-300 text-purple-700' : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:text-purple-700'
            }`}
          >
            <History className="w-4 h-4" />
            History
            {(activeMode === 'strategy-synthesis' ? strategySessions.length : sessions.length) > 0 && (
              <span className="bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                {activeMode === 'strategy-synthesis' ? strategySessions.length : sessions.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ── Session History Panel ─────────────────────────────────────────── */}
      {showHistory && (() => {
        // Scope history to active tab: Council tab shows council/pipeline sessions;
        // Strategy Synthesis tab shows intelligence strategy-synthesis sessions.
        const isStrategyTab = activeMode === 'strategy-synthesis';
        const tabLabel = isStrategyTab ? 'Strategy Synthesis' : 'AI Council';
        const isEmpty = isStrategyTab ? strategySessions.length === 0 : sessions.length === 0;
        return (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <History className="w-4 h-4 text-purple-600" />
                Past Sessions
                <span className="text-xs font-normal text-gray-400 ml-1">— {tabLabel}</span>
              </div>
              <span className="text-xs text-gray-400">Click to reload a session</span>
            </div>
            {loadingSessions ? (
              <div className="flex items-center gap-2 text-gray-400 text-sm p-5">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : isEmpty ? (
              <p className="text-sm text-gray-400 p-5 text-center">No past sessions for {tabLabel}</p>
            ) : isStrategyTab ? (
              // ── Strategy Synthesis sessions (from Intelligence table) ────────
              <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                {strategySessions
                  .slice()
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .map(s => (
                    <div
                      key={`strat-${s.id}`}
                      title={`${s.mode_name} · ${new Date(s.created_at).toLocaleString()}`}
                      onClick={() => loadStrategySession(s.id)}
                      className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-300" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded bg-purple-50 text-purple-600">{s.mode_name}</span>
                            <p className="text-sm text-gray-800 truncate max-w-xl">{s.title}</p>
                          </div>
                          <span className="text-xs text-gray-400 mt-0.5 block">{new Date(s.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              // ── AI Council / Pipeline sessions ───────────────────────────────
              <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                {sessions.map(s => (
                  <div
                    key={s.id}
                    title={`${s.title || s.prompt || 'AI Council session'} · ${new Date(s.created_at).toLocaleString()} · ${s.selected_models?.length || 0} models`}
                    onClick={() => loadSession(s.id)}
                    className={`flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${activeSessionId === s.id ? 'bg-purple-50' : ''}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${activeSessionId === s.id ? 'text-purple-600' : 'text-gray-300'}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <Crown className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                          <p className="text-sm text-gray-800 truncate max-w-xl">{s.title || s.prompt}</p>
                          {s.is_collaborator && (
                            <span className="flex-shrink-0 text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">shared</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-gray-400">{new Date(s.created_at).toLocaleString()}</span>
                          {s.summary?.successfulModels != null ? (
                            <span className="text-xs text-green-600">{s.summary.successfulModels} succeeded</span>
                          ) : null}
                          <span className="text-xs text-gray-400">{s.selected_models?.length} models</span>
                          {s.project_id && projects?.find(p => p.id === s.project_id) && (
                            <span className="text-xs text-purple-500 truncate max-w-[120px]">
                              {projects.find(p => p.id === s.project_id)!.name}
                            </span>
                          )}
                          {s.collaborator_names && s.collaborator_names.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 border border-purple-200">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-500">Collaborators</span>
                              <span className="text-[10px] text-purple-400">{s.collaborator_names.join(' · ')}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="flex-shrink-0 p-1.5 text-gray-300 hover:text-red-400 rounded transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Collaborator banner ─────────────────────────────────────────── */}
      {isCollaboratorSession && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-6">
          <p className="text-blue-900 font-medium">You are a collaborator on this session</p>
          <p className="text-blue-700 text-sm">You have full access to all features and can contribute to this AI Council session.</p>
        </div>
      )}


      {/* ── Setup Card ──────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        {/* Collapsible header — shown after Step 1 runs */}
        {pipelineData.memberResults && (
          <div
            className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 cursor-pointer select-none"
            onClick={() => setFormCollapsed(p => !p)}
          >
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <CheckCircle2 className="w-3 h-3 text-white" />
            </div>
            <span className="text-sm font-semibold text-gray-700 truncate flex-1">{(pipelinePrompt || prompt || '').slice(0, 80)}{(pipelinePrompt || prompt || '').length > 80 ? '…' : ''}</span>
            <span className="text-xs text-gray-400">{selectedModels.length} models</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${formCollapsed ? '' : 'rotate-180'}`} />
          </div>
        )}
        <div className={`p-6 ${formCollapsed ? 'hidden' : ''}`}>

        {/* Mode tabs */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 mb-6 w-fit">
          <button
            onClick={() => setActiveMode('council')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeMode === 'council' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            AI Council
          </button>
          <button
            onClick={() => setActiveMode('strategy-synthesis')}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              activeMode === 'strategy-synthesis' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Sparkles className="w-4 h-4" />
            Strategy Synthesis
          </button>
        </div>

        {/* Prompt — council mode only */}
        <div className={`mb-5${activeMode !== 'council' ? ' hidden' : ''}`}>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-semibold text-gray-700">
              Your Prompt <span className="text-red-500">*</span>
            </label>
            <button
              onClick={() => showPromptHelper ? handleCloseHelper() : setShowPromptHelper(true)}
              className="flex items-center gap-1.5 text-xs text-purple-600 hover:text-purple-800 font-medium transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {showPromptHelper ? 'Cancel' : 'Help me write a prompt'}
            </button>
          </div>

          {/* Prompt Helper — replaces textarea when open */}
          {showPromptHelper ? (
            <div className="border border-purple-200 rounded-lg overflow-hidden">
              {/* Step 1: Description input */}
              {!generatedPromptPreview && (
                <div className="p-4 bg-purple-50">
                  <p className="text-xs text-purple-700 font-medium mb-2">
                    Describe what you want to analyze or achieve — we'll turn it into an effective prompt.
                  </p>
                  <textarea
                    rows={3}
                    value={promptDescription}
                    onChange={e => setPromptDescription(e.target.value)}
                    placeholder="e.g. I want to understand the risks of moving our payroll system to the cloud..."
                    className="w-full border border-purple-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none bg-white"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">
                      Powered by {availableModels.find(m => selectedModels.includes(m.id) && m.available)?.name || 'Claude Opus 4.6'}
                    </span>
                    <button
                      onClick={handleGeneratePrompt}
                      disabled={generatingPrompt || !promptDescription.trim()}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        generatingPrompt || !promptDescription.trim()
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-purple-700 hover:bg-purple-800 text-white'
                      }`}
                    >
                      {generatingPrompt ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
                      ) : (
                        <><Sparkles className="w-3.5 h-3.5" /> Generate Prompt</>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 2: Preview generated prompt */}
              {generatedPromptPreview && (
                <div className="p-4 bg-white">
                  <p className="text-xs text-green-700 font-medium mb-2">
                    {generatingPrompt ? '⟳ Generating new prompt...' : '✓ Prompt generated — review and use it:'}
                  </p>
                  <div className={`bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 mb-3 whitespace-pre-wrap relative ${generatingPrompt ? 'opacity-50' : ''}`}>
                    {generatingPrompt ? (
                      <div className="flex items-center gap-2 text-purple-600">
                        <Loader2 className="w-4 h-4 animate-spin" /> Regenerating...
                      </div>
                    ) : generatedPromptPreview}
                  </div>
                  <div className="flex items-center gap-2 justify-end">
                    <button
                      onClick={handleGeneratePrompt}
                      disabled={generatingPrompt}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                        generatingPrompt ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {generatingPrompt ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Regenerate
                    </button>
                    <button
                      onClick={handleUseGeneratedPrompt}
                      disabled={generatingPrompt}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${generatingPrompt ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-purple-700 hover:bg-purple-800 text-white'}`}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" /> Use this prompt
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <textarea
              ref={el => {
                promptRef.current = el;
                if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
              }}
              rows={4}
              value={prompt}
              onChange={e => {
                setPrompt(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              placeholder="What do you want the AI Council to analyze or answer?"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none overflow-hidden"
            />
          )}
        </div>

        {/* Sources — council mode only */}
        <div className={`mb-6${activeMode !== 'council' ? ' hidden' : ''}`}>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            Sources <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <div className="border border-gray-200 rounded-xl shadow-sm bg-white overflow-hidden">
            {/* Tab bar */}
            <div className="flex border-b border-gray-200">
              {([
                { id: 'upload' as const, label: 'Upload Files', icon: Upload },
                { id: 'paste' as const, label: 'Paste Text', icon: Type },
                { id: 'project' as const, label: 'Project Data', icon: Database },
              ]).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setSourceTab(id)}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium transition-all border-b-2 ${
                    sourceTab === id
                      ? 'border-blue-500 text-blue-700 bg-blue-50/50'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-4">
              {/* Smart Data Analyst banner — shown when Excel/CSV uploaded */}
              {showDataAnalystBanner && (
                <div className="mb-3 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <span className="text-amber-500 mt-0.5 flex-shrink-0">⚠️</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-amber-800">Quantitative data detected</p>
                    <p className="text-amber-700 mt-0.5">For accurate numeric analysis on Excel/CSV files, use <strong>Smart Data Analyst</strong> in Jarvis Intelligence — it executes real Python code on your data instead of estimating.</p>
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={() => {
                          const excelFiles = uploadedDocs.filter(d => /\.(xlsx?|csv)$/i.test(d.name) && d.file).map(d => d.file!);
                          sessionStorage.setItem('_sda_intent', JSON.stringify({ prompt, fileNames: excelFiles.map(f => f.name) }));
                          // Store files in a ref accessible to Intelligence via custom event
                          const dt = new DataTransfer();
                          excelFiles.forEach(f => dt.items.add(f));
                          (window as any)._sdaFiles = excelFiles;
                          navigate('/app/intelligence', { state: { mode: 'smart-data-analyst', prompt, fileNames: excelFiles.map(f => f.name) } });
                        }}
                        className="px-3 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 transition-colors"
                      >
                        Go to Smart Data Analyst
                      </button>
                      <button
                        onClick={() => setShowDataAnalystBanner(false)}
                        className="px-3 py-1 text-xs text-amber-700 hover:text-amber-900 transition-colors"
                      >
                        Continue here anyway
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {sourceTab === 'upload' && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.md,.markdown,.json,text/csv,text/markdown,.png,.jpg,.jpeg,.gif,.webp,image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    className="hidden"
                    multiple
                    onChange={handleDocumentUpload}
                  />
                  <div className="flex flex-col gap-2">
                    {xlsWarning.length > 0 && (
                      <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-amber-800">Old Excel format not supported</p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            <strong>{xlsWarning.join(', ')}</strong> is in old <strong>.xls</strong> format which cannot be read. Please open in Excel, save as <strong>.xlsx</strong>, and re-upload.
                          </p>
                        </div>
                        <button onClick={() => setXlsWarning([])} className="text-amber-400 hover:text-amber-600 flex-shrink-0">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {smartStartPrefill && uploadedDocs.length > 0 && (
                      <div className="flex items-start gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <FileText className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-purple-800">
                            Loaded {smartStartPrefill.count} document{smartStartPrefill.count === 1 ? '' : 's'} from your SmartStart session
                          </p>
                          {smartStartPrefill.sessionTitle && (
                            <p className="text-xs text-purple-600 mt-0.5 truncate" title={smartStartPrefill.sessionTitle}>
                              From: {smartStartPrefill.sessionTitle}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => { setUploadedDocs([]); setSmartStartPrefill(null); }}
                          className="text-purple-400 hover:text-purple-600 flex-shrink-0"
                          title="Clear these documents"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    {uploadedDocs.map((doc, i) => (
                      <div key={i} className={`border rounded-lg overflow-hidden ${doc.extractionFailed ? 'border-red-300' : 'border-purple-100'}`}>
                        <div className={`flex items-center justify-between px-3 py-2 ${doc.extractionFailed ? 'bg-red-50' : 'bg-purple-50'}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {doc.extractionFailed ? (
                              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            ) : (
                              <FileText className="w-4 h-4 text-purple-500 flex-shrink-0" />
                            )}
                            <span className={`text-sm truncate ${doc.extractionFailed ? 'text-red-700 font-medium' : 'text-gray-700'}`}>{doc.name}</span>
                            {doc.extractionFailed ? (
                              <span className="text-xs text-red-600 font-semibold flex-shrink-0" title={doc.extractionError || undefined}>
                                ⚠ Extraction failed — excluded from analysis
                              </span>
                            ) : (
                              <button
                                onClick={() => setExpandedDocIndex(expandedDocIndex === i ? null : i)}
                                className="text-xs text-purple-500 hover:text-purple-700 flex-shrink-0 underline"
                              >
                                {(doc.text.length / 1000).toFixed(1)}k chars {expandedDocIndex === i ? '▲' : '▼'}
                              </button>
                            )}
                          </div>
                          <button
                            onClick={() => { setUploadedDocs(prev => prev.filter((_, idx) => idx !== i)); if (expandedDocIndex === i) setExpandedDocIndex(null); }}
                            className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {expandedDocIndex === i && (
                          <div className="border-t border-purple-100">
                            <div className="flex justify-end px-3 py-1 bg-gray-50 border-b border-purple-100">
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(doc.text);
                                  setCopiedDocIndex(i);
                                  setTimeout(() => setCopiedDocIndex(null), 2000);
                                }}
                                className="flex items-center gap-1 text-xs text-gray-500 hover:text-purple-600 transition-colors"
                              >
                                {copiedDocIndex === i ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                                {copiedDocIndex === i ? 'Copied!' : 'Copy'}
                              </button>
                            </div>
                            <pre className="text-xs text-gray-600 bg-white px-3 py-2 max-h-48 overflow-y-auto whitespace-pre-wrap">
                              {doc.text.slice(0, 3000)}{doc.text.length > 3000 ? '\n\n... (truncated for preview)' : ''}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                    {pipelineData.memberResults && (
                      <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />
                        <span>Documents added here will be included in <strong>Step 2, 3 &amp; 4</strong> without re-running Step 1.</span>
                      </div>
                    )}
                    <div
                      onClick={() => !uploadingDoc && fileInputRef.current?.click()}
                      className="border-2 border-dashed border-gray-200 rounded-lg px-4 py-10 text-center cursor-pointer hover:border-purple-300 hover:bg-purple-50 transition-all"
                    >
                      {uploadingDoc ? (
                        <div className="flex items-center justify-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                          <span className="text-xs text-gray-400">Processing...</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 text-gray-300 mx-auto mb-1" />
                          <p className="text-xs text-gray-400">
                            {uploadedDocs.length === 0
                              ? 'Drop files or click to upload'
                              : `Add more files (${uploadedDocs.length} uploaded)`}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}

              {sourceTab === 'paste' && (
                <textarea
                  ref={el => {
                    inputDataRef.current = el;
                    if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; }
                  }}
                  rows={5}
                  value={inputData}
                  onChange={e => {
                    setInputData(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  placeholder="Paste any data here — JSON, text, risk data, financial data, process descriptions, etc."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none overflow-hidden font-mono"
                />
              )}

              {sourceTab === 'project' && (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">Project:</span>
                    <select
                      value={currentProject?.id ?? ''}
                      onChange={e => {
                        const proj = projects.find(p => String(p.id) === e.target.value) || null;
                        setCurrentProject(proj);
                        setToggles({ info: false, risks: false, processes: false, interviews: false });
                        setProjectCache({ info: '', risks: '', processes: '', interviews: '' });
                      }}
                      className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 max-w-[240px]"
                    >
                      <option value="">Select a project…</option>
                      {projects.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <p className="text-xs text-gray-500">Toggle project data sources to include in the analysis:</p>
                  <div className="flex items-center gap-2">
                    {([
                      { key: 'info' as const, label: 'Project Info' },
                      { key: 'risks' as const, label: 'Risk Data' },
                      { key: 'processes' as const, label: 'Process Maps' },
                      { key: 'interviews' as const, label: 'Interview Intelligence' },
                    ]).map(({ key, label }) => {
                      const isOn = toggles[key];
                      const isLoading = toggleLoading[key];
                      const noProject = !currentProject;
                      return (
                        <div key={key} className="relative group">
                          <button
                            onClick={() => toggleProjectData(key)}
                            disabled={isLoading || noProject}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${
                              noProject
                                ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed'
                                : isLoading
                                  ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-wait'
                                  : isOn
                                    ? 'bg-purple-50 border-purple-500 text-purple-700'
                                    : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                            }`}
                          >
                            {isLoading ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <span className={`w-2 h-2 rounded-full ${isOn ? 'bg-purple-500' : 'bg-gray-300'}`} />
                            )}
                            {label}
                          </button>
                          {noProject && (
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              Select a project above to enable
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Source pills summary */}
            {(uploadedDocs.length > 0 || inputData.trim() || toggles.info || toggles.risks || toggles.processes || toggles.interviews) && (
              <div className="px-4 pb-3 flex flex-wrap items-center gap-1.5">
                {uploadedDocs.map((doc, i) => (
                  <span key={`doc-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 border border-purple-200 rounded-full text-[11px] text-purple-700 font-medium">
                    <FileText className="w-3 h-3" />
                    {doc.name}
                    <span className="text-purple-400">({(doc.text.length / 1000).toFixed(1)}k)</span>
                    <button onClick={() => setUploadedDocs(prev => prev.filter((_, idx) => idx !== i))} className="ml-0.5 text-purple-400 hover:text-red-500">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                {inputData.trim() && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-[11px] text-blue-700 font-medium">
                    <Type className="w-3 h-3" />
                    Pasted Text ({inputData.length.toLocaleString()} chars)
                  </span>
                )}
                {toggles.info && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full text-[11px] text-green-700 font-medium">
                    Project Info &#10003;
                  </span>
                )}
                {toggles.risks && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full text-[11px] text-green-700 font-medium">
                    Risk Data &#10003;
                  </span>
                )}
                {toggles.processes && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full text-[11px] text-green-700 font-medium">
                    Process Maps &#10003;
                  </span>
                )}
                {toggles.interviews && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 border border-green-200 rounded-full text-[11px] text-green-700 font-medium">
                    Interview Intelligence &#10003;
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── AI Council mode: model selector ──────────────────────────── */}
        {activeMode === 'council' && (
          <>
            {projectIngest.ingesting && (
              <div className="mb-4 flex items-center gap-2 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                Reading interviews in the background
                {projectIngest.snapshot.progressDetail?.filename
                  ? ` — ${projectIngest.snapshot.progressDetail.current} of ${projectIngest.snapshot.progressDetail.total} · ${projectIngest.snapshot.progressDetail.filename}`
                  : projectIngest.snapshot.progress ? ` — ${projectIngest.snapshot.progress}%` : ''}
                . Ingest keeps running if you leave this page.
              </div>
            )}
            {/* Council Members */}
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-gray-600" />
                <label className="text-sm font-semibold text-gray-700">Council Members</label>
                <span className="text-xs text-gray-400">({selectedModels.length} selected, max 6)</span>
              </div>

              {loadingModels ? (
                <div className="flex items-center gap-2 text-gray-400 text-sm py-4">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading available models...
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { provider: 'Anthropic', label: 'Claude',  subtitle: 'by Anthropic', accent: 'border-t-orange-400', badgeCls: 'bg-orange-100 text-orange-700' },
                    { provider: 'OpenAI',    label: 'GPT',     subtitle: 'by OpenAI',    accent: 'border-t-green-400',  badgeCls: 'bg-green-100 text-green-700'  },
                    { provider: 'Google',    label: 'Gemini',  subtitle: 'by Google',    accent: 'border-t-blue-400',   badgeCls: 'bg-blue-100 text-blue-700'    },
                    { provider: 'Mistral',   label: 'Mistral', subtitle: 'by Mistral AI',accent: 'border-t-purple-400', badgeCls: 'bg-purple-100 text-purple-700' },
                  ].map(({ provider, label, subtitle, accent, badgeCls }) => {
                    const group = selectableModels.filter(m => m.provider === provider);
                    const selectedCount = group.filter(m => selectedModels.includes(m.id)).length;
                    return (
                      <div key={provider} className={`rounded-xl border-2 border-t-4 border-gray-200 bg-white shadow-sm overflow-hidden ${accent}`}>
                        <div className="px-4 pt-4 pb-3 border-b border-gray-100">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-bold text-gray-800">{label}</p>
                              <p className="text-xs text-gray-400">{subtitle}</p>
                            </div>
                            {selectedCount > 0 && (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeCls}`}>{selectedCount} selected</span>
                            )}
                          </div>
                        </div>
                        <div className="p-3 grid grid-cols-2 gap-2">
                          {group.length === 0 ? (
                            <p className="col-span-2 text-xs text-gray-400 text-center py-3">No models available</p>
                          ) : (
                            group.map(model => {
                              const isSelected = selectedModels.includes(model.id);
                              const isDisabled = !model.available;
                              return (
                                <button
                                  key={model.id}
                                  onClick={() => !isDisabled && toggleModel(model.id)}
                                  disabled={isDisabled}
                                  title={MODEL_TOOLTIPS[model.id] || ''}
                                  className={`relative flex flex-col items-start p-3 rounded-lg border-2 text-left transition-all ${
                                    isDisabled ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                                    : isSelected ? 'border-purple-500 bg-purple-50'
                                    : 'border-gray-200 bg-white hover:border-purple-300'
                                  }`}
                                >
                                  {isSelected && <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-purple-600" />}
                                  <span className="text-xs font-semibold text-gray-800 pr-5 leading-tight">{model.name}</span>
                                  <span className={`mt-1.5 text-[10px] px-1.5 py-0.5 rounded font-medium ${PROVIDER_COLORS[model.provider] || 'bg-gray-100 text-gray-600'}`}>
                                    {model.provider}
                                  </span>
                                  {isDisabled && <span className="text-[10px] text-gray-400 mt-1">No API key</span>}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* President info bar */}

            <div className="max-w-md mx-auto mb-3">
              <VerificationToggle
                mode={verificationMode}
                onModeChange={setVerificationMode}
                disabled={pipelineRunning || running}
              />
            </div>

            {/* Web Search toggle — hidden if tenant has disabled it */}
            {webSearchTenantEnabled && (
              <div className="flex justify-center mb-4">
                <button
                  onClick={() => setWebSearch(prev => !prev)}
                  disabled={pipelineRunning || running}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                    webSearch
                      ? 'bg-blue-50 border-blue-400 text-blue-700'
                      : 'bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  <Globe className={`w-4 h-4 ${webSearch ? 'text-blue-500' : 'text-gray-400'}`} />
                  Web Search
                  {webSearch && <span className="w-2 h-2 rounded-full bg-blue-500 ml-1" />}
                </button>
              </div>
            )}

            {/* Run Council button — Step 1 of pipeline */}
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={handleRunPipelineStep1}
                disabled={pipelineRunning || running || clarifyLoading || !prompt.trim() || selectedModels.length === 0}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                  pipelineRunning || running || clarifyLoading || !prompt.trim() || selectedModels.length === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-700 hover:bg-purple-800 text-white shadow-sm'
                }`}
              >
                {clarifyLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Preparing questions...</>
                ) : pipelineRunning && pipelineStep === 1 ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Running Step 1 — Council Members...</>
                ) : (
                  <><Play className="w-4 h-4" /> Step 1 — Run Council Members <span className="opacity-75 font-normal">(~${councilCostBreakdown.step1.toFixed(2)})</span></>
                )}
              </button>
              <div className="relative group">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                  <DollarSign className="w-3.5 h-3.5" />
                  Est. cost to run the council: ${councilCostEstimate.toFixed(2)}
                </div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-gray-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  Includes Steps 1–5: council members, president, implementation review, final synthesis, and preview outline
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Strategy Synthesis mode ──────────────────────────────────── */}
        {activeMode === 'strategy-synthesis' && (
          <>
            {/* Sub-tool selector */}
            <div className="mb-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Select a Strategic Tool</p>
              {/* Thin-strip tiles matching Jarvis Intelligence: icon + name in one row, no visible
                  description, and a "?" that reveals the details on hover. */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 items-stretch">
                {STRATEGIC_SUBTOOLS.map((tool) => {
                  const isActive = selectedSubTool?.id === tool.id;
                  return (
                    <div key={tool.id} className="relative flex flex-col h-full">
                      <button
                        onClick={() => {
                          setSelectedSubTool(tool);
                          setStrategySynthesisResult(null);
                          setSynthesisPrompt('');
                          setSkipInterviewContext(false);
                        }}
                        className={`relative flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left hover:shadow-md w-full h-full min-h-[68px] ${
                          isActive ? 'border-purple-500 shadow-md bg-purple-50' : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tool.color + '18' }}>
                          <Sparkles style={{ color: tool.color, width: 18, height: 18 }} />
                        </div>
                        <p className={`text-sm font-semibold pr-5 line-clamp-2 ${isActive ? 'text-purple-700' : 'text-gray-800'}`} title={tool.name}>{tool.name}</p>
                        {isActive && <div className="absolute bottom-2 right-2 w-2 h-2 rounded-full" style={{ backgroundColor: tool.color }} />}
                      </button>
                      {tool.description && (
                        <div
                          className="absolute top-2 right-2 z-10"
                          onMouseEnter={(e) => {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setSubtoolHelpPos({ top: rect.bottom + 6, left: Math.min(rect.right - 280, window.innerWidth - 296) });
                            setSubtoolHelpId(tool.id);
                          }}
                          onMouseLeave={() => setSubtoolHelpId(null)}
                        >
                          <HelpCircle size={14} className="text-gray-300 hover:text-purple-600 transition-colors cursor-pointer" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Help popover — fixed overlay showing the tool's details, on top of everything. */}
              {subtoolHelpId && (() => {
                const t = STRATEGIC_SUBTOOLS.find((s) => s.id === subtoolHelpId);
                if (!t) return null;
                return (
                  <div
                    onMouseEnter={() => setSubtoolHelpId(subtoolHelpId)}
                    onMouseLeave={() => setSubtoolHelpId(null)}
                    className="bg-white border border-gray-200 rounded-xl shadow-2xl p-4 text-left"
                    style={{ position: 'fixed', top: subtoolHelpPos.top, left: subtoolHelpPos.left, width: 280, zIndex: 9999 }}
                  >
                    <p className="text-xs font-bold text-purple-700 mb-1">{t.name}</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{t.description}</p>
                  </div>
                );
              })()}
            </div>

            {projectIngest.ingesting && selectedSubTool?.id !== 'user-research-synthesis' && (
              <div className="mb-4 flex items-center gap-2 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                Reading interviews in the background
                {projectIngest.snapshot.progressDetail?.filename
                  ? ` — ${projectIngest.snapshot.progressDetail.current} of ${projectIngest.snapshot.progressDetail.total} · ${projectIngest.snapshot.progressDetail.filename}`
                  : projectIngest.snapshot.progress ? ` — ${projectIngest.snapshot.progress}%` : ''}
                . Ingest keeps running if you leave this tool.
              </div>
            )}

            {selectedSubTool?.id === 'synthesis-visualizations' ? (
              /* Synthesis Visualizations is a full 10-tab dashboard, not a prompt→PPTX flow —
                 render it in place of the prompt/upload/run controls. */
              <SynthesisVisualizations projectId={currentProject?.id != null ? String(currentProject.id) : undefined} />
            ) : (
            <>
            {/* Prompt — not rendered at all for a tool that never reads one. The line that
                replaces it says where the report's material comes from, so the missing field
                reads as deliberate rather than as something that failed to load. */}
            {promptRequirement === 'unknown' ? (
            // Same vertical space the decided states occupy, so the panel does not jump once
            // the answer arrives.
            <div className="mb-4">
              <div className="h-5 w-32 rounded bg-gray-100 animate-pulse" />
              <div className="mt-1 h-[122px] w-full rounded-xl border border-gray-200 bg-gray-50 animate-pulse" />
            </div>
            ) : promptOptionalForTool ? (
            <div className="mb-4 text-sm text-gray-500">
              This tool reads the interviews already ingested in this project. Add transcripts
              below — new files slot into the existing taxonomy; finished interviews are not re-read.
            </div>
            ) : (
            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Your Prompt<span className="text-red-500"> *</span>
              </label>
              <textarea
                value={synthesisPrompt}
                onChange={e => setSynthesisPrompt(e.target.value)}
                placeholder={selectedSubTool?.placeholder || 'Select a strategic tool above, then describe what you want to analyze...'}
                rows={5}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            )}

            {selectedSubTool?.id === 'user-research-synthesis' ? (
              <UrsWorkspace
                projectId={currentProject?.id}
                ingest={projectIngest}
                corpus={ursCorpus}
                selectedSourceIds={selectedSourceIds}
                onToggleSource={(id) => setSelectedSourceIds((prev) => (
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                ))}
                onSelectAllSources={setSelectedSourceIds}
                onRemoveIssue={handleRemoveInterview}
                removingId={removingInterviewId}
                onIngestSettled={onIngestSettled}
              />
            ) : (
            <>
            {selectedSubTool && AUTO_ATTACH_TOOL_IDS.has(selectedSubTool.id) && ursCorpus.usable.length > 0 && !skipInterviewContext && (
              <div className="mb-4 flex items-center gap-2 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                Using {ursCorpus.usable.length} ingested interview{ursCorpus.usable.length === 1 ? '' : 's'} — this run stays fast because they are already stored.
                <button type="button" onClick={() => setSkipInterviewContext(true)} className="ml-auto text-purple-500 hover:text-red-600" title="Omit interview findings for this run">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            {selectedSubTool && AUTO_ATTACH_TOOL_IDS.has(selectedSubTool.id) && skipInterviewContext && (
              <button type="button" onClick={() => setSkipInterviewContext(false)} className="mb-3 text-xs text-purple-700 hover:text-purple-900">
                Use ingested interviews for this run
              </button>
            )}
            {/* File upload */}
            <div className="mb-5">
              <input
                ref={synthesisFileInputRef}
                type="file"
                multiple
                accept=".pdf,.txt,.doc,.docx,.csv,.xlsx,.xls,.json"
                className="hidden"
                onChange={e => {
                  setSynthesisFiles(prev => [...prev, ...Array.from(e.target.files || [])]);
                  e.target.value = '';
                }}
              />
              <div
                onClick={() => synthesisFileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  setSynthesisFiles(prev => [...prev, ...Array.from(e.dataTransfer.files || [])]);
                }}
                className="border-2 border-dashed border-gray-200 rounded-xl px-5 py-4 cursor-pointer hover:border-purple-300 hover:bg-purple-50 transition-all"
              >
                <div className="flex flex-col items-center justify-center gap-2">
                  <Upload className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <p className="text-sm text-gray-400">
                    {synthesisFiles.length === 0 ? 'Drop files or click to upload' : `Add more files (${synthesisFiles.length} uploaded)`}
                  </p>
                </div>
              </div>
              {synthesisFiles.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {synthesisFiles.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 border border-purple-200 rounded-full text-xs text-purple-700 font-medium">
                      <FileText className="w-3 h-3" />{f.name}
                      <button onClick={e => { e.stopPropagation(); setSynthesisFiles(prev => prev.filter((_, idx) => idx !== i)); }} className="ml-0.5 text-purple-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
              {synthesisFiles.length > 0 && (
                <button onClick={() => setSynthesisFiles([])} className="mt-1 text-xs text-red-400 hover:text-red-600">Clear all</button>
              )}
            </div>
            </>
            )}

            {/* Run button */}
            <div className="flex items-center justify-center">
              <button
                onClick={handleRunStrategySynthesis}
                disabled={runningStrategySynthesis || !selectedSubTool || promptRequirement === 'unknown' || (!promptOptionalForTool && !synthesisPrompt.trim()) || (selectedSubTool?.id === 'user-research-synthesis' && !projectIngest.ingesting && ursCorpus.usable.length === 0)}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                  runningStrategySynthesis || !selectedSubTool || promptRequirement === 'unknown' || (!promptOptionalForTool && !synthesisPrompt.trim()) || (selectedSubTool?.id === 'user-research-synthesis' && !projectIngest.ingesting && ursCorpus.usable.length === 0)
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-700 hover:bg-purple-800 text-white shadow-sm'
                }`}
              >
                {runningStrategySynthesis
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Running {selectedSubTool?.name}...</>
                  : <><Sparkles className="w-4 h-4" /> {selectedSubTool ? `Run ${selectedSubTool.name}` : 'Select a tool to run'}</>
                }
              </button>
            </div>

            {/* Ingestion takes minutes per transcript. Without this the button reads
                "Running…" for eight minutes and looks hung — so say which stage it is on
                and that the wait is expected (M5, Asana 1216725280641177). */}
            {synthesisIngestStatus && (
              <div className="mt-3 flex items-center justify-center gap-2 text-xs text-purple-700">
                <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
                <span>{synthesisIngestStatus}</span>
              </div>
            )}

            {/* Export availability hint — the PowerPoint export appears with the
                result once a tool has been run, not before. */}
            {!strategySynthesisResult && (
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-400">
                <Download className="w-3.5 h-3.5" />
                Run a tool to generate the synthesis — you can then export it as PowerPoint.
              </p>
            )}
            </>
            )}
          </>
        )}
        </div>
      </div>

      {/* ── Results ─────────────────────────────────────────────────────── */}
      <div ref={resultsRef}>
        {result && verificationMetadata && (
          <div className="mb-4 flex items-center justify-end gap-2">
            <VerificationBadge
              metadata={verificationMetadata}
              size="md"
              onClick={() => setShowVerificationPanel(!showVerificationPanel)}
            />
          </div>
        )}
        {showVerificationPanel && verificationMetadata && (
          <div className="mb-4">
            <VerificationPanel metadata={verificationMetadata} embedded onClose={() => setShowVerificationPanel(false)} />
          </div>
        )}
        {/* excludedDocNames comes from result (a run-time snapshot, or restored from
            input_data on session load) rather than live uploadedDocs — uploadedDocs can
            change between when a run completes and when this re-renders (session switch,
            "Sync from server", new upload), which previously made this banner silently
            disappear for a run that genuinely excluded files. */}
        {result && activeMode === 'council' && (
          <CouncilResults result={result} availableModels={availableModels} sessionId={activeSessionId} excludedDocNames={result.excludedDocNames || []} />
        )}

        {strategySynthesisResult && activeMode === 'strategy-synthesis' && (
          <StrategySynthesisResultCard result={strategySynthesisResult} projectId={currentProject?.id} />
        )}

        {/* ── Implementation Review — optional deeper pass, offered after a council run ── */}
        {(result?.presidentEvaluation?.finalResponse) && !implementationResult && !showImplementationReview && (
          <div className="mt-6 flex items-center justify-between gap-4 bg-gray-50 border border-gray-200 rounded-xl px-5 py-3">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Wrench className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span>Optional: run an Implementation Review to pressure-test this into a phased plan.</span>
            </div>
            <button
              onClick={() => setShowImplementationReview(true)}
              className="flex-shrink-0 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-all"
            >
              Set up review
            </button>
          </div>
        )}

        {(result?.presidentEvaluation?.finalResponse) && (showImplementationReview || implementationResult) && (
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-indigo-200 overflow-hidden">

            {/* Header */}
            <div className="px-6 py-4 border-b border-indigo-100 flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <div className="p-1.5 bg-indigo-100 rounded-lg">
                    <Wrench className="w-4 h-4 text-indigo-700" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">Implementation Review</h2>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">Optional</span>
                </div>
                <p className="text-xs text-gray-500 ml-9">
                  Implementation specialists evaluate the council's recommendation — each from their professional lens — and produce an updated integrated plan. Skip this for flexible, non-roadmap prompts.
                </p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <button
                  onClick={() => setShowImplementationConfig(p => !p)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-700 transition-all"
                >
                  <Settings className="w-3.5 h-3.5" />
                  {showImplementationConfig ? 'Hide Config' : 'Configure Specialists'}
                </button>
                {!implementationResult && (
                  <button
                    onClick={() => { setShowImplementationReview(false); setShowImplementationConfig(false); }}
                    className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Hide implementation review"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Collapsible specialist config */}
            {showImplementationConfig && (
              <div className="px-6 py-5 bg-indigo-50 border-b border-indigo-100">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-semibold text-gray-600">
                    Implementation Specialists ({implementationPersonas.length}, max 6)
                  </span>
                  <button onClick={resetImplementationPersonas} className="text-xs text-gray-400 hover:text-indigo-600 transition-colors">
                    Reset to defaults
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {implementationPersonas.map(persona => (
                    <div key={persona.id} className="relative bg-white border border-gray-200 rounded-lg p-3">
                      <button
                        onClick={() => removeImplementationPersona(persona.id)}
                        className="absolute top-2 right-2 p-1 text-gray-300 hover:text-red-400 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="flex items-center gap-2 mb-2 pr-6">
                        <input
                          type="text"
                          value={persona.emoji}
                          onChange={e => updateImplementationPersona(persona.id, 'emoji', e.target.value)}
                          className="w-10 text-center border border-gray-200 rounded px-1 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          maxLength={2}
                        />
                        <input
                          type="text"
                          value={persona.name}
                          onChange={e => updateImplementationPersona(persona.id, 'name', e.target.value)}
                          className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          placeholder="Specialist name"
                        />
                      </div>
                      <textarea
                        ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                        value={persona.description}
                        onChange={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; updateImplementationPersona(persona.id, 'description', e.target.value); }}
                        className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none bg-white overflow-hidden"
                        placeholder="Describe this specialist's implementation lens..."
                      />
                    </div>
                  ))}
                </div>

                {implementationPersonas.length < 6 && (
                  <button
                    onClick={addImplementationPersona}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-indigo-600 hover:text-indigo-800 font-medium border border-indigo-200 hover:bg-indigo-100 transition-all"
                  >
                    <PlusCircle className="w-3.5 h-3.5" /> Add Specialist
                  </button>
                )}

                {/* Standard / Premium toggle — controls the model used for Step 3 specialists and Step 4 final synthesis. */}
                <div className="mt-4">
                  <div className="text-xs font-medium text-gray-600 mb-2">Review quality</div>
                  <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
                    <button
                      type="button"
                      onClick={() => setImplementationModel('claude-sonnet-4-6')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                        implementationModel !== 'claude-opus-4-6'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Standard (Sonnet)
                    </button>
                    <button
                      type="button"
                      onClick={() => setImplementationModel('claude-opus-4-6')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                        implementationModel === 'claude-opus-4-6'
                          ? 'bg-amber-500 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Premium (Opus)
                    </button>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5">
                    {implementationModel === 'claude-opus-4-6' ? (
                      <>Opus 4.6 — ~5× the cost of Sonnet for marginal quality gain. Best for high-stakes reviews.</>
                    ) : (
                      <>Sonnet 4.6 — recommended for nearly all reviews. Switch to Premium only when you need Opus-grade reasoning.</>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Run button */}
            <div className="px-6 py-4">
              <div className="flex items-start gap-2 text-xs text-gray-500 mb-4">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-indigo-400" />
                <span>
                  Each specialist evaluates the council's recommended path from their lens, then collaboratively produces an updated integrated implementation plan.
                </span>
              </div>
              <button
                onClick={handleRunImplementationReview}
                disabled={runningImplementation || implementationPersonas.length === 0}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm transition-all ${
                  runningImplementation || implementationPersonas.length === 0
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-indigo-700 hover:bg-indigo-800 text-white shadow-sm'
                }`}
              >
                {runningImplementation ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Running Implementation Review...</>
                ) : (
                  <><Wrench className="w-4 h-4" /> Run Implementation Review</>
                )}
              </button>
              {runningImplementation && (
                <p className="text-xs text-amber-600 font-medium mt-2 flex items-center gap-1">
                  ⏳ Each specialist is being analyzed in depth — this may take 7–10 minutes. Please keep this tab open.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Implementation Review Results */}
        {implementationResult && (
          <div ref={implementationRef} className="mt-6">
            <ImplementationReviewResults result={implementationResult} availableModels={availableModels} originalPrompt={pipelinePrompt || prompt} />
          </div>
        )}

        {/* Sync from server — top-right, always visible when a pipeline session is open */}
        {(activeSessionId || pipelineSessionId) && !pipelineRunning && (
          <div className="flex justify-end mt-4">
            <button
              onClick={() => loadSessionById((activeSessionId || pipelineSessionId)!, true, true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-500 hover:border-blue-300 hover:text-blue-600 transition-all bg-white"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Sync from server
            </button>
          </div>
        )}

        {/* ── Full Pipeline Results (step-by-step progressive display) ─────── */}
        {(pipelineRunning || pipelineStep > 0) && (
          <div ref={pipelineRef} className="mt-6 space-y-4">

            {/* Background processing banner — shown when network dropped but backend still running */}
            {pipelineBackgroundProcessing && (
              <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <Loader2 className="w-4 h-4 text-amber-500 animate-spin flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Processing in background</p>
                  <p className="text-xs text-amber-600">Step {pipelineStep} is still running on the server. Results will appear automatically within 30 seconds — no need to refresh.</p>
                </div>
              </div>
            )}

            {/* Step 1 Results — Council Members */}
            {pipelineData.memberResults && (
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div
                  className="flex items-center gap-2 px-5 py-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-gray-100 cursor-pointer select-none"
                  onClick={() => toggleStepCollapse(1)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${pipelineData.step1Failed ? 'bg-red-500' : 'bg-green-500'}`}>
                    {pipelineData.step1Failed
                      ? <AlertCircle className="w-3.5 h-3.5 text-white" />
                      : <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm font-semibold text-gray-800">Step 1 — AI Council Analysis</span>
                  <span className={`text-xs ${pipelineData.step1Failed ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{pipelineData.memberResults.filter(r => r.status === 'success').length}/{pipelineData.memberResults.length} models responded</span>
                  <div className="ml-auto flex items-center gap-2">
                    {pipelineData.step1Failed && (
                      <button
                        onClick={e => { e.stopPropagation(); setPipelineData(prev => ({ ...prev, step1Failed: false })); handleRunPipelineStep1(); }}
                        disabled={pipelineRunning}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-all disabled:opacity-50"
                      >
                        {pipelineRunning && pipelineStep === 1 ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Retry
                      </button>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${collapsedSteps.has(1) ? '' : 'rotate-180'}`} />
                  </div>
                </div>
                {!collapsedSteps.has(1) && (
                  <div className="p-5">
                    {/* Persistent reminder of documents excluded due to failed extraction — pipeline
                        mode's equivalent of the standard-council banner in CouncilResults. Reads the
                        run-time snapshot (pipelineExcludedDocNames), not live uploadedDocs, so it
                        survives session switch / reload / new uploads. */}
                    {pipelineExcludedDocNames.length > 0 && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">
                          <span className="font-semibold">This analysis excludes {pipelineExcludedDocNames.length} document{pipelineExcludedDocNames.length === 1 ? '' : 's'} that failed to read:</span>{' '}
                          {pipelineExcludedDocNames.join(', ')}. Any references below to these documents' contents were not verified against the actual source.
                        </p>
                      </div>
                    )}
                    {webSources.length > 0 && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                        <div className="flex items-center gap-2 mb-2">
                          <Globe className="w-3.5 h-3.5 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">Web Sources</span>
                        </div>
                        <div className="flex flex-col gap-1">
                          {webSources.map(s => (
                            <a key={s.index} href={s.url} target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-2 text-xs text-blue-600 hover:text-blue-800 hover:underline">
                              <span className="font-bold text-blue-400 w-5">[{s.index}]</span>
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              {s.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    <MemberResponseCards
                      memberResults={pipelineData.memberResults}
                      nameMap={Object.fromEntries(availableModels.map(m => [m.id, m.name]))}
                      onRetry={async (modelId) => {
                        try {
                          const res = await api.post('/api/ai-council/steps/retry-member', {
                            prompt: pipelinePrompt,
                            inputData: buildCombinedData(),
                            modelId,
                            project_id: currentProject?.id || null,
                          }, { timeout: 600000 }); // 10 min — matches server-side AI route timeout
                          const retryData = unwrapStepData<{ result: MemberResult }>(
                            res, `Retry failed for ${modelId} — please try again.`
                          );
                          const retried: MemberResult = retryData.result;
                          // Compute the merged array from the functional updater's `prev`
                          // (not the outer closure's pipelineData) so two retries fired in
                          // quick succession each persist against the latest state instead
                          // of racing to overwrite each other with a stale snapshot.
                          let updatedMemberResults: MemberResult[] = [];
                          setPipelineData(prev => {
                            updatedMemberResults = prev.memberResults!.map(r => r.modelId === modelId ? retried : r);
                            return { ...prev, memberResults: updatedMemberResults };
                          });
                          // Persist the retried result so it survives a reload/reopen —
                          // without this, the retry only lived in local component state and
                          // a fresh load re-fetched the original (failed) member result.
                          const sessionId = pipelineSessionId || activeSessionId;
                          if (sessionId) {
                            api.patch(`/api/ai-council/sessions/${sessionId}`, {
                              member_results: updatedMemberResults,
                            }).catch(e => console.error('[AICouncil] Failed to persist retry result:', e));
                          }
                          if (retried.status === 'success') toast.success(`${modelId} responded successfully`);
                          else toast.error(`${modelId} failed again — ${retried.error}`);
                        } catch (err: any) {
                          toast.error(getApiError(err));
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Step 2 Block — appears after Step 1 completes */}
            {pipelineData.memberResults && (
              <div className="bg-white border border-amber-200 rounded-xl shadow-sm overflow-hidden">
                <div
                  className="flex items-center gap-2 px-5 py-4 bg-gradient-to-r from-amber-50 to-yellow-50 border-b border-amber-100 cursor-pointer select-none"
                  onClick={() => pipelineData.presidentEvaluation && toggleStepCollapse(2)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${pipelineData.presidentEvaluation ? 'bg-green-500' : 'bg-amber-400'}`}>
                    {pipelineData.presidentEvaluation
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      : <Crown className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm font-semibold text-gray-800">Step 2 — Council President · First Synthesis</span>
                  <div className="ml-auto flex items-center gap-2">
                    {pipelineData.step2Failed && !pipelineData.presidentEvaluation && (
                      <button
                        onClick={e => { e.stopPropagation(); setPipelineData(prev => ({ ...prev, step2Failed: false })); handleRunPipelineStep2(); }}
                        disabled={pipelineRunning}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-all disabled:opacity-50"
                      >
                        {pipelineRunning && pipelineStep === 2 ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Retry
                      </button>
                    )}
                    {pipelineData.presidentEvaluation && (
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${collapsedSteps.has(2) ? '' : 'rotate-180'}`} />
                    )}
                  </div>
                </div>

                {!collapsedSteps.has(2) && (
                <>
                {/* Description — only before results load */}
                {!pipelineData.presidentEvaluation && <div className="px-5 pt-5 pb-2 flex flex-col items-center text-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-600" />
                  </div>
                  <p className="text-sm font-semibold text-gray-800">Council President: {presidentModelName}</p>
                  <p className="text-xs text-gray-500 max-w-lg">Evaluates all member responses and synthesizes the final answer — identifies agreements, resolves contradictions, and produces a unified strategic view.</p>
                </div>}

                {/* Run button — only when no result yet */}
                {!pipelineData.presidentEvaluation && (
                <div className="flex justify-center px-5 py-4">
                  <button
                    onClick={handleRunPipelineStep2}
                    disabled={pipelineRunning}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all bg-amber-600 text-white disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    {pipelineRunning && pipelineStep === 2
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Council President is synthesizing all responses...</>
                      : <><Crown className="w-3.5 h-3.5" /> Run Step 2 — First Synthesis <span className="opacity-75 font-normal">(~${councilCostBreakdown.step2.toFixed(2)})</span></>}
                  </button>
                </div>
                )}

                {/* Results — same UI as existing Presidential Evaluation */}
                {pipelineData.presidentEvaluation && (
                  <div className="px-5 pt-4 pb-5">
                    {uploadedDocs.some(d => d.extractionFailed) && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">
                          <span className="font-semibold">This analysis excludes {uploadedDocs.filter(d => d.extractionFailed).length} document{uploadedDocs.filter(d => d.extractionFailed).length === 1 ? '' : 's'} that failed to read:</span>{' '}
                          {uploadedDocs.filter(d => d.extractionFailed).map(d => d.name).join(', ')}. Any references below to these documents' contents were not verified against the actual source.
                        </p>
                      </div>
                    )}
                    <PresidentialEvaluation
                      evaluation={pipelineData.presidentEvaluation}
                      presidentModel={presidentModel}
                      sessionId={null}
                      hideHeader
                    />
                  </div>
                )}
                </>
                )}
              </div>
            )}

            {/* Step 3 offer — a non-roadmap prompt should not be handed a full pressure-testing
                card as its expected next step. Until the user opts in, this is a one-line prompt
                they can dismiss. Once Step 3 has run, the block below always renders its results. */}
            {pipelineData.presidentEvaluation && !pipelineData.personaResults && !showImplementationReview && !pressureTestDismissed && (
              <div className="flex items-center justify-between gap-4 bg-gray-50 border border-gray-200 rounded-xl px-5 py-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Wrench className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span>Optional: run an Implementation Review to pressure-test this into a phased plan.</span>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  <button
                    onClick={() => setShowImplementationReview(true)}
                    className="text-xs font-medium text-indigo-700 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-all"
                  >
                    Set up review
                  </button>
                  <button
                    onClick={() => setPressureTestDismissed(true)}
                    title="Dismiss — you can still reach this from Rerun"
                    className="p-1 text-gray-400 hover:text-gray-700 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3 Block — appears once the user opts in, or once it has results */}
            {pipelineData.presidentEvaluation && (showImplementationReview || pipelineData.personaResults) && (
              <div className="bg-white border border-indigo-200 rounded-xl shadow-sm overflow-hidden">
                <div
                  className="flex items-center gap-2 px-5 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 cursor-pointer select-none"
                  onClick={() => pipelineData.personaResults && toggleStepCollapse(3)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${pipelineData.step3Failed ? 'bg-red-500' : pipelineData.personaResults ? 'bg-green-500' : 'bg-indigo-500'}`}>
                    {pipelineData.step3Failed
                      ? <AlertCircle className="w-3.5 h-3.5 text-white" />
                      : pipelineData.personaResults
                        ? <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        : <Users className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm font-semibold text-gray-800">Step 3 — Implementation Council · Pressure Testing</span>
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full font-medium">Optional</span>
                  {pipelineData.personaResults
                    ? <>
                        <span className={`text-xs ${pipelineData.step3Failed ? 'text-red-600 font-medium' : 'text-gray-500'}`}>{pipelineData.personaResults.length} specialists</span>
                        <div className="ml-auto flex items-center gap-2">
                          {pipelineData.step3Failed && (
                            <button
                              onClick={e => { e.stopPropagation(); setPipelineData(prev => ({ ...prev, step3Failed: false })); handleRunPipelineStep3(); }}
                              disabled={pipelineRunning}
                              className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-all disabled:opacity-50"
                            >
                              {pipelineRunning && pipelineStep === 3 ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                              Retry
                            </button>
                          )}
                          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${collapsedSteps.has(3) ? '' : 'rotate-180'}`} />
                        </div>
                      </>
                    : (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowImplementationReview(false); setShowImplementationConfig(false); }}
                        title="Close — this step is optional"
                        className="ml-auto p-1 text-gray-400 hover:text-gray-700 transition-all"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                </div>

                {!collapsedSteps.has(3) && (
                <>
                {/* Description + Configure toggle — hidden after results load */}
                {!pipelineData.personaResults && (
                  <div className="px-5 pt-5 pb-2 flex flex-col items-center text-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800">6 Specialist Personas — Claude Sonnet 4.6</p>
                    <p className="text-xs text-gray-500 max-w-lg">Each specialist attacks the plan from a distinct lens: Tech & Data Architecture, Risk & Cybersecurity, People & Change Mgmt, Regulatory & Compliance, Process & Implementation, Devil's Advocate.</p>
                    <button
                      onClick={() => setShowImplementationConfig(p => !p)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-700 transition-all"
                    >
                      <Settings className="w-3.5 h-3.5" />
                      {showImplementationConfig ? 'Hide Config' : 'Configure Specialists'}
                    </button>
                  </div>
                )}

                {/* Collapsible config — reuses existing state + handlers */}
                {showImplementationConfig && !pipelineData.personaResults && (
                  <div className="mx-5 mb-3 p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-semibold text-gray-600">Implementation Specialists ({implementationPersonas.length}, max 6)</span>
                      <button onClick={resetImplementationPersonas} className="text-xs text-gray-400 hover:text-indigo-600 transition-colors">Reset to defaults</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      {implementationPersonas.map(persona => (
                        <div key={persona.id} className="relative bg-white border border-gray-200 rounded-lg p-3">
                          <button onClick={() => removeImplementationPersona(persona.id)} className="absolute top-2 right-2 p-1 text-gray-300 hover:text-red-400 transition-colors">
                            <X className="w-3.5 h-3.5" />
                          </button>
                          <div className="flex items-center gap-2 mb-2 pr-6">
                            <input type="text" value={persona.emoji} onChange={e => updateImplementationPersona(persona.id, 'emoji', e.target.value)}
                              className="w-10 text-center border border-gray-200 rounded px-1 py-1 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400" maxLength={2} />
                            <input type="text" value={persona.name} onChange={e => updateImplementationPersona(persona.id, 'name', e.target.value)}
                              className="flex-1 border border-gray-200 rounded-lg px-2 py-1 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-indigo-400" placeholder="Specialist name" />
                          </div>
                          <textarea
                            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            value={persona.description}
                            onChange={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; updateImplementationPersona(persona.id, 'description', e.target.value); }}
                            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-indigo-400 resize-none bg-white overflow-hidden"
                            placeholder="Describe this specialist's implementation lens..."
                          />
                        </div>
                      ))}
                    </div>
                    {implementationPersonas.length < 6 && (
                      <button onClick={addImplementationPersona} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-indigo-600 hover:text-indigo-800 font-medium border border-indigo-200 hover:bg-indigo-100 transition-all">
                        <PlusCircle className="w-3.5 h-3.5" /> Add Specialist
                      </button>
                    )}
                    <div className="mt-3">
                      <div className="text-xs font-medium text-gray-600 mb-2">Review quality</div>
                      <div className="inline-flex rounded-lg border border-gray-300 bg-white p-0.5">
                        <button
                          type="button"
                          onClick={() => setImplementationModel('claude-sonnet-4-6')}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                            implementationModel !== 'claude-opus-4-6'
                              ? 'bg-indigo-600 text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          Standard (Sonnet)
                        </button>
                        <button
                          type="button"
                          onClick={() => setImplementationModel('claude-opus-4-6')}
                          className={`px-3 py-1.5 text-sm font-medium rounded-md transition ${
                            implementationModel === 'claude-opus-4-6'
                              ? 'bg-amber-500 text-white shadow-sm'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          Premium (Opus)
                        </button>
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1.5">
                        {implementationModel === 'claude-opus-4-6'
                          ? 'Opus 4.6 — ~5× cost of Sonnet. Use only for high-stakes reviews.'
                          : 'Sonnet 4.6 — recommended for most reviews.'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Run button */}
                {!pipelineData.personaResults && (
                  <div className="flex flex-col items-center gap-3 px-5 py-4">
                    <button
                      onClick={handleRunPipelineStep3}
                      disabled={pipelineRunning}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all bg-indigo-600 text-white disabled:cursor-not-allowed disabled:opacity-80"
                    >
                      {pipelineRunning && pipelineStep === 3
                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> 6 specialists are pressure-testing the plan...</>
                        : <><Users className="w-3.5 h-3.5" /> Run Step 3 — Pressure Testing <span className="opacity-75 font-normal">(~${councilCostBreakdown.step3.toFixed(2)})</span></>}
                    </button>
                    {pipelineRunning && pipelineStep === 3 && (
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-300 rounded-lg">
                        <span className="text-lg">⏳</span>
                        <div>
                          <p className="text-sm font-semibold text-amber-800">Each specialist is performing a deep analysis — this may take 5–10 minutes.</p>
                          <p className="text-xs text-amber-600 mt-0.5">Please keep this page open and do not refresh.</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Results — same ImplementationPersonaCard UI as before */}
                {pipelineData.personaResults && (
                  <div className="px-5 pt-4 pb-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {pipelineData.personaResults.map((r, i) => (
                        <ImplementationPersonaCard key={i} result={r} />
                      ))}
                    </div>
                  </div>
                )}
                </>
                )}
              </div>
            )}

            {/* Step 4 Block — appears after Step 3 completes */}
            {(pipelineData.personaResults || pipelineData.finalSynthesis) && (
              <div className="bg-white border border-green-200 rounded-xl shadow-sm overflow-hidden">
                <div
                  className="flex items-center gap-2 px-5 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100 cursor-pointer select-none"
                  onClick={() => pipelineData.finalSynthesis && toggleStepCollapse(4)}
                >
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${pipelineData.finalSynthesis ? 'bg-green-500' : 'bg-green-400'}`}>
                    {pipelineData.finalSynthesis
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                      : <Crown className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <span className="text-sm font-semibold text-gray-800">Step 4 — Council President · Final Synthesis</span>
                  <div className="ml-auto flex items-center gap-2">
                    {pipelineData.step4Failed && (
                      <button
                        onClick={e => { e.stopPropagation(); setPipelineData(prev => ({ ...prev, step4Failed: false })); handleRunPipelineStep4(); }}
                        disabled={pipelineRunning}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 hover:bg-amber-200 transition-all disabled:opacity-50"
                      >
                        {pipelineRunning && pipelineStep === 4 ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        Retry
                      </button>
                    )}
                    {pipelineData.finalSynthesis && (
                      <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${collapsedSteps.has(4) ? '' : 'rotate-180'}`} />
                    )}
                  </div>
                </div>

                {!collapsedSteps.has(4) && (
                <>
                {/* Description — hidden after results load */}
                {!pipelineData.finalSynthesis && (
                  <div className="px-5 pt-5 pb-2 flex flex-col items-center text-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-green-700" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800">
                      Final Synthesis Engine — {implementationModel === 'claude-opus-4-6' ? 'Claude Opus 4.6 (Premium)' : 'Claude Sonnet 4.6 (Standard)'}
                    </p>
                    <p className="text-xs text-gray-500 max-w-lg">Classifies all specialist feedback into: Confirmed Strengths, Design Constraints, Required Modifications, Open Questions, Contradiction Resolution, Change Log, and Decision Register.</p>
                  </div>
                )}

                {/* Run button — only when no result yet */}
                {!pipelineData.finalSynthesis && (
                <div className="flex justify-center px-5 py-4">
                  <button
                    onClick={handleRunPipelineStep4}
                    disabled={pipelineRunning}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all bg-green-700 text-white disabled:cursor-not-allowed disabled:opacity-80"
                  >
                    {pipelineRunning && pipelineStep === 4
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Council President is building the final synthesis...</>
                      : <><Sparkles className="w-3.5 h-3.5" /> Run Step 4 — Final Synthesis <span className="opacity-75 font-normal">(~${councilCostBreakdown.step4.toFixed(2)})</span></>}
                  </button>
                </div>
                )}

                {/* Results — Final Synthesis sections only (no specialist cards, no exports) */}
                {pipelineData.finalSynthesis && (
                  <div className="px-5 pt-4 pb-5">
                    {/* Persistent reminder of which uploaded documents were excluded from
                        this analysis due to failed extraction — stays visible on the
                        output itself, not just as a one-time pre-run modal, so it travels
                        with the result if screenshotted/exported/shown to a client. */}
                    {uploadedDocs.some(d => d.extractionFailed) && (
                      <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-red-700">
                          <span className="font-semibold">This analysis excludes {uploadedDocs.filter(d => d.extractionFailed).length} document{uploadedDocs.filter(d => d.extractionFailed).length === 1 ? '' : 's'} that failed to read:</span>{' '}
                          {uploadedDocs.filter(d => d.extractionFailed).map(d => d.name).join(', ')}. Any references below to these documents' contents were not verified against the actual source.
                        </p>
                      </div>
                    )}
                    <FinalSynthesisSections finalSynthesis={pipelineData.finalSynthesis} />
                  </div>
                )}
                </>
                )}
              </div>
            )}

            {/* Step 5 Block — Deliverables — appears after Step 4 completes */}
            {pipelineData.finalSynthesis && (
              <div className="bg-white border border-violet-200 rounded-xl shadow-sm overflow-hidden">
                <div
                  className="flex items-center gap-2 px-5 py-4 bg-gradient-to-r from-violet-50 to-purple-50 border-b border-violet-100 cursor-pointer select-none"
                  onClick={() => toggleStepCollapse(5)}
                >
                  <div className="w-6 h-6 rounded-full flex items-center justify-center bg-violet-500">
                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                  </div>
                  <Download className="w-4 h-4 text-violet-700" />
                  <span className="text-sm font-semibold text-gray-800">Step 5 — Deliverables · Layered Output</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Pipeline Complete</span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 ml-auto transition-transform ${collapsedSteps.has(5) ? '' : 'rotate-180'}`} />
                </div>

                {!collapsedSteps.has(5) && (
                <>
                <div className="px-5 py-3 bg-violet-50 border-b border-violet-100 flex items-center gap-3 flex-wrap">
                  <p className="text-xs text-gray-600 flex-1">One plan, three audience-appropriate renderings + supporting documents. Download any or all of the outputs below.</p>
                  {brandingList.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 whitespace-nowrap">Branding:</span>
                      <select
                        value={selectedBrandingTagId ?? ''}
                        onChange={e => setSelectedBrandingTagId(e.target.value ? Number(e.target.value) : null)}
                        className="text-xs border border-violet-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-violet-400 max-w-[180px]"
                      >
                        <option value="">No branding</option>
                        {brandingList.map(b => (
                          <option key={b.tagId} value={b.tagId}>
                            {b.companyName || b.tagName}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>

                <div className="px-5 py-5 space-y-3">
                  <ExecutivePresentationButton
                    finalSynthesis={pipelineData.finalSynthesis}
                    councilOutput={pipelineData.councilOutput}
                    originalPrompt={pipelinePrompt}
                    brandingTagId={selectedBrandingTagId}
                    sessionId={pipelineSessionId}
                    savedStructure={presentationStructure}
                    refinementMessages={chatMessages}
                    onStructureSaved={persistPresentationStructure}
                  />
                  <VPWordButton
                    finalSynthesis={pipelineData.finalSynthesis}
                    councilOutput={pipelineData.councilOutput}
                    originalPrompt={pipelinePrompt}
                  />
                  <ImplementationWordButton
                    finalSynthesis={pipelineData.finalSynthesis}
                    councilOutput={pipelineData.councilOutput}
                    originalPrompt={pipelinePrompt}
                  />
                  <ImplementationExcelButton
                    finalSynthesis={pipelineData.finalSynthesis}
                    councilOutput={pipelineData.councilOutput}
                    originalPrompt={pipelinePrompt}
                  />
                </div>
                </>
                )}
              </div>
            )}

          </div>
        )}

      </div>
    </div>

      {/* ── Floating Refine & Chat button ── */}
      {SHOW_REFINE_CHAT && (result?.presidentEvaluation || pipelineData.presidentEvaluation || pipelineData.memberResults) && !chatPanelOpen && (
        <button
          onClick={() => setChatPanelOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 bg-[#4F46E5] text-white rounded-full shadow-lg hover:bg-[#4338CA] transition-all hover:scale-105"
        >
          <MessageSquare className="w-4 h-4" />
          <span className="text-sm font-semibold">Refine & Chat</span>
        </button>
      )}

      {/* ── Slide-out Chat Panel ── */}
      {chatPanelOpen && (
        <>
          <div className="fixed inset-0 bg-black/20 z-[55]" onClick={() => setChatPanelOpen(false)} />
          <div className="fixed top-0 right-0 bottom-0 w-[800px] max-w-[90vw] bg-white shadow-2xl z-[60] flex flex-col border-l border-gray-200 animate-in slide-in-from-right duration-200">
            <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-slate-50 flex-shrink-0">
              <div className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#4F46E5]" />
                  <span className="text-sm font-semibold text-gray-800">Refine & Chat</span>
                </div>
                <div className="flex items-center gap-1">
                  {typeof chatContextMeta?.context_usage_pct === 'number' && (
                    <div
                      title={`Context tokens: ${chatContextMeta.input_tokens?.toLocaleString() ?? '—'}${chatContextMeta.max_context_tokens ? ` / ${chatContextMeta.max_context_tokens.toLocaleString()}` : ''}`}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold tabular-nums flex-shrink-0 border ${
                        chatContextMeta.context_usage_pct >= 65
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : chatContextMeta.context_usage_pct >= 40
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-green-50 text-green-700 border-green-200'
                      }`}
                    >
                      <span className="opacity-70">Context</span>
                      <span>{chatContextMeta.context_usage_pct}%</span>
                    </div>
                  )}
                  {chatMessages.length > 0 && (
                    <button
                      onClick={handleNewChatWithSummary}
                      disabled={chatHandoffLoading}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-indigo-600 text-xs font-medium disabled:opacity-50"
                      title="Start a fresh chat that keeps a compact summary of this thread"
                    >
                      {chatHandoffLoading ? 'Summarizing…' : 'New Chat'}
                    </button>
                  )}
                  {chatMessages.length > 0 && (
                    <button
                      onClick={() => { setChatMessages([]); saveChatHistory([]); setChatContextMeta(null); }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-red-400 text-xs font-medium"
                      title="Clear chat"
                    >
                      Clear Chat
                    </button>
                  )}
                  <button onClick={() => setChatPanelOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 px-5 pb-2 flex-wrap">
                {brandingList.length > 0 && (
                  <>
                    <span className="text-xs text-gray-500">PPTX Branding:</span>
                    <select
                      value={selectedBrandingTagId ?? ''}
                      onChange={e => setSelectedBrandingTagId(e.target.value ? Number(e.target.value) : null)}
                      className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 max-w-[200px]"
                    >
                      <option value="">No branding</option>
                      {brandingList.map(b => (
                        <option key={b.projectId} value={b.projectId}>
                          {b.companyName}{b.projectName ? ` (${b.projectName})` : ''}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <label
                  className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer"
                  title="Design the deck with Claude's PowerPoint skill instead of our standard template — slower and more expensive, but a richer, more original layout."
                >
                  <input
                    type="checkbox"
                    checked={enhancedVisualization}
                    onChange={e => setEnhancedVisualization(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-400"
                  />
                  Enhanced visualization
                </label>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin">
              {chatMessages.length === 0 && (
                <div className="py-8 px-2 space-y-4">
                  <div className="text-center">
                    <MessageSquare className="w-7 h-7 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm font-semibold text-gray-600">Ask follow-up questions</p>
                    <p className="text-xs text-gray-400 mt-1">Refine the analysis, go deeper, or generate a presentation.</p>
                  </div>
                  {/* Suggestion chips */}
                  <div className="flex flex-col gap-2">
                    {[
                      { label: '📋 Summarize key recommendations', prompt: 'Summarize the top 3 recommendations from this council analysis in clear, concise bullet points.' },
                      { label: '⚠️ What are the biggest risks?', prompt: 'What are the biggest risks or concerns flagged by this council analysis? List them by priority.' },
                      { label: '✅ Build a 3-step action plan', prompt: 'Based on the council\'s findings, build a concrete 3-step action plan with clear owners and timelines.' },
                    ].map((chip) => (
                      <button
                        key={chip.label}
                        onClick={() => setChatInput(chip.prompt)}
                        className="text-left px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:border-[#4F46E5] hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors"
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'user' ? (
                    <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed bg-[#4F46E5] text-white">{msg.content}</div>
                  ) : msg.content.startsWith('__EXCEL__') ? (() => {
                    const parts = msg.content.replace('__EXCEL__|||', '').split('|||');
                    const blobUrl = parts[0];
                    const filename = parts[1];
                    return (
                      <div className="w-full rounded-xl px-4 py-3 bg-green-50 border border-green-200 text-gray-700 text-sm flex items-center gap-3">
                        <Download className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-green-800 text-sm">Excel file ready</p>
                          <p className="text-xs text-green-600 truncate">{filename}</p>
                        </div>
                        <button onClick={() => handleFileDownload(blobUrl, filename)} disabled={downloadingUrl === blobUrl} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors flex-shrink-0 disabled:opacity-60">
                          {downloadingUrl === blobUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                        </button>
                      </div>
                    );
                  })() : msg.content.startsWith('__PPTX__') ? (() => {
                    const parts = msg.content.replace('__PPTX__|||', '').split('|||');
                    const blobUrl = parts[0];
                    const filename = parts[1];
                    return (
                      <div className="w-full rounded-xl px-4 py-3 bg-indigo-50 border border-indigo-200 text-gray-700 text-sm flex items-center gap-3">
                        <Presentation className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-indigo-800 text-sm">Presentation ready</p>
                          <p className="text-xs text-indigo-600 truncate">{filename}</p>
                        </div>
                        <button onClick={() => handleFileDownload(blobUrl, filename)} disabled={downloadingUrl === blobUrl} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors flex-shrink-0 disabled:opacity-60">
                          {downloadingUrl === blobUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                        </button>
                      </div>
                    );
                  })() : msg.content.startsWith('__WORD__') ? (() => {
                    const parts = msg.content.replace('__WORD__|||', '').split('|||');
                    const blobUrl = parts[0];
                    const filename = parts[1];
                    return (
                      <div className="w-full rounded-xl px-4 py-3 bg-blue-50 border border-blue-200 text-gray-700 text-sm flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-blue-800 text-sm">Word file ready</p>
                          <p className="text-xs text-blue-600 truncate">{filename}</p>
                        </div>
                        <button onClick={() => handleFileDownload(blobUrl, filename)} disabled={downloadingUrl === blobUrl} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0 disabled:opacity-60">
                          {downloadingUrl === blobUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                        </button>
                      </div>
                    );
                  })() : msg.content.includes('\n\n__EXCEL__|||') ? (() => {
                    const [textPart, excelPart] = msg.content.split('\n\n__EXCEL__|||');
                    const [blobUrl, filename] = excelPart.split('|||');
                    return (
                      <div className="w-full space-y-3">
                        <div className="rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm prose prose-sm max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{textPart}</ReactMarkdown>
                        </div>
                        <div className="w-full rounded-xl px-4 py-3 bg-green-50 border border-green-200 flex items-center gap-3">
                          <Download className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-green-800 text-sm">Excel file ready</p>
                            <p className="text-xs text-green-600 truncate">{filename}</p>
                          </div>
                          <button onClick={() => handleFileDownload(blobUrl, filename)} disabled={downloadingUrl === blobUrl} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors flex-shrink-0 disabled:opacity-60">
                            {downloadingUrl === blobUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} {downloadingUrl === blobUrl ? 'Downloading…' : 'Download'}
                          </button>
                        </div>
                      </div>
                    );
                  })() : msg.content.startsWith('__COVERAGE__|||') ? (() => {
                    let data: any = null;
                    try { data = JSON.parse(msg.content.replace('__COVERAGE__|||', '')); } catch { data = null; }
                    if (!data) return null;
                    const ok = data.score >= 95;
                    const blocked = data.score < 80;
                    const headerColor = ok ? 'green' : blocked ? 'red' : 'amber';
                    const tone = headerColor === 'green'
                      ? 'bg-green-50 border-green-200'
                      : headerColor === 'red' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200';
                    const itemList = Array.isArray(data.items) ? data.items : [];
                    const gaps = itemList.filter((i: any) => i.status !== 'covered');
                    const hasItemDetail = itemList.length > 0;
                    const missingCount = typeof data.missing === 'number' ? data.missing : gaps.length;
                    const hasGaps = hasItemDetail ? gaps.length > 0 : missingCount > 0;
                    const countLabel = Number.isFinite(data.covered) && Number.isFinite(data.total) && data.covered != null && data.total != null
                      ? ` (${data.covered}/${data.total} items)`
                      : '';
                    return (
                      <div className={`w-full rounded-xl px-4 py-3 border ${tone} text-sm space-y-2`}>
                        <div className="flex items-center gap-2 font-semibold text-gray-800">
                          {ok ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
                          Coverage Score: {data.score}%{countLabel}
                        </div>
                        {data.lowConfidence && (
                          <p className="text-xs text-amber-700">This document has an unusual structure. Verify output completeness before sharing.</p>
                        )}
                        {!hasGaps ? (
                          <p className="text-xs text-gray-600">All required sections are present in the deck.</p>
                        ) : hasItemDetail ? (
                          <ul className="space-y-0.5">
                            {gaps.slice(0, 10).map((i: any, gi: number) => (
                              <li key={gi} className="text-xs text-gray-700 flex items-center gap-1.5">
                                <span>{i.status === 'missing' ? '❌' : '⚠️'}</span>{i.label}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-xs text-gray-700">{missingCount} item(s) may be incomplete. Review the deck before sharing.</p>
                        )}
                        {Array.isArray(data.ragMissing) && data.ragMissing.length > 0 && (
                          <p className="text-xs text-amber-700">Status colors possibly missing in output: {data.ragMissing.join(', ')}.</p>
                        )}
                        {hasGaps && !data.fixAttempt && (
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => handleChatGeneratePptx('Create a complete presentation covering all sections', true, true)}
                              disabled={pptxGenerating}
                              className="px-3 py-1.5 bg-orange-500 text-white text-xs font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-60"
                            >
                              {pptxGenerating ? 'Fixing…' : 'Fix Missing Items'}
                            </button>
                            <span className="text-xs text-gray-500">The download above is available regardless.</span>
                          </div>
                        )}
                        {hasGaps && data.fixAttempt && (
                          <p className="text-xs text-gray-500 pt-1">These items appear as structural labels in the source rather than slide content, so they may not warrant a dedicated slide. The deck above is complete and ready.</p>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="w-full rounded-xl px-4 py-3 bg-gray-50 border border-gray-200 text-gray-700 text-sm prose prose-sm max-w-none">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{msg.content}</ReactMarkdown>
                    </div>
                  )}
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                    <span className="text-xs text-gray-400">Thinking...</span>
                  </div>
                </div>
              )}
              {pptxGenerating && (
                <div className="flex justify-start">
                  <div className="bg-indigo-50 border border-indigo-200 rounded-2xl px-4 py-3 w-full max-w-sm space-y-1.5">
                    <p className="text-xs text-orange-700 font-semibold mb-1">Generating your presentation…</p>
                    {PPTX_PHASE_LABELS.map((label, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {i < pptxPhaseStep ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                        ) : i === pptxPhaseStep ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-500 flex-shrink-0" />
                        ) : (
                          <span className="w-3.5 h-3.5 flex-shrink-0 rounded-full border border-gray-300" />
                        )}
                        <span className={i <= pptxPhaseStep ? 'text-gray-700' : 'text-gray-400'}>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {excelGenerating && (
                <div className="flex justify-start">
                  <div className="bg-green-50 border border-green-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-green-500" />
                    <span className="text-xs text-green-600 font-medium">Generating Excel file…</span>
                  </div>
                </div>
              )}
              {wordGenerating && (
                <div className="flex justify-start">
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                    <span className="text-xs text-blue-600 font-medium">Generating Word document…</span>
                  </div>
                </div>
              )}
              {/* Chat web sources */}
              {chatWebSources.length > 0 && !chatLoading && (
                <div className="mx-2 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Globe className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700">Web Sources</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {chatWebSources.map(s => (
                      <a
                        key={s.index}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        <span className="font-bold text-blue-400">[{s.index}]</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                        {s.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="border-t border-gray-100 bg-white flex-shrink-0">
              {/* Mode toggle */}
              <div className="flex items-center gap-1 px-4 pt-3">
                <button
                  onClick={() => setChatMode('chat')}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${chatMode === 'chat' ? 'bg-[#4F46E5] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <MessageSquare className="w-3.5 h-3.5" /> Chat
                </button>
                <button
                  onClick={() => { setChatMode('pptx'); if (!chatInput.trim()) setChatInput('Create a complete presentation covering all sections'); }}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${chatMode === 'pptx' ? 'bg-orange-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <Presentation className="w-3.5 h-3.5" /> Presentation
                </button>
                <button
                  onClick={() => setChatMode('excel')}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${chatMode === 'excel' ? 'bg-green-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <Download className="w-3.5 h-3.5" /> Excel
                </button>
                <button
                  onClick={() => setChatMode('word')}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${chatMode === 'word' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                >
                  <FileText className="w-3.5 h-3.5" /> Word
                </button>
              </div>
              {/* Attachment pills */}
              {chatAttachments.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
                  {chatAttachments.map((att, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 border border-indigo-200 rounded-full text-xs text-indigo-700 max-w-[80%]">
                      <Paperclip className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{att.name}</span>
                      <button
                        onClick={() => setChatAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="ml-1 text-indigo-400 hover:text-indigo-700 flex-shrink-0"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="px-4 py-3 flex items-center gap-2">
                {/* Hidden file inputs */}
                <input ref={chatFileInputRef} type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" className="hidden" onChange={handleChatFileAttach} />
                <input ref={chatImageInputRef} type="file" multiple accept="image/png,image/jpeg,image/jpg,image/gif,image/webp" className="hidden" onChange={handleChatFileAttach} />

                {/* + attach button */}
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setChatAttachMenuOpen(p => !p)}
                    disabled={chatLoading || chatAttachLoading}
                    className="w-12 h-12 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 hover:text-[#4F46E5] hover:border-[#4F46E5] hover:bg-indigo-50 transition-colors disabled:opacity-40"
                    title="Attach"
                  >
                    {chatAttachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </button>
                  {chatAttachMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-[70]" onClick={() => setChatAttachMenuOpen(false)} />
                      <div className="absolute bottom-full left-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-lg z-[80] overflow-hidden min-w-[160px]">
                        <button
                          onClick={() => { setChatAttachMenuOpen(false); chatFileInputRef.current?.click(); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                        >
                          <Paperclip className="w-4 h-4" /> Upload File
                        </button>
                        <button
                          onClick={() => { setChatAttachMenuOpen(false); chatImageInputRef.current?.click(); }}
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors"
                        >
                          <ImageIcon className="w-4 h-4" /> Upload Image
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Textarea */}
                <textarea
                  value={chatInput}
                  onChange={e => {
                    setChatInput(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChat(); } }}
                  placeholder={chatMode === 'pptx' ? 'Press send for a complete deck covering all sections, or describe what you want…' : chatMode === 'excel' ? 'Describe what data you want in Excel…' : chatMode === 'word' ? 'Press send to download the full council output as a Word document, or describe a refinement…' : 'Ask a follow-up question...'}
                  rows={1}
                  disabled={chatLoading || pptxGenerating || excelGenerating || wordGenerating}
                  className={`flex-1 px-3 py-3 text-sm border rounded-xl resize-none overflow-hidden focus:outline-none placeholder-gray-400 disabled:opacity-50 leading-relaxed transition-colors ${chatMode === 'pptx' ? 'border-orange-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500' : chatMode === 'excel' ? 'border-green-300 focus:border-green-500 focus:ring-1 focus:ring-green-500' : chatMode === 'word' ? 'border-blue-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500' : 'border-gray-200 focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]'}`}
                  style={{ minHeight: '48px' }}
                  autoFocus
                />

                {/* Web search toggle (chat) — hidden if tenant has disabled it */}
                {chatMode === 'chat' && webSearchTenantEnabled && (
                  <button
                    onClick={() => setWebSearch(prev => !prev)}
                    title={webSearch ? 'Web Search ON — click to disable' : 'Web Search OFF — click to enable'}
                    className={`h-12 w-12 flex-shrink-0 flex items-center justify-center rounded-xl border transition-colors relative ${webSearch ? 'border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100' : 'border-gray-200 bg-white text-gray-400 hover:text-gray-600 hover:border-gray-300'}`}
                  >
                    <Globe className="w-4 h-4" />
                    {webSearch && <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-blue-500" />}
                  </button>
                )}

                {/* Send button */}
                <button
                  onClick={handleChat}
                  disabled={chatLoading || pptxGenerating || excelGenerating || wordGenerating || (chatMode !== 'word' && chatMode !== 'pptx' && !chatInput.trim() && chatAttachments.length === 0)}
                  className={`h-12 flex-shrink-0 flex items-center justify-center rounded-xl text-white disabled:opacity-40 transition-colors ${chatMode === 'pptx' ? 'px-3 gap-1.5 bg-orange-500 hover:bg-orange-600 text-xs font-semibold' : chatMode === 'excel' ? 'px-3 gap-1.5 bg-green-600 hover:bg-green-700 text-xs font-semibold' : chatMode === 'word' ? 'px-3 gap-1.5 bg-blue-600 hover:bg-blue-700 text-xs font-semibold' : 'w-12 bg-[#4F46E5] hover:bg-[#4338CA]'}`}
                >
                  {chatMode === 'pptx' ? <><Presentation className="w-4 h-4" /> .pptx</> : chatMode === 'excel' ? <><Download className="w-4 h-4" /> .xlsx</> : chatMode === 'word' ? <><FileText className="w-4 h-4" /> .docx</> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Large Input Warning Modal */}
      {failedExtractionWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-red-100 bg-red-50">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
              <span className="text-base font-semibold text-red-800">Some documents could not be read</span>
            </div>
            <div className="px-6 py-5 space-y-4 text-sm text-gray-700">
              <p>
                {failedExtractionWarning.failedDocNames.length} file{failedExtractionWarning.failedDocNames.length === 1 ? '' : 's'} failed text extraction and will be <span className="font-semibold text-red-700">completely excluded</span> from this Council run — the AI models will not see their content or even know they exist:
              </p>
              <ul className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 space-y-1">
                {failedExtractionWarning.failedDocNames.map(name => (
                  <li key={name} className="text-xs text-red-700 font-medium flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 flex-shrink-0" /> {name}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-500 leading-relaxed">
                If you proceed, the Council's analysis will be based only on the documents that extracted successfully. To include these files, remove them, fix the source file (e.g. remove password protection, re-export as a standard PDF), and re-upload.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  To proceed anyway, type the following exactly:
                  <span className="block mt-1 font-mono text-xs bg-gray-100 border border-gray-200 rounded px-2 py-1.5 text-gray-800 select-all">
                    {FAILED_EXTRACTION_CONFIRM_PHRASE}
                  </span>
                </label>
                <input
                  type="text"
                  value={failedExtractionConfirmText}
                  onChange={e => setFailedExtractionConfirmText(e.target.value)}
                  placeholder="Type the confirmation phrase above"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => { setFailedExtractionWarning(null); setFailedExtractionConfirmText(''); }}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={failedExtractionWarning.onConfirm}
                disabled={failedExtractionConfirmText.trim() !== FAILED_EXTRACTION_CONFIRM_PHRASE}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Run Anyway (excluding these files)
              </button>
            </div>
          </div>
        </div>
      )}

      {largeInputWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-100 bg-amber-50">
              <span className="text-2xl">⚠️</span>
              <span className="text-base font-semibold text-amber-800">Large Input Detected</span>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-gray-700">
              <p>
                <span className="font-semibold">{(largeInputWarning.totalChars / 1000).toFixed(0)}k characters</span> across{' '}
                <span className="font-semibold">{largeInputWarning.fileCount} file{largeInputWarning.fileCount === 1 ? '' : 's'}</span> detected.
              </p>
              <p>Estimated processing time: <span className="font-semibold text-amber-700">up to 10 minutes</span>.</p>
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-xs leading-relaxed">
                <span className="font-bold">Billing notice:</span> Input token costs are charged by Anthropic/OpenAI at the point of receipt — even if the session times out before output is returned.
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setLargeInputWarning(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={largeInputWarning.onConfirm}
                className="flex-1 px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {truncationWarning && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="flex items-center gap-3 px-6 py-4 border-b border-amber-100 bg-amber-50">
              <span className="text-2xl">⚠️</span>
              <span className="text-base font-semibold text-amber-800">Some models can't read the full input</span>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm text-gray-700">
              <p>
                Your input is <span className="font-semibold">{(truncationWarning.totalChars / 1000).toFixed(0)}k characters</span>, which exceeds the context window of {truncationWarning.models.length === 1 ? 'this selected model' : 'these selected models'}:
              </p>
              <ul className="list-disc list-inside text-gray-800 font-medium">
                {truncationWarning.models.map(m => (
                  <li key={m}>{availableModels.find(am => am.id === m)?.name || m}</li>
                ))}
              </ul>
              {truncationWarning.largeModels.length > 0 ? (
                <p>
                  Your other selected model{truncationWarning.largeModels.length === 1 ? '' : 's'} ({truncationWarning.largeModels.map(m => availableModels.find(am => am.id === m)?.name || m).join(', ')}) can read the full input. Choose how to proceed:
                </p>
              ) : (
                <p>
                  For {truncationWarning.models.length === 1 ? 'this model' : 'these models'}, the input will be <span className="font-semibold text-amber-700">truncated to ~{(truncationWarning.limitChars / 1000).toFixed(0)}k characters</span> so the run doesn't fail.
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
              {truncationWarning.onDropSmall && (
                <button
                  onClick={truncationWarning.onDropSmall}
                  className="w-full px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 transition-colors"
                >
                  Run only the large-context models at full fidelity
                </button>
              )}
              <button
                onClick={truncationWarning.onConfirm}
                className="w-full px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors"
              >
                Truncate the small-context model{truncationWarning.models.length === 1 ? '' : 's'} & run all
              </button>
              <button
                onClick={() => setTruncationWarning(null)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      {/* SmartStart Files Confirmation Modal */}
      {showSmartStartModal && pendingSmartStartDocs && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-purple-50">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <span className="text-base font-semibold text-gray-800">Previous Session Files Detected</span>
              </div>
              <button
                onClick={() => { if (pendingSmartStartDocs?.sessionId) localStorage.setItem(`smartstart_prefill_seen_${pendingSmartStartDocs.sessionId}`, '1'); setShowSmartStartModal(false); setPendingSmartStartDocs(null); }}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">{pendingSmartStartDocs.count} file{pendingSmartStartDocs.count === 1 ? '' : 's'}</span> from your previous SmartStart session {pendingSmartStartDocs.sessionTitle ? <span>(<span className="italic text-purple-700">{pendingSmartStartDocs.sessionTitle}</span>)</span> : ''} are available.
              </p>
              <p className="text-sm text-gray-500 mt-1">Would you like to load them into this session, or start fresh?</p>
              <ul className="mt-3 space-y-1">
                {pendingSmartStartDocs.docs.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <FileText className="w-3.5 h-3.5 text-purple-400 flex-shrink-0" />
                    <span className="truncate">{d.name}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => {
                  if (pendingSmartStartDocs?.sessionId) localStorage.setItem(`smartstart_prefill_seen_${pendingSmartStartDocs.sessionId}`, '1');
                  setShowSmartStartModal(false);
                  setPendingSmartStartDocs(null);
                  toast.success('Starting fresh — previous session files removed');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium border border-gray-200 rounded-lg hover:bg-gray-100 transition-all"
              >
                Remove &amp; Start Fresh
              </button>
              <button
                onClick={() => {
                  if (!pendingSmartStartDocs) return;
                  if (pendingSmartStartDocs.sessionId) localStorage.setItem(`smartstart_prefill_seen_${pendingSmartStartDocs.sessionId}`, '1');
                  setUploadedDocs(current =>
                    current.length > 0 ? current : pendingSmartStartDocs.docs
                  );
                  setSmartStartPrefill({
                    count: pendingSmartStartDocs.count,
                    sessionTitle: pendingSmartStartDocs.sessionTitle,
                    sessionUpdatedAt: pendingSmartStartDocs.sessionUpdatedAt,
                  });
                  setShowSmartStartModal(false);
                  setPendingSmartStartDocs(null);
                }}
                className="px-4 py-2 text-sm text-white bg-purple-600 hover:bg-purple-700 font-semibold rounded-lg transition-all"
              >
                Keep Files
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Clarification Panel ───────────────────────────────────────────── */}
      {showClarifyPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4 overflow-hidden">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-full">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Clarifying Questions</h2>
                  <p className="text-xs text-gray-500">Answer any that are relevant — all optional</p>
                </div>
              </div>
              <button onClick={() => { setShowClarifyPanel(false); runStep1(); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4 space-y-4">
              {clarifyLoading ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                  <p className="text-sm text-gray-500">Generating clarifying questions…</p>
                </div>
              ) : (
                clarifyQuestions.map((q, idx) => (
                  <div key={idx} className="space-y-1.5">
                    <label className="flex items-start gap-2 text-sm font-medium text-gray-800">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-700 text-xs flex items-center justify-center mt-0.5 font-bold">{idx + 1}</span>
                      <span>{q}</span>
                    </label>
                    <textarea
                      value={clarifyAnswers[idx] || ''}
                      onChange={e => setClarifyAnswers(prev => ({ ...prev, [idx]: e.target.value }))}
                      placeholder="Your answer (optional)…"
                      rows={2}
                      className="w-full ml-7 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 resize-none placeholder-gray-400 box-border"
                    />
                  </div>
                ))
              )}
            </div>
            {!clarifyLoading && (
              <div className="flex gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
                <button
                  onClick={() => { setShowClarifyPanel(false); runStep1(); }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors"
                >
                  Skip & Run Without Answers
                </button>
                <button
                  onClick={handleClarifySubmit}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Play className="w-4 h-4" /> Submit & Run Council
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ─── Results Component ────────────────────────────────────────────────────────
const CouncilResults: React.FC<{ result: CouncilResult; availableModels: CouncilModel[]; sessionId: number | null; excludedDocNames?: string[] }> = ({ result, availableModels, sessionId, excludedDocNames = [] }) => {
  const nameMap = Object.fromEntries(availableModels.map(m => [m.id, m.name]));
  return (
    <div className="space-y-6">
      {/* Persistent reminder of documents excluded due to failed extraction — see
          handleRun's pre-run gate and buildCombinedData's EXCLUDED marker. */}
      {excludedDocNames.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">
            <span className="font-semibold">This analysis excludes {excludedDocNames.length} document{excludedDocNames.length === 1 ? '' : 's'} that failed to read:</span>{' '}
            {excludedDocNames.join(', ')}. Any references below to these documents' contents were not verified against the actual source.
          </p>
        </div>
      )}
      {/* Summary bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-3 flex items-center gap-6 text-sm">
        <span className="font-semibold text-gray-700">Session Summary</span>
        <span className="flex items-center gap-1 text-green-600">
          <CheckCircle2 className="w-4 h-4" />
          {result.summary.successfulModels} succeeded
        </span>
        {result.summary.failedModels > 0 && (
          <span className="flex items-center gap-1 text-red-500">
            <XCircle className="w-4 h-4" />
            {result.summary.failedModels} failed
          </span>
        )}
        <span className="flex items-center gap-1 text-amber-600">
          <Crown className="w-4 h-4" />
          President: {result.summary.presidentSuccess ? 'Evaluated' : 'Failed'}
        </span>
      </div>

      {/* Member response cards */}
      <MemberResponseCards memberResults={result.memberResults} nameMap={nameMap} />

      {/* Presidential evaluation */}
      {result.presidentEvaluation && (
        <PresidentialEvaluation
          evaluation={result.presidentEvaluation}
          presidentModel={result.presidentModel}
          sessionId={sessionId}
        />
      )}
    </div>
  );
};

// ─── Member Response Cards ────────────────────────────────────────────────────
const MemberResponseCards: React.FC<{ memberResults: MemberResult[]; nameMap: Record<string, string>; onRetry?: (modelId: string) => Promise<void> }> = ({ memberResults, nameMap, onRetry }) => {
  return (
    <div>
      <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-gray-500" />
        Council Members' Responses
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {memberResults.map(r => (
          <MemberCard key={r.modelId} result={r} displayName={nameMap[r.modelId] || r.modelId} onRetry={onRetry} />
        ))}
      </div>
    </div>
  );
};

const MemberCard: React.FC<{ result: MemberResult; displayName: string; onRetry?: (modelId: string) => Promise<void> }> = ({ result, displayName, onRetry }) => {
  const success = result.status === 'success';
  const [retrying, setRetrying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleRetry = async () => {
    if (!onRetry || retrying) return;
    setRetrying(true);
    try { await onRetry(result.modelId); } finally { setRetrying(false); }
  };

  const handleCopy = () => {
    if (!result.response) return;
    navigator.clipboard.writeText(result.response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const cardHeader = (
    <div className={`flex items-center justify-between px-4 py-3 ${success ? 'bg-gray-50' : 'bg-red-50'}`}>
      <div className="flex items-center gap-2">
        {retrying
          ? <Loader2 className="w-4 h-4 text-amber-500 animate-spin" />
          : success
            ? <CheckCircle2 className="w-4 h-4 text-green-500" />
            : <XCircle className="w-4 h-4 text-red-500" />
        }
        <span className="text-sm font-semibold text-gray-800">{displayName}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">{(result.durationMs / 1000).toFixed(1)}s</span>
        {success && (
          <button onClick={handleCopy} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" title="Copy response">
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        )}
        {success && (
          <button onClick={() => setIsExpanded(true)} className="p-1 text-gray-400 hover:text-gray-600 transition-colors" title="Expand">
            <Expand className="w-3.5 h-3.5" />
          </button>
        )}
        {!success && onRetry && (
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-white border border-red-300 text-red-600 rounded-md hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            {retrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div className={`bg-white rounded-xl border-2 shadow-sm overflow-hidden ${success ? 'border-gray-200' : 'border-red-200'}`}>
        {cardHeader}
        {/* Card body */}
        <div className="px-4 py-3 max-h-[600px] overflow-y-auto">
          {success ? (
            <div className="prose prose-sm max-w-none text-gray-700">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.response || ''}</ReactMarkdown>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{result.error}</span>
            </div>
          )}
        </div>
      </div>

      {/* Expanded modal */}
      {isExpanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setIsExpanded(false)}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="text-sm font-semibold text-gray-800">{displayName}</span>
                <span className="text-xs text-gray-400 ml-1">{(result.durationMs / 1000).toFixed(1)}s</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleCopy} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors" title="Copy response">
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                <button onClick={() => setIsExpanded(false)} className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors" title="Close">
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Modal body */}
            <div className="px-8 py-6 overflow-y-auto flex-1">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.response || ''}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Presidential Evaluation ─────────────────────────────────────────────────
const PresidentialEvaluation: React.FC<{ evaluation: PresidentEvaluation; presidentModel: string; sessionId: number | null; hideHeader?: boolean }> = ({ evaluation, presidentModel, sessionId, hideHeader = false }) => {
  const [copied, setCopied] = useState(false);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'word' | 'pptx' | 'excel' | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(evaluation.finalResponse);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopySection = (key: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedSection(key);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const handleExport = async (format: 'word' | 'pptx' | 'excel') => {
    if (!sessionId || exporting) return;
    setExporting(format);
    try {
      const response = await api.post(
        `/api/ai-council/sessions/${sessionId}/export/${format}`,
        {},
        { responseType: 'blob' }
      );
      const ext = format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : 'pptx';
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AI_Council_Report.${ext}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`${format === 'word' ? 'Word' : format === 'excel' ? 'Excel' : 'PowerPoint'} report downloaded`);
    } catch (error) {
      console.error(`Export ${format} failed:`, error);
      toast.error(`Failed to export ${format === 'word' ? 'Word' : format === 'excel' ? 'Excel' : 'PowerPoint'} report`);
    } finally {
      setExporting(null);
    }
  };

  return (
    <div>
      {!hideHeader && <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-500" />
          Presidential Evaluation
          <span className="text-xs font-normal text-gray-400">by {presidentModel}</span>
        </h2>
        {sessionId && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExport('word')}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-all disabled:opacity-50"
              title="Export as Word document (consolidated, no model names)"
            >
              {exporting === 'word' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              Word
            </button>
            <button
              onClick={() => handleExport('pptx')}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 transition-all disabled:opacity-50"
              title="Export as PowerPoint (consolidated, no model names)"
            >
              {exporting === 'pptx' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              PowerPoint
            </button>
            <button
              onClick={() => handleExport('excel')}
              disabled={!!exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 transition-all disabled:opacity-50"
              title="Export as Excel workbook (all sections)"
            >
              {exporting === 'excel' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Excel
            </button>
          </div>
        )}
      </div>}
      <div className="space-y-4">

        {/* Convergence */}
        {evaluation.convergence && (
          <div className="bg-white rounded-xl border border-green-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-green-50 border-b border-green-200">
              <div className="flex items-center gap-2">
                <span className="text-green-700 text-lg">🟢</span>
                <span className="font-semibold text-green-800 text-sm">Points of Convergence</span>
                <span className="text-xs text-green-600 font-normal">— where models agreed</span>
              </div>
              <button onClick={() => handleCopySection('convergence', evaluation.convergence)} className="p-1 text-gray-400 hover:text-green-600 transition-colors" title="Copy">
                {copiedSection === 'convergence' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="px-5 py-4">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{evaluation.convergence}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Divergence */}
        {evaluation.divergence && (
          <div className="bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-orange-50 border-b border-orange-200">
              <div className="flex items-center gap-2">
                <span className="text-orange-700 text-lg">🟠</span>
                <span className="font-semibold text-orange-800 text-sm">Points of Divergence</span>
                <span className="text-xs text-orange-600 font-normal">— unique perspectives & disagreements</span>
              </div>
              <button onClick={() => handleCopySection('divergence', evaluation.divergence)} className="p-1 text-gray-400 hover:text-orange-600 transition-colors" title="Copy">
                {copiedSection === 'divergence' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="px-5 py-4">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{evaluation.divergence}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Quality Assessment */}
        {evaluation.qualityAssessment && (
          <div className="bg-white rounded-xl border border-blue-200 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-blue-50 border-b border-blue-200">
              <div className="flex items-center gap-2">
                <span className="text-blue-700 text-lg">🔵</span>
                <span className="font-semibold text-blue-800 text-sm">Quality Assessment</span>
              </div>
              <button onClick={() => handleCopySection('qualityAssessment', evaluation.qualityAssessment)} className="p-1 text-gray-400 hover:text-blue-600 transition-colors" title="Copy">
                {copiedSection === 'qualityAssessment' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="px-5 py-4">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{evaluation.qualityAssessment}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Final Combined Answer */}
        {evaluation.finalResponse && (
          <div className="bg-white rounded-xl border-2 border-purple-400 shadow-md overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 bg-purple-700 border-b border-purple-600">
              <div className="flex items-center gap-2">
                <span className="text-white text-lg">⭐</span>
                <span className="font-bold text-white text-sm">Final Combined Answer</span>
                <span className="text-xs text-purple-200 font-normal">— synthesized by Council President</span>
              </div>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-600 hover:bg-purple-500 text-white transition-all"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="px-5 py-5 bg-purple-50">
              <div className="prose prose-sm max-w-none text-gray-800">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{evaluation.finalResponse}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}

        {/* Fallback: show raw if parsing failed */}
        {!evaluation.convergence && !evaluation.finalResponse && evaluation.raw && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="prose prose-sm max-w-none text-gray-700">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{evaluation.raw}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Strategy Synthesis Result Card ──────────────────────────────────────────
const StrategySynthesisResultCard: React.FC<{ result: StrategySynthesisResult; projectId?: number }> = ({ result, projectId }) => {
  const [copied, setCopied] = useState(false);
  const [exportingPptx, setExportingPptx] = useState(false);
  // Opt-in only — see the "Enhanced visualization" checkbox next to the chat
  // PPTX branding picker above for the cost tradeoff.
  const [enhancedVisualization, setEnhancedVisualization] = useState(false);
  // Q7 (parent epic, Ryan 2026-07-15): hidden by default. The single-transcript path should
  // feel like "upload a transcript, get a report", not like being handed an API response.
  // The data is already in memory — this only decides whether it is on screen.
  const [showUnderlyingData, setShowUnderlyingData] = useState(false);
  // Q8: a banner, never a silent auto-promote. Dismissing is a per-view choice; it does not
  // decide anything, so it is deliberately not persisted.
  const [promotionDismissed, setPromotionDismissed] = useState(false);


  const handleExportPptx = async () => {
    if (exportingPptx) return;
    setExportingPptx(true);
    try {
      const response = await api.post(
        '/api/intelligence/export-pptx',
        // projectId lets the backend resolve this project's client-tag branding
        // (logo/colors) — it was previously omitted here, so every Strategy
        // Synthesis export rendered unbranded regardless of the client's setup.
        { analysis: result.response, modeName: result.subToolName, projectId, enhanced: enhancedVisualization },
        { responseType: 'blob' },
      );
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (result.subToolName || 'Strategy_Synthesis').replace(/[^a-z0-9]+/gi, '_').slice(0, 60);
      a.download = `${safeName}_${new Date().toISOString().slice(0, 10)}.pptx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('PowerPoint downloaded');
    } catch (err: any) {
      // Backend returns JSON when erroring on a route that normally streams a
      // blob — extract the error message out of the blob so we can show it.
      let msg = 'Failed to export PowerPoint';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.error) msg = parsed.error;
        } else if (err?.response?.data?.error) {
          msg = err.response.data.error;
        }
      } catch { /* fall through to default message */ }
      toast.error(msg);
    } finally {
      setExportingPptx(false);
    }
  };

  const s = result.structured;

  return (
    <div className="space-y-4">
      {result.preliminary && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 text-sm text-blue-900">
          Preliminary — {result.promotion?.interview_count ?? result.structured?.run_count ?? 0} interviews in.
          Themes will update as the rest finish.
        </div>
      )}
      {result.interviewContextUsed && !result.preliminary && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl px-5 py-3 text-sm text-purple-900">
          Used {result.interviewContextCount ?? result.promotion?.interview_count ?? 'stored'} ingested interviews from this project.
        </div>
      )}
      {/* Q8 — promotion path. A second interview UNLOCKS cross-interview rollup; it does not
          trigger it. The first run is not redone either way, so nothing is lost by waiting,
          and a consultant who never clicks keeps the report they already have. */}
      {result.promotion?.eligible && !promotionDismissed && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <span className="font-semibold">
              This project has {countOf(result.promotion.interview_count, 'interview')}.
            </span>{' '}
            Promote it to unlock the cross-interview rollup — themes deduplicated across
            transcripts, with prevalence counted over all {result.promotion.interview_count}.
            Nothing already extracted is re-run.
          </div>
          <button
            onClick={() => setPromotionDismissed(true)}
            className="ml-auto text-xs text-amber-700 hover:text-amber-900 underline shrink-0"
          >
            Not now
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-3 flex items-center gap-4 text-sm">
        <span className="font-semibold text-gray-700">Strategy Synthesis</span>
        <span className="flex items-center gap-1.5 text-purple-600">
          <Sparkles className="w-4 h-4" />
          {result.subToolName}
        </span>
        <div className="ml-auto flex items-center gap-3">
          <label
            className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer"
            title="Design the deck with Claude's PowerPoint skill instead of our standard template — slower and more expensive, but a richer, more original layout."
          >
            <input
              type="checkbox"
              checked={enhancedVisualization}
              onChange={e => setEnhancedVisualization(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-400"
            />
            Enhanced visualization
          </label>
          <button
            onClick={handleExportPptx}
            disabled={exportingPptx}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export the synthesis as a PowerPoint deck"
          >
            {exportingPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {exportingPptx ? 'Building…' : 'Export as PowerPoint'}
          </button>
          <button
            onClick={() => { navigator.clipboard.writeText(result.response); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
          >
            {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-purple-200 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 bg-purple-50 border-b border-purple-100">
          <Sparkles className="w-4 h-4 text-purple-600" />
          <span className="font-semibold text-purple-800 text-sm">{result.subToolName}</span>
        </div>
        <div className="px-5 py-4 prose prose-sm max-w-none text-gray-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.response}</ReactMarkdown>
        </div>
        {/* Q7 — the findings are here, behind one explicit click. Shown only when the report
            came from the pipeline; every legacy result renders exactly as it did before. */}
        {s && (
          <div className="px-5 py-3 border-t border-purple-100">
            <button
              onClick={() => setShowUnderlyingData(v => !v)}
              className="flex items-center gap-1.5 text-xs font-medium text-purple-700 hover:text-purple-900"
            >
              {showUnderlyingData ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showUnderlyingData ? 'Hide underlying data' : 'View underlying data'}
              <span className="font-normal text-gray-500">
                — {countOf(s.citations.length, 'quote')}, traceable to {countOf(s.run_count, 'transcript')}
              </span>
            </button>

            {showUnderlyingData && (
              <div className="mt-3 space-y-3">
                {/* The three badges again, together. 100% ingested is the number most likely
                    to be quoted on its own, and on its own it says nothing about who was not
                    interviewed or how many findings may still be duplicates. */}
                <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <span className="font-semibold">{Math.round(s.ingestion_completeness * 100)}% ingested</span>
                  {' · '}
                  {s.pending_merge_count > 0
                    ? `${countOf(s.pending_merge_count, 'unresolved merge')} may affect these counts`
                    : 'no unresolved merges'}
                  {' · '}
                  {s.stakeholder_coverage?.status === 'unknown'
                    ? 'stakeholder coverage unknown'
                    /* The noun counts the roster, not the people seen: "1 of 2 stakeholders"
                       is right, "1 of 1 stakeholders" is not. Mirrors renderBadgeLine(). */
                    : `${s.stakeholder_coverage?.interviewed} of ${countOf(s.stakeholder_coverage?.expected, 'stakeholder')} interviewed`}
                </div>

                <div className="max-h-80 overflow-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-gray-600">
                        <th className="px-3 py-2 font-medium">Quote</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Speaker</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {s.citations.map((c, i) => (
                        <tr key={c.extracted_value_id || i} className="align-top">
                          <td className="px-3 py-2 text-gray-700">{c.verbatim}</td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{c.speaker || '—'}</td>
                          <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{c.timestamp || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(s.json, null, 2)], { type: 'application/json' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `user_research_findings_${new Date().toISOString().slice(0, 10)}.json`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200"
                >
                  <Download className="w-3.5 h-3.5" />
                  Download findings as JSON
                </button>
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-purple-100 bg-purple-50/40 flex justify-end">
          <button
            onClick={handleExportPptx}
            disabled={exportingPptx}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export the synthesis as a PowerPoint deck"
          >
            {exportingPptx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {exportingPptx ? 'Building…' : 'Export as PowerPoint'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Updated Plan Section (collapsible) ──────────────────────────────────────

const ExecutivePresentationButton: React.FC<{ finalSynthesis: FinalSynthesis; councilOutput: string; originalPrompt?: string; brandingTagId?: number | null; sessionId?: number | null; savedStructure?: SlideOutlineItem[] | null; refinementMessages?: { role: 'user' | 'assistant'; content: string }[]; onStructureSaved?: (s: SlideOutlineItem[]) => void }> = ({ finalSynthesis, councilOutput, originalPrompt = '', brandingTagId = null, sessionId = null, savedStructure = null, refinementMessages = [], onStructureSaved }) => {
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [outline, setOutline] = useState<SlideOutlineItem[]>([]);

  const renumber = (slides: SlideOutlineItem[]) =>
    (Array.isArray(slides) ? slides : [])
      .filter((s): s is SlideOutlineItem => !!s && typeof s === 'object')
      .map((s, i) => {
        const bullets = Array.isArray(s.bullets)
          ? s.bullets
          : (!s.type || s.type === 'bullets') && Array.isArray(s.items)
            ? s.items.map((b: any) => (b == null ? '' : String(b)))
            : [];
        return { ...s, slideNum: i + 1, bullets };
      });

  const isBulletsSlide = (s: SlideOutlineItem) => !s.type || s.type === 'bullets';
  const richSlideSummary = (s: SlideOutlineItem): string => {
    switch (s.type) {
      case 'cover':   return s.subtitle ? `Cover — ${s.subtitle}` : 'Cover slide';
      case 'closing': return s.subtitle ? `Closing — ${s.subtitle}` : 'Closing slide';
      case 'kpis':    return `${(s.items || []).length} KPI card(s)`;
      case 'insight': return 'Insight — Observation / So What / Now What';
      case 'table':   return `Table — ${(s.headers || []).length} cols × ${(s.rows || []).length} rows`;
      case 'actions': return `${(s.items || []).length} action item(s)`;
      default:        return s.type ? `${s.type} slide` : 'Slide';
    }
  };

  const handlePreview = async () => {
    const hasRefinements = Array.isArray(refinementMessages) && refinementMessages.length > 0;
    if (!hasRefinements && savedStructure && savedStructure.length > 0) {
      setOutline(renumber(savedStructure));
      setShowModal(true);
      return;
    }
    setPreviewing(true);
    try {
      const res = await api.post('/api/ai-council/preview-outline', {
        originalPrompt,
        councilOutput,
        finalSynthesis,
        messages: refinementMessages,
        currentOutline: savedStructure || undefined,
      });
      setOutline(renumber(res.data.outline || []));
      setShowModal(true);
    } catch (err: any) {
      console.error('[PreviewOutline] error:', err?.response?.status, err?.response?.data, err?.message);
      toast.error('Failed to generate preview. Please try again.');
    } finally {
      setPreviewing(false);
    }
  };

  const handleGenerate = async () => {
    const finalOutline = renumber(outline);
    setShowModal(false);
    setGenerating(true);
    // Persist locally first so the agreed structure is never lost on reload.
    onStructureSaved?.(finalOutline);
    try {
      const res = await api.post('/api/ai-council/executive-presentation', {
        originalPrompt,
        councilOutput,
        finalSynthesis,
        slideOutline: finalOutline,
        messages: refinementMessages,
        brandingTagId: brandingTagId,
        sessionId,
      }, { responseType: 'blob', timeout: 600000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Executive_Briefing_${Date.now()}.pptx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('[GeneratePPTX] error:', err?.response?.status, err?.message);
      toast.error('Failed to generate executive presentation');
    } finally {
      setGenerating(false);
    }
  };

  const updateTitle = (i: number, val: string) =>
    setOutline(prev => prev.map((s, idx) => idx === i ? { ...s, title: val } : s));
  const updateBullet = (si: number, bi: number, val: string) =>
    setOutline(prev => prev.map((s, idx) => idx === si ? { ...s, bullets: s.bullets.map((b, bi2) => bi2 === bi ? val : b) } : s));
  const removeBullet = (si: number, bi: number) =>
    setOutline(prev => prev.map((s, idx) => idx === si ? { ...s, bullets: s.bullets.filter((_, bi2) => bi2 !== bi) } : s));
  const addBullet = (si: number) =>
    setOutline(prev => prev.map((s, idx) => idx === si ? { ...s, bullets: [...s.bullets, ''] } : s));
  // Structure-level edits — directly satisfy "add/remove/reorder slides".
  const moveSlide = (i: number, dir: -1 | 1) =>
    setOutline(prev => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return renumber(next);
    });
  const removeSlide = (i: number) =>
    setOutline(prev => renumber(prev.filter((_, idx) => idx !== i)));
  const addSlide = (after: number) =>
    setOutline(prev => {
      const next = [...prev];
      next.splice(after + 1, 0, { slideNum: 0, title: 'New Slide', bullets: ['New point'] });
      return renumber(next);
    });

  return (
    <>
      <div className="bg-white border border-indigo-200 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-gray-800 font-semibold text-sm flex items-center gap-2">
              <span>📊</span> Executive Presentation
            </div>
            <div className="text-gray-500 text-xs mt-0.5">
              Strategic PowerPoint for C-suite — preview slides before generating
            </div>
          </div>
          <button
            onClick={handlePreview}
            disabled={previewing || generating}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
          >
            {generating
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating… (7–10 min)</>
              : previewing
              ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating preview...</>
              : <><Presentation className="w-4 h-4" /> Preview & Download</>}
          </button>
        </div>
        {generating && (
          <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-amber-50 border border-amber-300 rounded-lg">
            <span className="text-base">⏳</span>
            <div>
              <p className="text-xs font-semibold text-amber-800">This may take 7–10 minutes</p>
              <p className="text-xs text-amber-600">Presentation is being generated — please keep this page open and do not refresh.</p>
            </div>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-indigo-50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Presentation className="w-5 h-5 text-indigo-600" />
                <span className="text-base font-semibold text-gray-800">Presentation Preview</span>
                <span className="text-xs text-gray-500 bg-white border border-gray-200 rounded px-2 py-0.5">{outline.length} slides</span>
              </div>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Slide list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              <p className="text-xs text-gray-400 mb-2">Reorder, add, remove, and edit slides below. The exported PowerPoint follows this exact sequence and content.</p>
              {outline.map((slide, si) => (
                <div key={si} className="border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100">
                    <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{slide.slideNum}</span>
                    <input
                      value={slide.title}
                      onChange={e => updateTitle(si, e.target.value)}
                      className="flex-1 text-sm font-semibold text-gray-800 bg-transparent border-none outline-none focus:bg-indigo-50 rounded px-1 py-0.5"
                    />
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button onClick={() => moveSlide(si, -1)} disabled={si === 0} title="Move up" className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveSlide(si, 1)} disabled={si === outline.length - 1} title="Move down" className="p-1 text-gray-400 hover:text-indigo-600 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => addSlide(si)} title="Add slide below" className="p-1 text-gray-400 hover:text-green-600"><PlusCircle className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeSlide(si)} disabled={outline.length <= 1} title="Remove slide" className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  {isBulletsSlide(slide) ? (
                    <div className="px-4 py-2 space-y-1.5">
                      {slide.bullets.map((bullet, bi) => (
                        <div key={bi} className="flex items-center gap-2">
                          <span className="text-gray-400 text-xs flex-shrink-0">•</span>
                          <input
                            value={bullet}
                            onChange={e => updateBullet(si, bi, e.target.value)}
                            className="flex-1 text-sm text-gray-700 bg-transparent border-none outline-none focus:bg-gray-50 rounded px-1 py-0.5"
                          />
                          <button onClick={() => removeBullet(si, bi)} className="text-gray-300 hover:text-red-400 flex-shrink-0">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => addBullet(si)} className="text-xs text-indigo-400 hover:text-indigo-600 flex items-center gap-1 mt-1">
                        <PlusCircle className="w-3 h-3" /> Add bullet
                      </button>
                    </div>
                  ) : (
                    <div className="px-4 py-2 flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">{slide.type}</span>
                      <span className="text-xs text-gray-500">{richSlideSummary(slide)}</span>
                    </div>
                  )}
                </div>
              ))}
              <button onClick={() => addSlide(outline.length - 1)} className="w-full text-xs text-indigo-500 hover:text-indigo-700 border border-dashed border-indigo-200 hover:border-indigo-400 rounded-xl py-2 flex items-center justify-center gap-1 transition-colors">
                <PlusCircle className="w-3.5 h-3.5" /> Add slide
              </button>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium">Cancel</button>
              <button
                onClick={handleGenerate}
                disabled={generating}
                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-all"
              >
                {generating
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating… (7–10 min)</>
                  : <><Presentation className="w-4 h-4" /> Generate & Download .pptx</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const VPWordButton: React.FC<{ finalSynthesis: FinalSynthesis; councilOutput: string; originalPrompt: string }> = ({ finalSynthesis, councilOutput, originalPrompt }) => {
  const [loading, setLoading] = useState(false);
  const hasSynthesisContent = finalSynthesis && Object.values(finalSynthesis).some(v => typeof v === 'string' && v.trim().length > 20);
  const handleDownload = async () => {
    if (!hasSynthesisContent) { toast.error('Complete Step 4 — Final Synthesis before downloading the VP/Director brief.'); return; }
    setLoading(true);
    try {
      const res = await api.post('/api/ai-council/export/vp-word', {
        originalPrompt,
        councilOutput,
        finalSynthesis,
      }, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `VP_Director_Brief_${Date.now()}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const detail = await readBlobError(err);
      toast.error(detail || 'Failed to generate the document. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
      <div>
        <div className="text-gray-800 font-semibold text-sm flex items-center gap-2">
          <span>📄</span> {finalSynthesis?.customStructure ? 'Council Synthesis Brief' : 'VP / Director Brief'}
        </div>
        <div className="text-gray-500 text-xs mt-0.5">
          {finalSynthesis?.customStructure
            ? 'Structured Word document — output formatted per your requested structure'
            : 'Structured Word document — all 7 synthesis sections formatted for executive review'}
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={loading || !hasSynthesisContent}
        title={!hasSynthesisContent ? 'Run Step 4 — Final Synthesis first' : undefined}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileText className="w-4 h-4" /> Download .docx</>}
      </button>
    </div>
  );
};

const ImplementationExcelButton: React.FC<{ finalSynthesis: FinalSynthesis; councilOutput: string; originalPrompt: string }> = ({ finalSynthesis, councilOutput, originalPrompt }) => {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/ai-council/export/implementation-excel', {
        originalPrompt,
        councilOutput,
        finalSynthesis,
      }, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Implementation_Plan_${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const detail = await readBlobError(err);
      toast.error(detail || 'Failed to generate the Excel workbook. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const hasSynthesisContent = finalSynthesis && Object.values(finalSynthesis).some(v => typeof v === 'string' && v.trim().length > 20);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
      <div>
        <div className="text-gray-800 font-semibold text-sm flex items-center gap-2">
          <span>📊</span> Implementation Plan
        </div>
        <div className="text-gray-500 text-xs mt-0.5">
          Excel workbook — each synthesis section as a structured, actionable worksheet
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={loading || !hasSynthesisContent}
        title={!hasSynthesisContent ? 'Run Step 4 — Final Synthesis first' : undefined}
        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><Download className="w-4 h-4" /> Download .xlsx</>}
      </button>
    </div>
  );
};

const ImplementationWordButton: React.FC<{ finalSynthesis: FinalSynthesis; councilOutput: string; originalPrompt: string }> = ({ finalSynthesis, councilOutput, originalPrompt }) => {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const res = await api.post('/api/ai-council/export/implementation-word', {
        originalPrompt,
        councilOutput,
        finalSynthesis,
      }, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Implementation_Plan_${Date.now()}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      const detail = await readBlobError(err);
      toast.error(detail || 'Failed to generate the implementation plan. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between">
      <div>
        <div className="text-gray-800 font-semibold text-sm flex items-center gap-2">
          <span>📋</span> Implementation Plan — Full Detail
        </div>
        <div className="text-gray-500 text-xs mt-0.5">
          Word document — Phases · Milestones · Owners · Dependencies · Governance · KPI Criteria
        </div>
      </div>
      <button
        onClick={handleDownload}
        disabled={loading}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</> : <><FileText className="w-4 h-4" /> Download .docx</>}
      </button>
    </div>
  );
};

const UpdatedPlanSection: React.FC<{ plan: string }> = ({ plan }) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="bg-white rounded-xl border-2 border-indigo-400 shadow-md overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 bg-indigo-700 border-b border-indigo-600">
        <div className="flex items-center gap-2">
          <span className="text-white text-lg">🔄</span>
          <span className="font-bold text-white text-sm">Updated Integrated Plan</span>
          <span className="text-xs text-indigo-200 font-normal">— incorporating all specialist insights</span>
        </div>
        <button
          onClick={() => setExpanded(p => !p)}
          className="text-indigo-200 hover:text-white transition-colors"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
      {expanded && (
        <div className="px-5 py-5 bg-indigo-50">
          <div className="prose prose-sm max-w-none text-gray-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{plan}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Implementation Persona Card ─────────────────────────────────────────────
const SYNTHESIS_SECTIONS = [
  { key: 'confirmedStrengths',      label: '✅ Confirmed Strengths'      },
  { key: 'designConstraints',       label: '🔒 Design Constraints'        },
  { key: 'requiredModifications',   label: '✏️ Required Modifications'    },
  { key: 'openQuestions',           label: '❓ Open Questions'            },
  { key: 'contradictionResolution', label: '⚖️ Contradiction Resolution'  },
  { key: 'changeLog',               label: '📋 Change Log'                },
  { key: 'decisionRegister',        label: '📌 Decision Register'         },
];

const FinalSynthesisSections: React.FC<{ finalSynthesis: FinalSynthesis }> = ({ finalSynthesis }) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const handleCopySection = (key: string, content: string) => {
    navigator.clipboard.writeText(content);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleCopyAll = () => {
    const textToCopy = finalSynthesis.customStructure
      ? (finalSynthesis.raw || '')
      : SYNTHESIS_SECTIONS
          .map(({ key, label }) => {
            const content = (finalSynthesis as any)[key];
            return content ? `${label}\n${'─'.repeat(40)}\n${content}` : null;
          })
          .filter(Boolean)
          .join('\n\n');
    navigator.clipboard.writeText(textToCopy);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-xs font-bold">4</span>
          Final Synthesis Engine
          <span className="text-xs font-normal text-orange-600 bg-orange-50 border border-orange-200 rounded px-2 py-0.5">Claude Opus</span>
        </h2>
        <button
          onClick={handleCopyAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-lg transition-colors"
        >
          {copiedAll ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
          {copiedAll ? 'Copied!' : 'Copy All'}
        </button>
      </div>
      {finalSynthesis.customStructure ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Synthesis Output</h3>
            <button
              onClick={() => handleCopySection('raw', finalSynthesis.raw)}
              className="p-1 text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
              title="Copy output"
            >
              {copiedKey === 'raw' ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          </div>
          <div className="prose prose-sm max-w-none text-gray-700">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{finalSynthesis.raw}</ReactMarkdown>
          </div>
        </div>
      ) : (
        SYNTHESIS_SECTIONS.map(({ key, label }) => {
          const content = (finalSynthesis as any)[key];
          if (!content) return null;
          return (
            <div key={key} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
                <button
                  onClick={() => handleCopySection(key, content)}
                  className="p-1 text-gray-400 hover:text-indigo-600 transition-colors flex-shrink-0"
                  title="Copy section"
                >
                  {copiedKey === key ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{content}</ReactMarkdown>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

const ImplementationPersonaCard: React.FC<{ result: ImplementationPersonaResult }> = ({ result }) => {
  const [expanded, setExpanded] = useState(true);
  const [isModal, setIsModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(result.response);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const cardHeader = (
    <div className="flex items-center justify-between px-4 py-3 bg-indigo-50">
      <div className="flex items-center gap-2">
        <span className="text-lg">{result.emoji}</span>
        <span className="text-sm font-semibold text-gray-800">{result.personaName}</span>
      </div>
      <div className="flex items-center gap-1">
        <button onClick={handleCopy} className="p-1 text-gray-400 hover:text-indigo-600 transition-colors" title="Copy">
          {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => setIsModal(true)} className="p-1 text-gray-400 hover:text-indigo-600 transition-colors" title="Expand">
          <Expand className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setExpanded(p => !p)} className="p-1 text-gray-400 hover:text-gray-600">
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
  return (
    <>
      <div className="bg-white rounded-xl border-2 border-indigo-100 shadow-sm overflow-hidden">
        {cardHeader}
        {expanded && (
          <div className="px-4 py-3 max-h-[600px] overflow-y-auto">
            <div className="prose prose-sm max-w-none text-gray-700">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.response}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>
      {isModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setIsModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 bg-indigo-50 border-b border-indigo-100 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-lg">{result.emoji}</span>
                <span className="text-sm font-semibold text-gray-800">{result.personaName}</span>
              </div>
              <button onClick={() => setIsModal(false)} className="p-1.5 hover:bg-indigo-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600">
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="prose prose-sm max-w-none text-gray-700">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.response}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── Implementation Review Results ───────────────────────────────────────────
const ImplementationReviewResults: React.FC<{ result: ImplementationReviewResult; availableModels: CouncilModel[]; originalPrompt?: string }> = ({ result, availableModels, originalPrompt = '' }) => {
  const modelName = availableModels.find(m => m.id === result.modelUsed)?.name || result.modelUsed;
  return (
    <div className="space-y-6">

      {/* Summary bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-3 flex items-center gap-6 text-sm">
        <span className="font-semibold text-gray-700">Implementation Review</span>
        <span className="flex items-center gap-1.5 text-indigo-600">
          <Wrench className="w-4 h-4" />
          {result.personaResults.length} specialists evaluated
        </span>
        <span className="text-gray-400 text-xs">by {modelName}</span>
      </div>

      {/* Specialist evaluation cards */}
      <div>
        <h2 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-gray-500" />
          Specialist Evaluations
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {result.personaResults.map((r, i) => (
            <ImplementationPersonaCard key={i} result={r} />
          ))}
        </div>
      </div>

      {/* Final Synthesis Engine — Step 4 (Claude Opus) */}
      {result.finalSynthesis && (
        <FinalSynthesisSections finalSynthesis={result.finalSynthesis} />
      )}

      {/* Export Downloads */}
      {result.finalSynthesis && (
        <div className="space-y-3">
          <ExecutivePresentationButton
            finalSynthesis={result.finalSynthesis}
            councilOutput={result.councilOutput}
            originalPrompt={originalPrompt}
          />
          <VPWordButton
            finalSynthesis={result.finalSynthesis}
            councilOutput={result.councilOutput}
            originalPrompt={originalPrompt}
          />
          <ImplementationExcelButton
            finalSynthesis={result.finalSynthesis}
            councilOutput={result.councilOutput}
            originalPrompt={originalPrompt}
          />
          <ImplementationWordButton
            finalSynthesis={result.finalSynthesis}
            councilOutput={result.councilOutput}
            originalPrompt={originalPrompt}
          />
        </div>
      )}

      {/* Fallback: old updatedPlan if no finalSynthesis */}
      {!result.finalSynthesis && result.updatedPlan && <UpdatedPlanSection plan={result.updatedPlan} />}

      {/* Fallback: raw if parsing failed */}
      {result.personaResults.length === 0 && result.raw && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="prose prose-sm max-w-none text-gray-700">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>{result.raw}</ReactMarkdown>
          </div>
        </div>
      )}

    </div>
  );
};

export default AICouncil;
